import React, { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import { scrollToTarget } from "@/lib/scrollToTarget";
import { api } from "@/lib/api";
import { money, listMoney, usdApprox, useCurrency, usdFor } from "@/lib/currency";
import { ChargeDisclosure } from "@/components/util/ChargeDisclosure";
import { useSalesTax } from "@/lib/tax";
import { BrandedLoader } from "@/components/util/BrandedLoader";
import { usePageSeo } from "@/lib/usePageSeo";
import { useJsonLd, buildServiceSchema } from "@/lib/seoSchema";
import { trackEvent } from "@/lib/analytics";
import { useCart } from "@/contexts/CartContext";
import {
  POPULAR_CARD_CLASSES,
  POPULAR_ROW_CLASSES,
  PopularCardBadge,
  PopularRowMarker,
} from "@/components/popular/PopularPreview";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  ShoppingCart,
  Star,
  Sparkles,
  Package,
  Pencil,
  Crown,
  Layers,
  Palette,
  Wand2,
  FileText,
  Briefcase,
  ShieldCheck,
  Clock,
  MapPin,
  Upload,
  X as XIcon,
  Loader2,
  Image as ImageIcon,
  ArrowUp,
  ArrowDown,
  Copy,
  Minus,
  Plus,
  ChevronDown,
} from "lucide-react";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { toast } from "sonner";
import { ServiceHelpLinks } from "@/components/util/ServiceHelpLinks";

// =====================================================================
// PackageBuilder — visual redesign (Phase 6):
//   • Compact header (breadcrumb + title + tagline + description; no image)
//   • Sticky numbered step tracker (scrolls with the page, click-to-jump)
//   • Symmetric icon-anchored cards across ALL pricing_models
//       tiered / volume_tiered  – icon per tier (escalating: Pencil/Package/Crown)
//       custom_items            – icon-anchored card grid (was checkbox rows)
//       kits                    – kit cards + optional item-pool grid underneath
//   • Step 3 project intake form (business/industry/description/deadline/budget/refs)
//     writes to meta.project_brief so it flows straight into the cart / admin Orders
//   • Step 4 review block with final "Add to Cart" CTA
//   • Sticky running summary sidebar preserved (no click-through wizard)
//   • ?legacy=1 → still routes to the pre-Phase-3 configurator (handled in App.js)
// =====================================================================

const ITEM_ICON_POOL = [Package, Sparkles, Layers, Palette, Wand2, FileText, Briefcase, Star];
const TIER_ICONS = [Pencil, Package, Crown]; // escalating weight
const tierIconFor = (i, total) => {
  if (total <= 3) return TIER_ICONS[i] || TIER_ICONS[TIER_ICONS.length - 1];
  // For 4+ tiers, insert Layers between Package and Crown
  const extended = [Pencil, Layers, Package, Crown];
  return extended[i] || extended[extended.length - 1];
};
const itemIconFor = (i) => ITEM_ICON_POOL[i % ITEM_ICON_POOL.length];

// =====================================================================
// PackageBuilder — Phase 7 rearchitecture:
//   Step-gated collapsing flow. Only one step is expanded at a time.
//   Confirmed steps collapse into chips; future steps are NOT in the DOM
//   until the previous one is explicitly confirmed. This structurally
//   eliminates the class of bugs where displayed state diverges from
//   actual state (Phase 6/6.6 tracker inferred completion from scroll +
//   form-field inspection; that machinery is now removed entirely).
//
//   Preserved verbatim from prior phases:
//     • Compact header (no hero image)
//     • Card visuals for tiers/kits (AnchoredCard + popular decoupling)
//     • Checkbox list for add-ons/items/pool (CheckboxList)
//     • ProjectBriefForm with shadcn Select + ReferenceUploader
//     • Trust-signal row under CTAs
//     • useSelectionRows / addSelectionsToCart math + cart write
//     • ?legacy=1 → routes to ServiceConfiguratorPage (handled in App.js)
// =====================================================================

// Contact-field validators (module-level so both parent gating and inline
// per-field feedback share one source of truth). Basic length check on
// phone — Aruba numbers are 7 digits (+297 country code stripped) so >= 7
// digits after removing non-numerics passes.
const isValidEmail = (s) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((s || "").trim());
const isValidPhone = (s) => (s || "").replace(/\D/g, "").length >= 7;

// Quote mode — introduced in Phase Services Information Architecture
// Restructure and extended in Phase Services Commercial Model:
//   - service.cta_type === "quote"                 → whole service is quote-only
//   - OR the customer selects any tier/item/add-on with request_estimate=true
// The second path is reversible: deselecting the request_estimate item
// automatically restores Buy Online mode (Phase Commercial Model refinement 2).
const isQuoteMode = (service) => (service?.cta_type || "").toLowerCase() === "quote";

// Returns { hasEstimate, hasFixed } — the granular partition needed
// for BMD's mixed-mode Review (Feb 2026). `hasEstimate=true` means at
// least one selected leaf carries `request_estimate=true` (e.g. Other
// Design). `hasFixed=true` means at least one selected leaf has a
// known price. Both may be true simultaneously (mixed mode). Recomputed
// on every render — deselecting the estimate item flips back to
// hasEstimate=false automatically.
const selectionEstimatePartition = ({ service, pm, tierId, selectedAddons, selectedItemIds, selectedKitId }) => {
  const out = { hasEstimate: false, hasFixed: false };
  if (!service) return out;
  const mark = (row) => {
    if (!row) return;
    if (row.request_estimate) out.hasEstimate = true;
    else out.hasFixed = true;
  };
  if (pm === "tiered" || pm === "volume_tiered") {
    const t = (service.tiers || []).find((x) => x.id === tierId);
    if (t) mark(t);
    for (const a of service.addons || []) {
      if (selectedAddons.has(a.id)) mark(a);
    }
    return out;
  }
  if (pm === "custom_items") {
    for (const i of service.standalone_items || []) {
      if (selectedItemIds.has(i.id)) mark(i);
    }
    return out;
  }
  if (pm === "kits") {
    const k = (service.kits || []).find((x) => x.id === selectedKitId);
    if (k) mark(k);
    for (const i of service.standalone_items || []) {
      if (selectedItemIds.has(i.id)) mark(i);
    }
    return out;
  }
  return out;
};

// Returns true if any of the currently-selected leaves carries the
// request_estimate flag. Kept as a boolean-only wrapper for the many
// legacy call sites that only care about "any estimate?". New code
// should call selectionEstimatePartition() to also detect fixed lines
// (mixed mode) rather than losing that information.
const selectionHasEstimateItem = (args) => selectionEstimatePartition(args).hasEstimate;

// ---------------------------------------------------------------------------
// Event Creative & Promotion — package-aware Step 2 overrides.
//
// Scoped to service slug `event-creative-promotion` (the only kits-model
// service today). Given the selected kit name + an item, returns any of
// { displayName, description, disabled, disabledNote }. Missing keys keep
// the DB defaults. Prices, availability of other items, ordering, and DB
// copy are untouched — this only reshapes what Step 2 shows the customer
// for THIS kit choice.
// ---------------------------------------------------------------------------
const EVENT_CREATIVE_SLUG = "event-creative-promotion";

// Human-readable rendering of a `<input type="date">` value (YYYY-MM-DD).
// Locale-friendly, timezone-safe (parse the parts, not the string, so the
// user sees the date they picked regardless of browser TZ).
const formatEventDate = (iso) => {
  if (!iso) return "";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(String(iso).trim());
  if (!m) return String(iso);
  const [, y, mo, d] = m;
  const dt = new Date(Number(y), Number(mo) - 1, Number(d));
  if (Number.isNaN(dt.getTime())) return String(iso);
  return dt.toLocaleDateString(undefined, {
    weekday: "short",
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};


// ---------- Event Essentials / Campaign / Full-Creative synthetic add-ons -----------
// Synthetic (not backed by DB rows) items injected into Step 2 based on the
// selected kit. Two families:
//   • Story Upgrades — replace/extend the package's included story. Prices
//     differ per package because the starting deliverable differs
// P1 Frontend Cutover — commercial data (name/description/price/active/
// dependency/quantity_pricing/option_group) for these 11 items lives in
// the DB `standalone_items` list. React keeps a stable *synthetic id*
// per row only so `selectedItemIds`, mutex groupings and package-
// conditional injection can reference them without depending on the
// DB's mutable UUID `id`. `getMigratedItemView(service, syntheticId)`
// is the single lookup — it returns the DB row plus a merged
// presentation `id` for local state.
const ECP_STORY_UP_10_ESS = "ecp-story-up-10-essentials";
const ECP_STORY_UP_15_ESS = "ecp-story-up-15-essentials";
const ECP_STORY_UP_30_ESS = "ecp-story-up-30-essentials";
const ECP_STORY_UP_15_CAMP = "ecp-story-up-15-campaign";
const ECP_STORY_UP_30_CAMP = "ecp-story-up-30-campaign";
const ECP_3D_COMPLEX_UPGRADE = "ecp-3d-complex-upgrade";
// Every synthetic story-upgrade id. Retained for the kit-change cleanup
// effect only — the Step 2 builder now derives visibility from DB
// dependency fields via isAddonEligible() (no per-kit hardcoded array).
const ECP_ALL_STORY_UPGRADE_IDS = new Set([
  ECP_STORY_UP_10_ESS, ECP_STORY_UP_15_ESS, ECP_STORY_UP_30_ESS,
  ECP_STORY_UP_15_CAMP, ECP_STORY_UP_30_CAMP,
]);

// ---------- Advertising, Campaigns & Motion — synthetic package-dependent add-ons ------
// The Campaign Design Package (a `custom_items` selection in the ACM service)
// includes a Static Story + core coordinated deliverables. Selecting the
// package unlocks five package-dependent options:
//   • Additional Campaign Graphic — quantity add-on (tiered like Event
//     Creative's Additional Campaign Graphic: 175 / 150 / 125-floor).
//   • Main Artwork — Additional Size / Format — quantity add-on with
//     linear AWG 75 pricing to adapt the approved Main Event Artwork.
//   • Animated Story Upgrade — up to 10 sec / 15 sec / 30 sec — mutually
//     exclusive replacements of the included Static Story (250 / 400 / 650).
// Prices for the story ladder mirror the equivalent Event Creative
// Essentials story upgrades so cross-service pricing is consistent.
const ACM_SLUG = "advertising-campaigns-motion";
const ACM_CAMPAIGN_PACKAGE_ITEM_CODE = "acm_campaign_design_package";
const ACM_STORY_UP_10 = "acm-story-up-10";
const ACM_STORY_UP_15 = "acm-story-up-15";
const ACM_STORY_UP_30 = "acm-story-up-30";
const ACM_ADDL_CAMPAIGN_GRAPHIC = "acm-additional-campaign-graphic";
const ACM_ADDL_SIZE_FORMAT = "acm-additional-size-format";
const ACM_STORY_MUTEX = "acm-story-upgrade";
const ACM_STORY_UPGRADE_IDS = [ACM_STORY_UP_10, ACM_STORY_UP_15, ACM_STORY_UP_30];
const ACM_QTY_ADDON_IDS = [ACM_ADDL_CAMPAIGN_GRAPHIC, ACM_ADDL_SIZE_FORMAT];
const ACM_ALL_DEPENDENT_IDS = new Set([
  ...ACM_STORY_UPGRADE_IDS,
  ...ACM_QTY_ADDON_IDS,
]);

// Batch 2 P.B.5 — ACM Step-1 grouping. Four customer-understandable
// sections presented on Step 1 in place of the previous flat 14-item
// list. Every item, price, dependency, mutex, and quantity behavior
// stays exactly as before — this is presentation-only. Membership
// keyed by CANONICAL item_code so a CMS rename cannot silently
// dislodge a product into the wrong section.
//
// Rule reminder:
//   • Campaign-dependent add-ons (Additional Campaign Graphic, Main
//     Artwork Additional Size/Format, Animated Story Upgrade 10/15/30)
//     remain inside the Campaign Package group and are auto-injected
//     underneath the package row only when Campaign Package is
//     selected — identical to the pre-Batch-2 injection logic.
//   • Items whose name mentions "Story" or "Animated" are NOT moved
//     into the Adapt group merely because of the label.
const ACM_GROUP_CAMPAIGN = "campaign";
const ACM_GROUP_VIDEO = "video";
const ACM_GROUP_ADAPT = "adapt";
const ACM_GROUP_CUSTOM = "custom";
const ACM_GROUP_ADDITIONAL = "additional";
const ACM_STEP1_GROUPS = [
  {
    id: ACM_GROUP_CAMPAIGN,
    heading: "Campaign Package",
    subhead: "One package that covers the core artwork you need for a campaign.",
    memberItemCodes: new Set(["acm_campaign_design_package"]),
  },
  {
    id: ACM_GROUP_VIDEO,
    heading: "Video & Animation",
    subhead: "Video, animation and logo motion options.",
    memberItemCodes: new Set([
      "acm_promo_video_15", "acm_promo_video_30", "acm_promo_video_60",
      "acm_logo_animation",
    ]),
  },
  {
    id: ACM_GROUP_ADAPT,
    heading: "Adapt Existing Designs",
    subhead: "Bring life to designs and content you already have.",
    memberItemCodes: new Set([
      "acm_animate_15", "acm_animate_30", "acm_animate_60",
    ]),
  },
  {
    id: ACM_GROUP_CUSTOM,
    heading: "Custom Motion Graphics",
    subhead: "Work outside the defined options — we'll send a tailored estimate.",
    memberItemCodes: new Set(["acm_motion_graphics"]),
  },
];

// Fallback group. Any active ACM item whose item_code is not mapped
// into one of the four semantic groups above (e.g. a future CMS-added
// item, or a legacy row without a recognised code) still renders here
// instead of silently disappearing from Step 1. CMS remains the source
// of truth for what is offered.
const ACM_STEP1_ADDITIONAL_GROUP = {
  id: ACM_GROUP_ADDITIONAL,
  heading: "Additional Options",
  subhead: "Other options available for this service.",
};

// BMD Step-1 grouping. Mirrors the ACM Batch 2 pattern: partition the
// flat CMS item list into semantic groups on the same page, with a
// safe fallback so any future active item whose item_code is not
// mapped still surfaces to the customer.
const BMD_GROUP_ESSENTIALS = "essentials";
const BMD_GROUP_PROMO = "promo";
const BMD_GROUP_APPAREL = "apparel";
const BMD_GROUP_OTHER = "other";
const BMD_GROUP_ADDITIONAL = "additional";
const BMD_STEP1_GROUPS = [
  {
    id: BMD_GROUP_PROMO,
    heading: "Promotional Artwork",
    subhead: "Marketing artwork for social, print or billboard — choose your creative level and primary format.",
    memberItemCodes: new Set([
      "bmd_artwork_standard", "bmd_artwork_advanced",
      "bmd_main_artwork_additional_format",
      "bmd_source_file",
      "bmd_additional_concept_direction",
      // Phase 4-Nav Audit (Feb 2026) — Poster/Flyer + Banner families
      // added inside Promo so the currently-active production items
      // stop falling into the leftover "Additional Options" bucket.
      "bmd_poster_simple", "bmd_poster_complex",
      "bmd_banner_simple", "bmd_banner_complex",
    ]),
  },
  {
    id: BMD_GROUP_ESSENTIALS,
    heading: "Business Essentials",
    subhead: "Menus, cards, brochures and the everyday business materials your business runs on.",
    memberItemCodes: new Set([
      "bmd_menu_2p", "bmd_menu_4p", "bmd_menu_6p", "bmd_additional_menu_page",
      "bmd_brochure_4p", "bmd_brochure_6p", "bmd_additional_brochure_page",
      "bmd_business_card",
      "bmd_business_stationery", "bmd_additional_stationery_piece",
      "bmd_card_design",
      "bmd_forms_documents",
      "bmd_yearly_calendar",
      "bmd_email_design",
      "bmd_presentation", "bmd_additional_slide",
    ]),
  },
  {
    id: BMD_GROUP_APPAREL,
    heading: "Apparel & Merchandise",
    subhead: "Custom design for apparel and merchandise — production not included.",
    memberItemCodes: new Set([
      "bmd_merch_simple", "bmd_merch_complex",
    ]),
  },
  {
    id: BMD_GROUP_OTHER,
    heading: "Something Else",
    subhead: "Not on the list? Tell us what you need and we'll send a tailored estimate.",
    memberItemCodes: new Set(["bmd_other_design"]),
  },
];
const BMD_STEP1_ADDITIONAL_GROUP = {
  id: BMD_GROUP_ADDITIONAL,
  heading: "Additional Options",
  subhead: "Other options available for this service.",
};

// BMD Step-1 UX Pass (Feb 2026) — customer-guidance refinement.
// Reorders each BMD group's item list into a customer-scanning order,
// interleaves eligible dependent items DIRECTLY after their parent
// family (rather than as loose rows at the end), and tags dependents
// with a synthetic option_group + option_group_label so CheckboxList
// renders a "Options for your X" sub-heading in the shared style.
// Shared cross-family options (Additional Revision Round, Additional
// Stationery Piece) are rendered exactly once inside the arrangement
// so the customer never sees the same optional row duplicated.
// CMS remains the source of truth for prices, dependencies and caps.
const BMD_ESSENTIALS_FAMILIES = [
  { id: "menu",                codes: ["bmd_menu_2p", "bmd_menu_4p", "bmd_menu_6p"], optionLabel: "Options for your Menu & Price List", optionCodes: ["bmd_additional_menu_page"] },
  { id: "business_card",       codes: ["bmd_business_card"],                          optionLabel: "Options for your Business Card",           optionCodes: ["bmd_additional_stationery_piece"] },
  { id: "business_stationery", codes: ["bmd_business_stationery"],                    optionLabel: "Options for your Business Cards + Letterhead", optionCodes: ["bmd_additional_stationery_piece"] },
  { id: "card_design",         codes: ["bmd_card_design"],                            optionLabel: "Options for your Custom Card Design",      optionCodes: ["bmd_additional_stationery_piece"] },
  { id: "brochure",            codes: ["bmd_brochure_4p", "bmd_brochure_6p"],         optionLabel: "Options for your Brochure",                optionCodes: ["bmd_additional_brochure_page"] },
  { id: "presentation",        codes: ["bmd_presentation"],                           optionLabel: "Options for your Presentation",            optionCodes: ["bmd_additional_slide"] },
  { id: "forms_documents",     codes: ["bmd_forms_documents"],                        optionLabel: "Options for your Forms & Documents",       optionCodes: [] },
  { id: "email_design",        codes: ["bmd_email_design"],                           optionLabel: "Options for your Email Design",            optionCodes: [] },
  { id: "yearly_calendar",     codes: ["bmd_yearly_calendar"],                        optionLabel: "Options for your Yearly Calendar",         optionCodes: [] },
];
const BMD_PROMO_FAMILIES = [
  { id: "promo_artwork",       codes: ["bmd_artwork_standard", "bmd_artwork_advanced"], optionLabel: "Options for your Promotional Artwork", optionCodes: ["bmd_main_artwork_additional_format", "bmd_source_file", "bmd_additional_concept_direction"] },
  // Phase 4-Nav Audit — separate Poster/Flyer + Banner family so the
  // Flyer finder tile can deep-link and the customer sees a coherent
  // family heading instead of loose rows at the bottom.
  { id: "flyer_poster",        codes: ["bmd_poster_simple", "bmd_poster_complex"], optionLabel: "", optionCodes: [] },
  { id: "banner",              codes: ["bmd_banner_simple", "bmd_banner_complex"], optionLabel: "", optionCodes: [] },
];

const arrangeBmdItemsForStep1 = (items, groupId, activeCodes) => {
  if (groupId !== BMD_GROUP_ESSENTIALS && groupId !== BMD_GROUP_PROMO) return items;
  const families = groupId === BMD_GROUP_ESSENTIALS ? BMD_ESSENTIALS_FAMILIES : BMD_PROMO_FAMILIES;
  const byCode = new Map(items.map((it) => [it.item_code || "", it]));
  const includedCodes = new Set(items.map((it) => it.item_code || ""));
  const usedIds = new Set();
  const renderedOptionCodes = new Set();
  // Union of every option code across every family in this group. A
  // "leftover" item whose code is in this set is a cross-family option
  // (e.g. Additional Revision Round) — if no family within this group
  // activated it, we deliberately drop it instead of rendering it at
  // the bottom outside of any family context.
  const familyOptionCodes = new Set();
  families.forEach((fam) => (fam.optionCodes || []).forEach((c) => familyOptionCodes.add(c)));
  const out = [];
  for (const fam of families) {
    const variants = fam.codes.map((c) => byCode.get(c)).filter(Boolean);
    if (variants.length === 0) continue;
    for (const v of variants) {
      out.push(v);
      usedIds.add(v.id);
    }
    const familyActive = fam.codes.some((c) => activeCodes.has(c));
    if (!familyActive) continue;
    for (const optCode of (fam.optionCodes || [])) {
      if (renderedOptionCodes.has(optCode)) continue;
      if (!includedCodes.has(optCode)) continue;
      const opt = byCode.get(optCode);
      if (!opt) continue;
      renderedOptionCodes.add(optCode);
      out.push({
        ...opt,
        _quantityEnabled: opt.quantity_enabled ? true : opt._quantityEnabled,
        option_group: `${fam.id}_options`,
        option_group_label: fam.optionLabel,
        _secondaryOption: true,
      });
      usedIds.add(opt.id);
    }
  }
  for (const it of items) {
    if (usedIds.has(it.id)) continue;
    // Cross-family option that no family activated in this group → skip.
    if (familyOptionCodes.has(it.item_code || "")) continue;
    out.push(it);
  }
  return out;
};

// ---------------------------------------------------------------------------
// PLVW Step-1 grouping (Feb 2026 — final restructure)
// ---------------------------------------------------------------------------
// Data-driven groups keyed on stable plvw_* item_codes. Mirrors the
// BMD pattern so PLVW gets the same customer-scanning UX (family
// heading, mutex variants, contextual dependent options).
const PLVW_GROUP_LABELS    = "plvw-labels";
const PLVW_GROUP_PACKAGING = "plvw-packaging";
const PLVW_GROUP_WRAPS     = "plvw-wraps";
const PLVW_GROUP_OTHER     = "plvw-other";
const PLVW_STEP1_GROUPS = [
  {
    id: PLVW_GROUP_LABELS,
    heading: "Product Label Design",
    subhead: "Design system for a product line — start with the Base label; add matching variants for the rest of the range.",
    memberItemCodes: new Set(["plvw_label_base", "plvw_label_variant"]),
  },
  {
    id: PLVW_GROUP_PACKAGING,
    heading: "Packaging Design",
    subhead: "Boxes, sleeves, pouches — pick the level of complexity your project needs.",
    memberItemCodes: new Set(["plvw_packaging_standard", "plvw_packaging_advanced"]),
  },
  {
    id: PLVW_GROUP_WRAPS,
    heading: "Vehicle Wrap Design",
    subhead: "Cars, vans, trucks — from partial branding to full-wrap concepts.",
    memberItemCodes: new Set([
      "plvw_vehicle_wrap_standard",
      "plvw_vehicle_wrap_advanced",
      "plvw_vehicle_model_adaptation",
    ]),
  },
  {
    id: PLVW_GROUP_OTHER,
    heading: "Something Else",
    subhead: "Signage, environments, uniforms at scale — tell us about it and we'll send an estimate.",
    memberItemCodes: new Set(["plvw_other"]),
  },
];
const PLVW_FAMILIES = {
  [PLVW_GROUP_LABELS]: [
    { id: "labels",     codes: ["plvw_label_base"], optionLabel: "Options for your Product Label", optionCodes: ["plvw_label_variant"] },
  ],
  [PLVW_GROUP_PACKAGING]: [
    { id: "packaging",  codes: ["plvw_packaging_standard", "plvw_packaging_advanced"], optionLabel: "", optionCodes: [] },
  ],
  [PLVW_GROUP_WRAPS]: [
    { id: "wrap",       codes: ["plvw_vehicle_wrap_standard", "plvw_vehicle_wrap_advanced"], optionLabel: "Options for your Vehicle Wrap", optionCodes: ["plvw_vehicle_model_adaptation"] },
  ],
  [PLVW_GROUP_OTHER]: [
    { id: "other",      codes: ["plvw_other"], optionLabel: "", optionCodes: [] },
  ],
};

// ---------------------------------------------------------------------------
// 3D Rendering & Visualization Step-1 grouping (Feb 2026 — final restructure)
// ---------------------------------------------------------------------------
const VIZ_GROUP_PRODUCT   = "viz-product";
const VIZ_GROUP_INTERIOR  = "viz-interior";
const VIZ_GROUP_EXTERIOR  = "viz-exterior";
const VIZ_GROUP_EVENT     = "viz-event";
const VIZ_GROUP_OTHER     = "viz-other";
const VIZ_STEP1_GROUPS = [
  {
    id: VIZ_GROUP_PRODUCT,
    heading: "Product Rendering",
    subhead: "Photoreal renders for products, packaging or branded objects.",
    memberItemCodes: new Set(["viz_product_standard", "viz_product_advanced"]),
  },
  {
    id: VIZ_GROUP_INTERIOR,
    heading: "Interior Visualization",
    subhead: "Homes, condos, hospitality, retail or commercial interiors.",
    memberItemCodes: new Set([
      "viz_interior_standard", "viz_interior_advanced",
      "viz_interior_video",
      "viz_measure_standard", "viz_measure_advanced",
    ]),
  },
  {
    id: VIZ_GROUP_EXTERIOR,
    heading: "Exterior Visualization",
    subhead: "Building shells, landscaping and architectural exteriors.",
    memberItemCodes: new Set([
      "viz_exterior_standard", "viz_exterior_advanced",
      "viz_exterior_video",
      "viz_measure_standard", "viz_measure_advanced",
    ]),
  },
  {
    id: VIZ_GROUP_EVENT,
    heading: "Event & Venue Visualization",
    subhead: "Photoreal preview of a stage, venue or event environment before production. Different from Event Creative's 3D Event Map (functional top-down layout).",
    memberItemCodes: new Set(["viz_event_standard", "viz_event_advanced"]),
  },
  {
    id: VIZ_GROUP_OTHER,
    heading: "Something Else",
    subhead: "Standalone modelling, resort-scale projects or anything outside the standard scope.",
    memberItemCodes: new Set(["viz_custom_estimate"]),
  },
];
const VIZ_FAMILIES = {
  [VIZ_GROUP_PRODUCT]: [
    { id: "product",  codes: ["viz_product_standard", "viz_product_advanced"], optionLabel: "", optionCodes: [] },
  ],
  [VIZ_GROUP_INTERIOR]: [
    { id: "interior", codes: ["viz_interior_standard", "viz_interior_advanced"], optionLabel: "Options for your Interior visualization", optionCodes: ["viz_interior_video", "viz_measure_standard", "viz_measure_advanced"] },
  ],
  [VIZ_GROUP_EXTERIOR]: [
    { id: "exterior", codes: ["viz_exterior_standard", "viz_exterior_advanced"], optionLabel: "Options for your Exterior visualization", optionCodes: ["viz_exterior_video", "viz_measure_standard", "viz_measure_advanced"] },
  ],
  [VIZ_GROUP_EVENT]: [
    { id: "event",    codes: ["viz_event_standard", "viz_event_advanced"], optionLabel: "", optionCodes: [] },
  ],
  [VIZ_GROUP_OTHER]: [
    { id: "other",    codes: ["viz_custom_estimate"], optionLabel: "", optionCodes: [] },
  ],
};

// Shared arrangement helper for PLVW + 3D. Same pattern as
// arrangeBmdItemsForStep1: iterate approved families for the given
// group, emit each variant, then emit eligible dependent options
// (deduped across families) as secondary rows under a synthetic
// option_group_label heading. Anything unmapped falls through.
const arrangeCustomFamilyItemsForStep1 = (items, families, activeCodes) => {
  if (!families || families.length === 0) return items;
  const byCode = new Map(items.map((it) => [it.item_code || "", it]));
  const includedCodes = new Set(items.map((it) => it.item_code || ""));
  const usedIds = new Set();
  const renderedOptionCodes = new Set();
  const familyOptionCodes = new Set();
  families.forEach((fam) => (fam.optionCodes || []).forEach((c) => familyOptionCodes.add(c)));
  const out = [];
  for (const fam of families) {
    const variants = fam.codes.map((c) => byCode.get(c)).filter(Boolean);
    if (variants.length === 0) continue;
    for (const v of variants) {
      out.push(v);
      usedIds.add(v.id);
    }
    const familyActive = fam.codes.some((c) => activeCodes.has(c));
    if (!familyActive) continue;
    for (const optCode of (fam.optionCodes || [])) {
      if (renderedOptionCodes.has(optCode)) continue;
      if (!includedCodes.has(optCode)) continue;
      const opt = byCode.get(optCode);
      if (!opt) continue;
      renderedOptionCodes.add(optCode);
      out.push({
        ...opt,
        _quantityEnabled: opt.quantity_enabled ? true : opt._quantityEnabled,
        option_group: `${fam.id}_options`,
        option_group_label: fam.optionLabel,
        _secondaryOption: true,
      });
      usedIds.add(opt.id);
    }
  }
  for (const it of items) {
    if (usedIds.has(it.id)) continue;
    if (familyOptionCodes.has(it.item_code || "")) continue;
    out.push(it);
  }
  return out;
};

// ---------------------------------------------------------------------------
// Focus-only routing hook — reads ?focus=<family-or-group-or-code> once
// on mount, scrolls the matching element into view, adds a subtle ring
// (respects prefers-reduced-motion), and canonicalises the URL by
// stripping the focus param via history.replaceState so it never becomes
// a distinct SEO landing page. Never modifies selection/subtotal/cart.
//
// Phase 4-Nav Audit (Feb 2026) — target hierarchy expanded to
// EXACT ITEM → FAMILY → GROUP → item_code fallback → data-focus-target.
// A stable per-service map translates a family-level focus value
// (e.g. `bmd_menu`, `plvw_packaging`, `viz_product`) into the primary
// item_code it should scroll to. The hook waits with polling retries
// until the CMS data has actually rendered before scrolling —
// prevents scrolling before an async render finishes ("only works
// after refresh" race).
// ---------------------------------------------------------------------------
const FOCUS_FAMILY_TO_ITEM_CODES = {
  // BMD families
  bmd_menu:          ["bmd_menu_2p", "bmd_menu_4p", "bmd_menu_6p"],
  bmd_business_card: ["bmd_business_card"],
  bmd_brochure:      ["bmd_brochure_4p", "bmd_brochure_6p"],
  bmd_presentation:  ["bmd_presentation"],
  bmd_flyer_poster:  ["bmd_poster_simple", "bmd_poster_complex", "bmd_artwork_standard", "bmd_artwork_advanced"],
  bmd_merch:         ["bmd_merch_simple", "bmd_merch_complex"],
  // PLVW families
  plvw_packaging:    ["plvw_packaging_standard", "plvw_packaging_advanced"],
  plvw_wrap:         ["plvw_vehicle_wrap_standard", "plvw_vehicle_wrap_advanced"],
  // VIZ families
  viz_product:       ["viz_product_standard", "viz_product_advanced"],
  viz_interior:      ["viz_interior_standard", "viz_interior_advanced"],
  viz_exterior:      ["viz_exterior_standard", "viz_exterior_advanced"],
  viz_event:         ["viz_event_standard", "viz_event_advanced"],
};

const useFocusRouting = (serviceSlug) => {
  const locationKey = useLocation().key;
  React.useEffect(() => {
    if (!serviceSlug) return;
    const params = new URLSearchParams(window.location.search);
    const focus = params.get("focus");
    if (!focus) return;

    let timer = 0;

    const findTarget = () => {
      // 1. Exact family match — the first rendered <li data-item-code>
      //    for any code mapped to this focus value. Family granularity.
      const mappedCodes = FOCUS_FAMILY_TO_ITEM_CODES[focus] || [];
      for (const code of mappedCodes) {
        const el = document.querySelector(`li[data-item-code="${code}"]`);
        if (el) return el;
      }
      // 2. Group / focus-target / testid fallbacks (legacy behaviour).
      const selectors = [
        `[data-testid="${serviceSlug}-group-${focus}"]`,
        `[data-testid="bmd-group-${focus}"]`,
        `[data-testid="plvw-group-${focus}"]`,
        `[data-testid="viz-group-${focus}"]`,
        `[data-focus-target="${focus}"]`,
        `[data-testid="${focus}"]`,
      ];
      for (const sel of selectors) {
        const el = document.querySelector(sel);
        if (el) return el;
      }
      // 3. Item-code fallback — if the focus value is itself a raw
      //    item_code (e.g. someone shares ?focus=bmd_presentation).
      const codeEl = document.querySelector(`li[data-item-code="${focus}"]`);
      if (codeEl) return codeEl;
      return null;
    };

    // Single jump once the target renders (scrollToTarget holds it under
    // the navbar while async content settles). Strip ?focus= afterwards,
    // preserving React Router's history state (key/idx) so Back/Forward
    // scroll restoration still recognises this entry.
    const stop = scrollToTarget(findTarget, {
      offset: 160,
      onFound: (el) => {
        el.setAttribute("data-focus-active", "true");
        timer = window.setTimeout(() => el.removeAttribute("data-focus-active"), 3200);
        try {
          const url = new URL(window.location.href);
          url.searchParams.delete("focus");
          const clean = url.pathname + (url.searchParams.toString() ? `?${url.searchParams.toString()}` : "") + url.hash;
          window.history.replaceState(window.history.state, "", clean);
        } catch (_) { /* noop */ }
      },
    });

    return () => {
      stop();
      if (timer) window.clearTimeout(timer);
    };
  }, [serviceSlug, locationKey]);
};





// Frontend synthetic id → backend immutable item_code mapping. Every
// commercial value below (name, description, price, quantity_pricing,
// dependency, option_group) comes from the DB row selected via this map.
const ACM_SYNTHETIC_ITEM_CODE_BY_ID = {
  [ACM_STORY_UP_10]: "acm_story_upgrade_10",
  [ACM_STORY_UP_15]: "acm_story_upgrade_15",
  [ACM_STORY_UP_30]: "acm_story_upgrade_30",
  [ACM_ADDL_CAMPAIGN_GRAPHIC]: "acm_additional_campaign_graphic",
  [ACM_ADDL_SIZE_FORMAT]: "acm_main_artwork_additional_format",
};

const ECP_SYNTHETIC_ITEM_CODE_BY_ID = {
  [ECP_STORY_UP_10_ESS]: "ecp_story_upgrade_10_essentials",
  [ECP_STORY_UP_15_ESS]: "ecp_story_upgrade_15_essentials",
  [ECP_STORY_UP_30_ESS]: "ecp_story_upgrade_30_essentials",
  [ECP_STORY_UP_15_CAMP]: "ecp_story_upgrade_15_campaign",
  [ECP_STORY_UP_30_CAMP]: "ecp_story_upgrade_30_campaign",
  [ECP_3D_COMPLEX_UPGRADE]: "ecp_3d_event_map_complex_upgrade",
};

const MIGRATED_ITEM_CODE_BY_ID = {
  ...ACM_SYNTHETIC_ITEM_CODE_BY_ID,
  ...ECP_SYNTHETIC_ITEM_CODE_BY_ID,
};

// Single lookup — returns a merged view model whose commercial fields
// (name, description, price, active) come from the authoritative DB
// row. The returned object keeps the frontend synthetic `id` so React
// state (selectedItemIds, mutex sets) stays stable across renders. If
// the DB row is missing (e.g. deactivated in Dashboard), returns null
// and the specialized UX skips rendering — backend enforcement handles
// the rest.
const getMigratedItemView = (service, syntheticId) => {
  const itemCode = MIGRATED_ITEM_CODE_BY_ID[syntheticId];
  if (!itemCode || !service?.standalone_items) return null;
  const row = service.standalone_items.find((i) => i.item_code === itemCode);
  if (!row || row.active === false) return null;
  return {
    id: syntheticId,               // stable local reference for React state
    item_code: row.item_code,      // authoritative commercial identity
    name: row.name,                // DB
    description: row.description,  // DB
    price: row.price,              // DB
    active: row.active !== false,  // DB
    popular: !!row.popular,
    request_estimate: !!row.request_estimate,
    recurring_price: row.recurring_price || 0,
    // DB dependency / option_group — passed through so the reusable
    // eligibility + option-group helpers work on synthetic view models
    // (Story Upgrades, 3D Complex Upgrade) identically to raw DB rows.
    depends_on_item_code: row.depends_on_item_code || "",
    depends_on_item_code_any: Array.isArray(row.depends_on_item_code_any) ? row.depends_on_item_code_any : [],
    option_group: row.option_group || "",
    quantity_pricing: Array.isArray(row.quantity_pricing) ? row.quantity_pricing : [],
    quantity_enabled: !!row.quantity_enabled,
    quantity_min: row.quantity_min || 1,
    quantity_max: row.quantity_max || 1,
  };
};

// Combined synthetic registry — materializeSelectedItems + cart / quote
// consult this so both Event Creative and ACM synthetics flow through.
// P1 Synthetic Migration — the 11 formerly-synthetic ACM+ECP items now
// live in the DB `standalone_items` list (authoritative source of price,
// dependency, quantity_pricing, option_group). The specialised
// configurator UX in this file still uses hand-crafted synthetic view
// models to drive the package-conditional rendering (quantity steppers,
// story-upgrade mutex, 3D upgrade upsell). To guarantee the atomic-
// release "no duplicates" rule, we exclude any DB row whose item_code
// is already handled by a synthetic view model — the styled version is
// what the customer sees; the DB row is what the checkout resolves
// against. Delete this set (and the synthetic constructors above) in a
// future iteration when the specialised UX is refactored to render
// directly off DB rows.
// PRESENTATION-ONLY set — these item_codes get specialised UX styling
// (quantity stepper, mutex story-upgrade card, 3D upgrade card) so the
// generic row renderer skips them. NOT a commercial workaround: all
// commercial data (price/description/dependency/schedule) comes from
// the same DB rows via getMigratedItemView(). One catalog source, one
// presentation layer chosen by item_code.
const SPECIALISED_ITEM_CODES = new Set(Object.values(MIGRATED_ITEM_CODE_BY_ID));
const isSpecialisedItem = (i) => SPECIALISED_ITEM_CODES.has((i && i.item_code) || "");

// ---------------------------------------------------------------------------
// Frontend eligibility + option-group helpers (Feb 2026)
// ---------------------------------------------------------------------------
// PRINCIPLE — frontend filtering is UX. Backend validation is security.
// These helpers consume the SAME DB rule fields the backend authority
// uses (depends_on_item_code, depends_on_item_code_any, option_group)
// so the customer never sees a configuration the backend will reject.

// Return the set of "active" item_codes in the current configuration.
// For any service: the selected kit's item_code + item_codes of every
// selected standalone / synthetic item. This is the input for
// isAddonEligible() below.
const getActiveItemCodes = (service, selectedKitId, selectedItemIds) => {
  const codes = new Set();
  if (selectedKitId) {
    const kit = (service?.kits || []).find((k) => k.id === selectedKitId);
    if (kit?.item_code) codes.add(kit.item_code);
  }
  const items = service?.standalone_items || [];
  for (const id of selectedItemIds || []) {
    const dbItem = items.find((i) => i.id === id);
    if (dbItem?.item_code) codes.add(dbItem.item_code);
    // Synthetic ids resolve to their authoritative DB item_code.
    const synCode = MIGRATED_ITEM_CODE_BY_ID[id];
    if (synCode) codes.add(synCode);
  }
  return codes;
};

// Return the set of item_codes that a kit already delivers as
// entitlements — read straight from the CMS-authoritative
// `included_items[]`. Used by the generic "hide when included" rule in
// buildEventCreativeStep2Items so a sellable SKU can never surface as a
// paid add-on when the parent package already ships it (e.g. Full
// Event Creative includes `ecp_stage_screen_video_15`; that item MUST
// disappear from Step 2 for that kit).
const getKitIncludedItemCodes = (service, selectedKitId) => {
  const kit = (service?.kits || []).find((k) => k.id === selectedKitId);
  const codes = new Set();
  for (const it of kit?.included_items || []) {
    const c = (it?.code || "").trim();
    if (c) codes.add(c);
  }
  return codes;
};

// Reusable eligibility check — DB-rule-driven, no hardcoded per-service maps.
//
//   No dependency                → eligible
//   depends_on_item_code         → eligible iff that code is active
//   depends_on_item_code_any     → eligible iff ANY listed code is active
//
// Uses stable item_codes only — never names, never React-side heuristics.
const isAddonEligible = (item, activeCodes) => {
  if (!item) return false;
  const dep = (item.depends_on_item_code || "").trim();
  const depAnyRaw = item.depends_on_item_code_any;
  const depAny = Array.isArray(depAnyRaw) ? depAnyRaw.filter(Boolean) : [];
  if (!dep && depAny.length === 0) return true;
  if (dep && !activeCodes.has(dep)) return false;
  if (depAny.length > 0 && !depAny.some((c) => activeCodes.has(c))) return false;
  return true;
};

// Return the option_group / _mutexGroup identifier for an item — either
// the CMS-authoritative DB field (option_group) or the frontend-tagged
// _mutexGroup (only used for kit-scoped presentation groupings like
// event-map / branding-wrap that don't map to a shared DB option_group).
const getMutexGroup = (item) => {
  if (!item) return "";
  return (item.option_group || item._mutexGroup || "").trim();
};

// Radio-style selection helper for items that belong to a mutually
// exclusive group. Given the candidate list currently being rendered
// and the current selection set, returns the next selection set with:
//   • the new item added
//   • any previously-selected sibling from the same mutex group removed
//   • selections from other groups untouched
// Toggling the currently-selected member deselects it (empty state).
const applyOptionGroupSelection = (items, targetItem, currentIds) => {
  const next = new Set(currentIds);
  if (next.has(targetItem.id)) {
    next.delete(targetItem.id);
    return next;
  }
  const og = getMutexGroup(targetItem);
  if (og) {
    for (const other of items) {
      if (other.id === targetItem.id) continue;
      if (getMutexGroup(other) === og) next.delete(other.id);
    }
  }
  next.add(targetItem.id);
  return next;
};


// Merge selectedItemIds with the specialised view models sourced from
// DB rows. If a selectedId matches a DB `id`, use the row as-is. If
// it's a synthetic id (frontend-only local reference), fetch the DB
// row via getMigratedItemView. Only one representation reaches
// downstream math / cart / quote / review consumers.
const materializeSelectedItems = (service, selectedItemIds) => {
  const base = (service?.standalone_items || []).filter((i) => selectedItemIds.has(i.id));
  const extras = [];
  for (const id of selectedItemIds) {
    if (MIGRATED_ITEM_CODE_BY_ID[id]) {
      const view = getMigratedItemView(service, id);
      if (view) extras.push(view);
    }
  }
  return extras.length > 0 ? [...base, ...extras] : base;
};

const getEventCreativeItemOverride = (kitName, itemName) => {
  if (!kitName || !itemName) return {};
  const kit = kitName.trim();
  const name = itemName.trim();

  // Additional 5-Day Countdown Posts — selectable for every kit, but the
  // display title + copy change based on what the kit already bundles.
  if (name === "Additional 5-Day Countdown Posts") {
    if (kit === "Event Essentials") {
      return {
        displayName: "5-Day Event Countdown Posts",
        description:
          "5 coordinated countdown posts leading up to your event, adapted from the approved campaign design.",
      };
    }
    if (kit === "Event Campaign") {
      return {
        displayName: "Additional 5 Countdown Posts",
        description:
          "Add 5 more coordinated countdown posts beyond the 5 already included in your Event Campaign, adapted from the approved campaign design.",
      };
    }
    if (kit === "Full Event Creative") {
      return {
        displayName: "Additional 5 Countdown Posts",
        description:
          "Add 5 more coordinated countdown posts beyond the 5 already included in your Full Event Creative package, adapted from the approved campaign design.",
      };
    }
  }

  // Additional Menu / Signage Item — selectable for every kit; description
  // reframes per package. No disabled state.
  if (name === "Additional Menu / Signage Item") {
    if (kit === "Event Essentials") {
      return {
        description:
          "Add one standard single-layout food/drink menu, bar menu, welcome sign, directional sign or similar event item using your established event creative.",
      };
    }
    if (kit === "Event Campaign") {
      return {
        description:
          "Add one standard single-layout food/drink menu, bar menu, welcome sign, directional sign or similar event item beyond the 1 Event Signage Design already included in your Event Campaign.",
      };
    }
    if (kit === "Full Event Creative") {
      return {
        description:
          "Add one standard single-layout food/drink menu, bar menu, welcome sign, directional sign or similar event item beyond those already included in your Full Event Creative package.",
      };
    }
  }

  // 3D Event Maps — Essentials-only DB items (Campaign/Full include a
  // Standard 3D Event Map, so those kits see only the synthetic
  // "3D Event Map Upgrade — Complex" instead — the DB rows for Standard
  // and Complex are hidden under those packages). Descriptions here are
  // Essentials-facing "Add" wording; the DB descriptions are also fine
  // but this keeps the copy aligned with the other Essentials add-ons.
  if (name === "3D Event Map — Standard") {
    if (kit === "Event Essentials") {
      return {
        description:
          "Single event area with key zones, structures and guest points shown in a clear 3D layout. Based on client-provided event information.",
      };
    }
  }
  if (name === "3D Event Map — Complex") {
    if (kit === "Event Essentials") {
      return {
        description:
          "Larger or more detailed event area with multiple zones, structures and guest points shown in a detailed 3D layout. Based on client-provided event information.",
      };
    }
  }

  return {};
};

// Apply overrides to a full list of standalone_items. Non-Event-Creative
// services and un-selected-kit states pass through unchanged.
// P1 Eligibility Audit v1 — the two hard-coded hiding rules (countdown
// posts on Camp/Full; 3D map Standard/Complex on Camp/Full) have been
// retired. Visibility is now driven by DB `depends_on_item_code`
// + kit.included_items[] (via getKitIncludedItemCodes) in
// buildEventCreativeStep2Items. This function now only carries the
// display-only overrides (Essentials-facing descriptions) + quantity
// flag mapping.
const applyEventCreativeOverrides = (items, service, selectedKitId) => {
  if (service?.slug !== EVENT_CREATIVE_SLUG) return items;
  const kit = (service.kits || []).find((k) => k.id === selectedKitId);
  return items.map((it) => {
    const o = kit ? getEventCreativeItemOverride(kit.name, it.name) : {};
    const qtyEnabled = EVENT_CREATIVE_QTY_ITEM_NAMES.has(it.name);
    if (!qtyEnabled && !o.displayName && !o.description && !o.disabled) return it;
    return {
      ...it,
      _displayName: o.displayName,
      _description: o.description,
      _disabled: !!o.disabled,
      _disabledNote: o.disabledNote,
      _quantityEnabled: qtyEnabled,
    };
  });
};

// Format one included_items[] entry for the "Already Included" strip.
// Prefers the current catalog SKU name (so a Dashboard rename of a
// sellable item — e.g. "3D Event Map — Standard" → "3D Event Map — Pro"
// — surfaces automatically). Falls back to the entry's own `name` for
// package-only deliverables that have no separate catalog row.
const formatIncludedItem = (entry, service) => {
  if (!entry || !entry.code) return "";
  let displayName = entry.name || entry.code;
  const catalogHit = (service?.standalone_items || []).find(
    (i) => (i.item_code || "").trim() === entry.code,
  );
  if (catalogHit && (catalogHit.name || "").trim()) {
    displayName = catalogHit.name;
  }
  const qty = Number(entry.quantity || 1);
  return qty > 1 ? `${qty} × ${displayName}` : displayName;
};

// Compact reassurance strip shown at the top of Step 2 for Event Creative
// only. Renders nothing if the service/kit isn't a match. Uses the already
// selected kit — no separate state.
// P1 Eligibility Audit v1 — the map mutex is now driven by the DB
// `option_group=ecp_3d_event_map` on all three 3D map SKUs (Standard,
// Complex, Complex Upgrade). The frontend no longer needs a per-kit
// name-set to enforce mutex; applyOptionGroupSelection reads the DB
// option_group directly. Kept only as a documentation anchor.
// All synthetic Map ids. Used by the kit-change cleanup so switching
// packages never carries a stale map selection across pricing rules.
const ECP_ALL_MAP_SYNTHETIC_IDS = new Set([
  ECP_3D_COMPLEX_UPGRADE,
]);

// Assemble the ordered Step 2 items list for Event Creative kits:
//   • Apply package-aware overrides (descriptions, quantity, hidden, etc.).
//   • Inject Story Upgrade synthetic add-ons in the approved position, with
//     per-kit pricing (Essentials: 10/15/30 sec · Campaign & Full: 15/30 sec).
//     All story upgrades share a mutex group so only one duration selects.
//   • Append the synthetic "3D Event Map Upgrade — Complex" for Campaign
//     and Full (Standard 3D is included in those kits; the DB-backed 3D
//     Standard + Complex items are hidden — see applyEventCreativeOverrides).
//     Essentials has no map included, so the DB-backed 3D Standard + 3D
//     Complex items remain visible at the tail of the standalone_items list
//     and no synthetic map is injected.
//   • Tag every map option in the kit's mutex set so only one map
//     selection is ever active.
//   • Filter out any items flagged `_hidden`.
const buildEventCreativeStep2Items = (service, selectedKitId) => {
  // P1 Eligibility Audit v1 — visibility for EVERY item is now derived
  // from DB rules:
  //   1. Filter out inactive rows.
  //   2. Filter out DB rows for migrated synthetics (they are re-injected
  //      as view models below so the specialised UX for Story Upgrades +
  //      3D Complex Upgrade keeps working).
  //   3. Filter out items whose item_code is in kit.included_items — the
  //      package already delivers this deliverable so it must never
  //      surface as a paid add-on ("hide when included" rule).
  //   4. Filter out items whose depends_on_item_code /
  //      depends_on_item_code_any is not satisfied by the active kit.
  // No hardcoded per-name / per-kit rule remains.
  const activeCodes = getActiveItemCodes(service, selectedKitId, new Set());
  const includedCodes = getKitIncludedItemCodes(service, selectedKitId);
  const raw = (service?.standalone_items || [])
    .filter((i) => i.active !== false)
    .filter((i) => !isSpecialisedItem(i))
    .filter((i) => !includedCodes.has(i.item_code || ""))
    .filter((i) => isAddonEligible(i, activeCodes));
  const overridden = applyEventCreativeOverrides(raw, service, selectedKitId);
  if (service?.slug !== EVENT_CREATIVE_SLUG) return overridden;
  const kit = (service.kits || []).find((k) => k.id === selectedKitId);
  if (!kit) return overridden;
  let result = [...overridden];

  // Specialised UX injection — story upgrades + 3D complex upgrade
  // continue to render as styled synthetic view models. Which
  // synthetics get injected is derived 100% from DB dependency rules;
  // there is no per-kit hardcoded ID list.
  const storyItems = Object.keys(ECP_SYNTHETIC_ITEM_CODE_BY_ID)
    .filter((synId) => synId !== ECP_3D_COMPLEX_UPGRADE)
    .map((synId) => getMigratedItemView(service, synId))
    .filter((view) => view && isAddonEligible(view, activeCodes));
  storyItems.sort((a, b) => (a.price || 0) - (b.price || 0));
  if (storyItems.length > 0) {
    const menuIdx = result.findIndex((i) => i.name === "Additional Menu / Signage Item");
    if (menuIdx >= 0) result.splice(menuIdx + 1, 0, ...storyItems);
    else result.push(...storyItems);
  }

  const complexUpgradeView = getMigratedItemView(service, ECP_3D_COMPLEX_UPGRADE);
  if (complexUpgradeView && isAddonEligible(complexUpgradeView, activeCodes)) {
    result.push(complexUpgradeView);
  }
  return result;
};

const EventCreativeInclusionsStrip = ({ service, selectedKitId }) => {
  if (service?.slug !== EVENT_CREATIVE_SLUG) return null;
  const kit = (service.kits || []).find((k) => k.id === selectedKitId);
  if (!kit) return null;
  const items = kit.included_items || [];
  if (items.length === 0) return null;
  return (
    <div
      data-testid="step2-included-strip"
      data-kit={kit.name}
      data-included-count={items.length}
      className="mt-4 mb-5 rounded-md border border-[#1F1F1F] bg-white/[0.02] px-4 py-3"
    >
      <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#D1D1D1]">
        Already included in <span className="text-[#FF6600]">{kit.name}</span>
      </div>
      <p className="mt-1.5 text-xs text-[#8A8A8A] leading-relaxed">
        {items.map((it) => formatIncludedItem(it, service)).filter(Boolean).join(" · ")}
      </p>
    </div>
  );
};

// Step 2 "Already Included" strip for Branding, driven directly by the
// selected tier's `features[]` array — the same DB field the Step 1 tier
// card renders. This keeps Step 1 and Step 2 in sync from a single source
// of truth: editing a Complete Brand System feature in Admin updates both
// the card and the strip automatically.
//
// The only normalization applied is stripping the trailing " included"
// suffix used by the Complete Brand System feature copy ("Vehicle Wrap
// Design — Simple included"), because the strip already has an
// "Already included in [PACKAGE]" heading and repeating the word would
// read awkwardly. See the CMS audit trail in PRD.md.
const _stripIncludedSuffix = (s) =>
  typeof s === "string" ? s.replace(/\s+included\s*$/i, "").trim() : s;

const BrandingInclusionsStrip = ({ service, tierId }) => {
  if (service?.slug !== BRANDING_SLUG) return null;
  const tier = (service.tiers || []).find((t) => t.id === tierId);
  if (!tier) return null;
  const features = (tier.features || [])
    .map(_stripIncludedSuffix)
    .filter((s) => s && s.length > 0);
  if (features.length === 0) return null;
  return (
    <div
      data-testid="step2-included-strip"
      data-tier={tier.name}
      className="mt-4 mb-5 rounded-md border border-[#1F1F1F] bg-white/[0.02] px-4 py-3"
    >
      <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#D1D1D1]">
        Already included in <span className="text-[#FF6600]">{tier.name}</span>
      </div>
      <p className="mt-1.5 text-xs text-[#8A8A8A] leading-relaxed">
        {features.join(" · ")}
      </p>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Quantity-priced items — DB-authoritative after migrate_qty_pricing_cutover_v1
// (Feb 2026). Every ACM + Event Creative + Branding quantity item now
// carries a `quantity_pricing[]` schedule on its DB catalog row and
// `effectiveItemPrice` / `effectiveAddonPrice` read that schedule first.
// This React map is retained empty as a legacy safety fallback ONLY —
// `getItemTierPrice` returns 0 when a name is not listed, and both
// resolvers gracefully fall through to `price × qty`. Do not repopulate
// this map; commercial ladders belong on the catalog row.
// ---------------------------------------------------------------------------
const QTY_PRICE_SCHEDULES = {};
const EVENT_CREATIVE_QTY_ITEM_NAMES = new Set([
  "Additional Campaign Graphic",
  "Additional Menu / Signage Item",
  "Main Artwork — Additional Size / Format",
]);
const ACM_SYNTHETIC_QTY_ITEM_NAMES = new Set([
  "Additional Campaign Graphic",
  "Main Artwork — Additional Size / Format",
]);
// Branding qty-enabled add-ons. Mirrors the checkbox/stepper matrix
// from the approved spec: quantity for genuine repeat-purchase items
// (uniforms, packaging SKUs, social sets, poster layouts) and for the
// two shared adaptation ladders (Vehicle Adaptation, Size / Format).
// The Vehicle Wrap Simple/Complex remain checkbox with a mutex group.
const BRANDING_SLUG = "logo-brand-identity";
// Branding wrap mutex is now enforced via DB `option_group="brand_wrap_design"`
// on the two Vehicle Wrap addons (migrate_qty_pricing_cutover_v1). The
// shared `applyOptionGroupSelection` mutex path reads it directly; no
// per-name frontend set is required.
const BRANDING_WRAP_OPTION_GROUP = "brand_wrap_design";
const BRANDING_ADDITIONAL_VEHICLE_ADAPT_CODE = "brand_additional_vehicle_adaptation";
const EVENT_CREATIVE_QTY_MIN = 0;
const EVENT_CREATIVE_QTY_MAX = 10;

const isEventCreativeQtyItem = (service, item) =>
  !!service && !!item &&
  service.slug === EVENT_CREATIVE_SLUG &&
  EVENT_CREATIVE_QTY_ITEM_NAMES.has(item.name);

const isAcmQtyItem = (service, item) =>
  !!service && !!item &&
  service.slug === ACM_SLUG &&
  ACM_SYNTHETIC_QTY_ITEM_NAMES.has(item.name);

// Complete Brand System — "Additional …" framing for the shared DB items.
// The DB item id stays the same; only the customer-facing name/description
// is overridden when the selected tier is Complete Brand System. Prevents
// customers from being told they can "purchase" the first unit that is
// already bundled in Complete. Undefined name → no override applied.
const BRANDING_COMPLETE_OVERRIDES = {
  "Uniform Design Concept — Simple": {
    displayName: "Additional Uniform Design Concept — Simple",
    description: "Add another straightforward branded uniform concept beyond the 1 Simple concept already included in your Complete Brand System.",
  },
  "Uniform Design Concept — Complex": {
    displayName: "Additional Uniform Design Concept — Complex",
    description: "Add another detailed branded uniform concept beyond the uniform concept already included in your Complete Brand System.",
  },
  "Packaging Design — Standard": {
    displayName: "Additional Packaging Design — Standard",
    description: "Add one additional standard packaging design for another product/SKU beyond the packaging already included in your Complete Brand System.",
  },
  "Packaging Design — Advanced": {
    displayName: "Additional Packaging Design — Advanced",
    description: "Add one additional advanced packaging design for another product/SKU beyond the Advanced Packaging Design already included in your Complete Brand System.",
  },
  "Social Media Templates — 3 Designs": {
    displayName: "Add 3 More Social Media Templates",
    description: "Add 3 more reusable social media templates beyond the 3 already included in your Complete Brand System. Quantity 1 = +3 templates, 2 = +6, etc.",
  },
  "Poster / Billboard Design — 1 Layout": {
    displayName: "Additional Poster / Billboard Design — 1 Layout",
    description: "Add one additional branded poster or billboard layout beyond the 1 layout already included in your Complete Brand System.",
  },
  "Upgrade Vehicle Wrap to Complex": {
    displayName: "Upgrade Included Vehicle Wrap to Complex",
    description: "Upgrade the Simple Vehicle Wrap Design included in Complete Brand System to a full-wrap or highly detailed Complex concept.",
  },
  "Upgrade Uniform to Complex": {
    displayName: "Upgrade Included Uniform to Complex",
    description: "Upgrade the Simple Uniform Design Concept included in Complete Brand System to a more detailed Complex concept.",
  },
};

const isBrandingQtyAddon = (service, addon) =>
  !!service && !!addon && !!addon.quantity_enabled;

// Progressive-disclosure grouping for Branding Step 2. Maps every
// Branding addon (by DB name) to one of six customer-facing categories.
// New addons should be added here explicitly; anything unmapped falls
// into "Logo & Brand" as a safe default so nothing ever disappears.
const BRANDING_ADDON_GROUP_ORDER = [
  { id: "logo-brand", label: "Logo & Brand" },
  { id: "vehicle", label: "Vehicle Branding" },
  { id: "uniforms", label: "Uniforms" },
  { id: "packaging", label: "Packaging" },
  { id: "social", label: "Social Media" },
  { id: "advertising", label: "Advertising" },
];
const BRANDING_ADDON_GROUP_BY_NAME = {
  "Logo Animation": "logo-brand",
  "Upgrade to Comprehensive Brand Guidelines": "logo-brand",
  "Vehicle Wrap Design — Simple": "vehicle",
  "Vehicle Wrap Design — Complex": "vehicle",
  "Additional Vehicle Adaptation": "vehicle",
  "Upgrade Vehicle Wrap to Complex": "vehicle",
  "Uniform Design Concept — Simple": "uniforms",
  "Uniform Design Concept — Complex": "uniforms",
  "Upgrade Uniform to Complex": "uniforms",
  "Packaging Design — Standard": "packaging",
  "Packaging Design — Advanced": "packaging",
  "Social Media Templates — 3 Designs": "social",
  "Poster / Billboard Design — 1 Layout": "advertising",
};
const groupForBrandingAddon = (addon) =>
  BRANDING_ADDON_GROUP_BY_NAME[(addon?.name || "").trim()] || "logo-brand";

// Read a schedule from a DB item row and price N units. Returns 0 if
// the schedule is empty or missing (caller falls back to flat price ×
// qty). Same math as backend/core/pricing.py — Nth unit priced at the
// entry whose min_quantity is the largest ≤ N.
const priceFromDbSchedule = (schedule, qty) => {
  if (!Array.isArray(schedule) || schedule.length === 0 || qty <= 0) return 0;
  const rows = [...schedule].sort((a, b) => (a.min_quantity || 0) - (b.min_quantity || 0));
  let total = 0;
  for (let n = 1; n <= qty; n++) {
    let unit = null;
    for (const r of rows) {
      const mq = Number(r.min_quantity || 0);
      if (mq <= n) unit = Number(r.unit_price);
    }
    if (unit == null) unit = Number(rows[0]?.unit_price || 0);
    total += unit;
  }
  return total;
};

const isQtyItem = (service, item) =>
  isEventCreativeQtyItem(service, item) || isAcmQtyItem(service, item)
  // BMD architecture v1 (Feb 2026) — generic DB-driven path. Any item
  // whose catalog row carries quantity_enabled=true (BMD Additional
  // Menu Page / Stationery Piece / Concept Direction, and any future
  // service that opts in via CMS) participates in the qty math. React
  // no longer needs a service-specific list.
  || !!item?.quantity_enabled;

const getItemTierPrice = (itemName, qty) => {
  if (!qty || qty <= 0) return 0;
  const s = QTY_PRICE_SCHEDULES[itemName];
  if (!s) return 0;
  let total = 0;
  for (let i = 0; i < qty; i++) {
    total += i < s.tiers.length ? s.tiers[i] : s.floor;
  }
  return total;
};

// Backwards-compatible alias (kept for the Event Creative CheckboxList base
// price display which reads eventCreativeQtyPrice(name, 1)).
const eventCreativeQtyPrice = getItemTierPrice;

// Effective per-line price for the running total / cart / quote payload.
// Non-qty items keep their DB price; qty items follow this priority:
//   1. DB `quantity_pricing` schedule (authoritative when present),
//   2. Legacy React QTY_PRICE_SCHEDULES (ACM/ECP synthetics — kept
//      byte-identical to preserve customer-facing totals),
//   3. Flat DB `price` × qty (generic fallback for BMD items with
//      quantity_enabled but no schedule).
// The backend independently applies the same rules via
// core/pricing.py::_price_flat_or_scheduled — frontend is UX only.
const effectiveItemPrice = (service, item, qtysMap) => {
  if (isQtyItem(service, item)) {
    const q = qtysMap?.[item.id] || 0;
    if (q <= 0) return 0;
    // 1) DB schedule (BMD architecture v1 authoritative path).
    const dbTotal = priceFromDbSchedule(item?.quantity_pricing, q);
    if (dbTotal > 0) return dbTotal;
    // 2) Legacy React schedule (ACM/ECP synthetics).
    const scheduled = getItemTierPrice(item.name, q);
    if (scheduled > 0) return scheduled;
    // 3) Flat price × qty (BMD generic fallback).
    return (item.price || 0) * q;
  }
  return item.price;
};

const effectiveItemQty = (service, item, qtysMap) => {
  if (isQtyItem(service, item)) return qtysMap?.[item.id] || 0;
  return 1;
};

// Branding add-ons pricing/qty helpers — mirror effectiveItemPrice/effectiveItemQty
// but operate on `service.addons[i]` and use the shared qtysMap so the
// tiered add-on state and the qty state stay in sync.
//
// Cutover Feb 2026 — DB `addon.quantity_pricing` is now the authoritative
// path for every Branding + Website Design quantity add-on. The legacy
// name-keyed React map is retained ONLY as a safety fallback for any row
// whose DB schedule is somehow missing at read time.
const effectiveAddonPrice = (service, addon, qtysMap) => {
  if (!isBrandingQtyAddon(service, addon)) return addon.price;
  const q = qtysMap?.[addon.id] || 0;
  const qty = Math.max(1, q);
  // 1) DB schedule (authoritative).
  const dbTotal = priceFromDbSchedule(addon?.quantity_pricing, qty);
  if (dbTotal > 0) return dbTotal;
  // 2) Legacy React schedule (safety fallback only — kept for rows that
  //    predate the DB cutover). Empty in the shipping catalog today.
  const scheduled = getItemTierPrice(addon.name, qty);
  if (scheduled > 0) return scheduled;
  // 3) Flat DB price × qty.
  return (addon.price || 0) * qty;
};

const effectiveAddonQty = (service, addon, qtysMap) => {
  if (!isBrandingQtyAddon(service, addon)) return 1;
  return qtysMap?.[addon.id] || 1;
};

// ---------------------------------------------------------------------------
// Main Artwork — Additional Size / Format — package-aware format picker.
//
// Replaces the previous linear quantity stepper. Under both Event Creative
// (DB standalone item) and ACM's Campaign Design Package (frontend
// synthetic), the customer picks specific output sizes/formats to adapt
// their approved Main Artwork into. Included formats (those already part
// of the selected package's deliverables) are shown as ✓ INCLUDED and
// cannot contribute to the price. Custom Size expects width + height +
// unit before it counts as a valid paid selection.
//
// Storage of selection travels through cart / quote / order payloads so
// the designer sees the exact production brief on fulfilment.
// ---------------------------------------------------------------------------
const MAIN_ARTWORK_ITEM_NAME = "Main Artwork — Additional Size / Format";
const MAIN_ARTWORK_ITEM_CODES = new Set([
  "acm_main_artwork_additional_format",
  "ecp_main_artwork_additional_format",
  // BMD Promotional Artwork — Additional Size / Format (Mar 2026).
  // Wired into the SAME MainArtworkPickerRow so its qty stepper is
  // replaced by the CMS-driven multi-format selector that ACM and
  // Event Creative already use. Reuses the shared
  // `service.main_artwork_config.available_formats` list so Calvin
  // manages formats from a single Dashboard place. Pricing (AWG 75
  // each, `additional_price`) and included-format collision logic
  // continue to flow through the existing helpers — no bespoke BMD
  // pricing branch is added.
  "bmd_main_artwork_additional_format",
]);
const MAIN_ARTWORK_FORMAT_PRICE = 75;
// Approved USD display list price for the same AWG 75 format (Sep 2026).
const MAIN_ARTWORK_FORMAT_PRICE_USD = 42;
const MAIN_ARTWORK_UNITS = ["px", "mm", "cm", "in"];

// P1 Main Artwork CMS — the format library + package-inclusion mapping
// now live on the service record (`service.main_artwork_config.available_formats`
// and `kit.included_main_artwork_format_ids` / `standalone_item.included_main_artwork_format_ids`).
// These helpers are the single source of truth on the frontend — no
// hard-coded format list, no hard-coded package inclusion map.
const getMainArtworkFormatsList = (service) => {
  const list = (service?.main_artwork_config?.available_formats) || [];
  // Defensive: filter out inactive + sort by display_order (backend stores
  // canonical order but a manual pymongo edit could break it).
  return list.filter((f) => f.active !== false).slice().sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
};

const getMainArtworkFormatById = (service, id) => {
  if (!id) return null;
  return getMainArtworkFormatsList(service).find((f) => f.id === id) || null;
};

const getMainArtworkFormatGroups = (service) => {
  const seen = new Set();
  const groups = [];
  for (const f of getMainArtworkFormatsList(service)) {
    const g = f.group || "";
    if (!g || seen.has(g)) continue;
    seen.add(g);
    groups.push(g);
  }
  return groups;
};

// Format the customer-facing "label — dimensions" string. Custom Size uses
// the customer-supplied width/height/unit; presets read from the DB config.
const formatDimensions = (fmt) => {
  if (!fmt) return "";
  if (fmt.custom) return "";
  const w = fmt.width, h = fmt.height, u = fmt.unit || "";
  if (!w || !h) return "";
  return `${w} × ${h}${u ? ` ${u}` : ""}`;
};

const isMainArtworkItem = (item) =>
  !!item && MAIN_ARTWORK_ITEM_CODES.has((item.item_code || "").trim());

const isValidMainArtworkEntry = (entry) => {
  if (!entry || !entry.id) return false;
  if (entry.id !== "custom") return true;
  const w = String(entry.width || "").trim();
  const h = String(entry.height || "").trim();
  const u = String(entry.unit || "").trim();
  return w.length > 0 && h.length > 0 && MAIN_ARTWORK_UNITS.includes(u);
};

const countPaidMainArtworkFormats = (entries, includedSet, baseFormatId) => {
  const inc = includedSet || new Set();
  return (entries || []).reduce((n, e) => {
    if (!isValidMainArtworkEntry(e)) return n;
    if (e.id === "custom") {
      // Custom Size is instance-keyed by dimensions, not by shared id.
      // A custom additional never collides with a custom base — different
      // dimensions ARE different formats. Always count as paid.
      return n + 1;
    }
    if (baseFormatId && e.id === baseFormatId) return n;
    if (inc.has(e.id)) return n;
    return n + 1;
  }, 0);
};

// Per-format additional-price sum. Uses format.additional_price when the
// CMS supplies one (BMD wires 75/format, Billboard billed via primary
// surcharge below rather than the resize row) and falls back to the
// legacy uniform MAIN_ARTWORK_FORMAT_PRICE for services predating the
// per-format field. Ignores the base format and inclusion-covered formats
// exactly like countPaidMainArtworkFormats.
const sumPaidMainArtworkFormatPrice = (entries, includedSet, baseFormatId, formats) => {
  const inc = includedSet || new Set();
  const byId = new Map((formats || []).map((f) => [f.id, f]));
  const priceOf = (fid) => {
    const f = byId.get(fid);
    const p = f && typeof f.additional_price === "number" ? f.additional_price : null;
    return p !== null && p > 0 ? p : MAIN_ARTWORK_FORMAT_PRICE;
  };
  return (entries || []).reduce((n, e) => {
    if (!isValidMainArtworkEntry(e)) return n;
    if (e.id === "custom") return n + priceOf("custom");
    if (baseFormatId && e.id === baseFormatId) return n;
    if (inc.has(e.id)) return n;
    return n + priceOf(e.id);
  }, 0);
};

// Primary-format surcharge lookup. CMS-driven, no hard-coded prices in
// the frontend. Returns the surcharge for the format currently chosen as
// the customer's primary Main Artwork output (e.g. Billboard = 150).
const getPrimaryFormatSurcharge = (baseFormatId, formats) => {
  if (!baseFormatId) return 0;
  const byId = new Map((formats || []).map((f) => [f.id, f]));
  const f = byId.get(baseFormatId);
  const s = f && typeof f.primary_surcharge === "number" ? f.primary_surcharge : 0;
  return s > 0 ? s : 0;
};

// Which BMD items produce a "Main Artwork" (i.e. surface the base-format
// picker + trigger the primary_surcharge). CMS-driven via item_code so
// admins can wire more producers later without touching the frontend.
const BMD_MAIN_ARTWORK_PRODUCERS = new Set(["bmd_artwork_standard", "bmd_artwork_advanced"]);

const isMainArtworkProducingItem = (service, item) => {
  if (!service || !item) return false;
  if (service.slug === ACM_SLUG) return (item.item_code || "") === ACM_CAMPAIGN_PACKAGE_ITEM_CODE;
  if (service.slug === "business-marketing-design") return BMD_MAIN_ARTWORK_PRODUCERS.has(item.item_code || "");
  return false;
};

// Package-context helper: for the current service + selected kit, resolve
// the set of Main Artwork format ids that are delivered as their own
// standalone deliverables (so they cannot be sold again as an "additional
// format"). Reads DIRECTLY from the DB — kit.included_main_artwork_format_ids
// for kit-based services, or the ACM Campaign Design Package standalone
// item's field.
const getMainArtworkIncludedSet = (service, selectedKitId) => {
  if (!service) return new Set();
  if (service.slug === EVENT_CREATIVE_SLUG) {
    const kit = (service.kits || []).find((k) => k.id === selectedKitId);
    return new Set(kit?.included_main_artwork_format_ids || []);
  }
  if (service.slug === ACM_SLUG) {
    const cdp = (service.standalone_items || []).find(
      (i) => (i.item_code || "") === ACM_CAMPAIGN_PACKAGE_ITEM_CODE,
    );
    return new Set(cdp?.included_main_artwork_format_ids || []);
  }
  return new Set();
};

const formatMainArtworkEntry = (entry, service) => {
  if (!entry || !entry.id) return "";
  if (entry.id === "custom") {
    return `Custom Size — ${entry.width} × ${entry.height} ${entry.unit}`;
  }
  const meta = getMainArtworkFormatById(service, entry.id);
  if (!meta) return entry.id;
  const dims = formatDimensions(meta);
  return dims ? `${meta.label} — ${dims}` : meta.label;
};

// Structured meta for a Main Artwork entry — used in cart / order
// payloads so the checkout summary can render label + dimensions per
// format without re-parsing the label string.
const mainArtworkEntryMeta = (entry, service) => {
  if (!entry || !isValidMainArtworkEntry(entry)) return null;
  const base = { id: entry.id, label: formatMainArtworkEntry(entry, service) };
  if (entry.id === "custom") {
    return { ...base, width: entry.width, height: entry.height, unit: entry.unit };
  }
  const meta = getMainArtworkFormatById(service, entry.id);
  const dims = formatDimensions(meta);
  return dims ? { ...base, dimensions: dims } : base;
};

export default function PackageBuilder() {
  useCurrency(); // re-render all prices when the display currency changes
  const { slug } = useParams();
  const { addItem } = useCart();
  const navigate = useNavigate();
  const [service, setService] = useState(null);
  const [error, setError] = useState(null);
  const [settings, setSettings] = useState(null);

  // tiered / volume_tiered
  const [tierId, setTierId] = useState(null);
  const [selectedAddons, setSelectedAddons] = useState(() => new Set());

  // custom_items / kits
  const [selectedItemIds, setSelectedItemIds] = useState(() => new Set());
  const [selectedKitId, setSelectedKitId] = useState(null);
  // Per-item quantity map for quantity-enabled items (Event Creative:
  // Additional Campaign Graphic + Additional Menu / Signage Item +
  // Additional Size / Format). Absent
  // ids default to 0. Kept alongside selectedItemIds — the id lives in
  // selectedItemIds when qty > 0 so downstream "chosen items" logic still
  // works. Empty for every non-qty item and every non-Event-Creative
  // service.
  const [selectedItemQtys, setSelectedItemQtys] = useState(() => ({}));

  // Main Artwork — Additional Size / Format multi-select state. Keyed by
  // item id (works transparently for both the Event Creative DB item and
  // the ACM synthetic item since ids never collide across services).
  // Each entry is `{ id, width?, height?, unit? }`. Included formats
  // remain in the array so package-switching preserves the customer's
  // intent; the paid count is computed against the current package's
  // included set at read time.
  const [mainArtworkFormats, setMainArtworkFormats] = useState(() => ({}));

  // Per-page SEO — sets a unique <title>, meta description, canonical
  // URL and OG tags from CMS data. Canonical URL is the clean service
  // URL so any ?focus= link never becomes a distinct SEO landing page.
  const unavailable = error === "not_found";
  usePageSeo({
    title: unavailable ? "Service Unavailable — Ur Designs" : service?.name ? `${service.name} — Ur Designs` : undefined,
    description: unavailable ? "This service is not currently offered by Ur Designs." : service?.short_description || service?.long_description,
    path: !unavailable && slug ? `/services/${slug}` : undefined,
    image: service?.image || undefined,
    noindex: unavailable,
  });
  useJsonLd(buildServiceSchema(service, null));

  // GA4: configurator_start — fires once per configurator visit, only on
  // the customer's first item selection (never on load or programmatic
  // state changes).
  const _firedConfiguratorStart = React.useRef(false);
  useEffect(() => {
    if (!_firedConfiguratorStart.current && selectedItemIds.size > 0) {
      _firedConfiguratorStart.current = true;
      trackEvent("configurator_start", { service_slug: slug });
    }
  }, [selectedItemIds.size]);

  const updateMainArtworkFormats = (itemId, entries) => {
    setMainArtworkFormats((prev) => ({ ...prev, [itemId]: entries }));
  };

  // Main Artwork BASE format — the primary output format the customer
  // selects for their approved Main Artwork. One base format per
  // configurator visit; kept in root state so it persists across the
  // Event Creative kit switch (Essentials ↔ Campaign ↔ Full). Cleared
  // on service change (natural — the whole PackageBuilder state resets).
  // Shape: null | { id, width?, height?, unit? }. AWG 0 — included with
  // the package. Excluded from the paid Additional Size / Format count.
  const [mainArtworkBaseFormat, setMainArtworkBaseFormat] = useState(null);

  // Sync Main Artwork paid-format count into the shared qty/selection maps
  // so downstream pricing, review, cart, quote and order paths need no
  // per-service branching. Runs on every relevant change: format edits,
  // package/kit switch (which changes the included set), and service load.
  useEffect(() => {
    if (!service) return;
    const included = getMainArtworkIncludedSet(service, selectedKitId);
    const baseId = mainArtworkBaseFormat?.id || null;
    const ids = Object.keys(mainArtworkFormats);
    if (ids.length === 0) return;
    const desiredQty = {};
    for (const id of ids) {
      desiredQty[id] = countPaidMainArtworkFormats(mainArtworkFormats[id], included, baseId);
    }
    setSelectedItemQtys((prev) => {
      let changed = false;
      const nx = { ...prev };
      for (const [id, q] of Object.entries(desiredQty)) {
        if (q <= 0) {
          if (id in nx) { delete nx[id]; changed = true; }
        } else if (nx[id] !== q) {
          nx[id] = q; changed = true;
        }
      }
      return changed ? nx : prev;
    });
    setSelectedItemIds((prev) => {
      let changed = false;
      const nx = new Set(prev);
      for (const [id, q] of Object.entries(desiredQty)) {
        if (q > 0) { if (!nx.has(id)) { nx.add(id); changed = true; } }
        else if (nx.has(id)) { nx.delete(id); changed = true; }
      }
      return changed ? nx : prev;
    });
  }, [service, selectedKitId, mainArtworkFormats, mainArtworkBaseFormat]);

  // project brief (Step 3)
  const [brief, setBrief] = useState({
    business_name: "",
    industry: "",
    description: "",
    deadline: "",
    // Event Creative Phase C — one optional scheduling signal.
    // Rendered only when service.slug === "event-creative-promotion";
    // persisted into the shared meta.project_brief when non-empty.
    event_date: "",
    contact_name: "",
    email: "",
    phone: "",
    reference_images: [],
    reference_links: "",
  });
  // Website Design Pass 3C (Feb 2026) — structured Website Details.
  // Rendered only when service.slug === "website-design". Persisted
  // into every cart/quote line's `meta.project_brief.website_details`
  // so Admin Orders + email templates render a human-readable block
  // instead of reverse-engineering the request from a paragraph.
  const [websiteDetails, setWebsiteDetails] = useState({
    project_type: "",           // "new" | "redesign"
    current_url: "",             // required when project_type === "redesign"
    domain_status: "",           // "own" | "need_help"
    domain_url: "",              // required when domain_status === "own"
    content_readiness: "",       // "yes" | "partial" | "no"
    languages: "",               // "one" | "many"
    languages_detail: "",        // required when languages === "many"
    primary_goal: "",            // "leads" | "info" | "bookings" | "payments" | "sell" | "other"
    expected_pages: "",          // free text
    brand_assets: "",            // "yes" | "partial" | "no"
  });

  // Website Design Pass 3C — attach the structured Website Details
  // block to the shared brief so both cart + quote paths persist it
  // into `meta.project_brief.website_details` (Admin Orders +
  // emails render a human-readable Website Details section rather
  // than reverse-engineering the request from a paragraph).
  const briefForSubmit = useMemo(() => {
    if (service?.slug !== "website-design") return brief;
    return { ...brief, website_details: websiteDetails };
  }, [brief, websiteDetails, service?.slug]);

  // Step gating — the ONLY source of truth for "which step is done".
  // A step is confirmed by clicking Continue (or Skip on Step 2). Editing
  // any step sets currentStep back to it, which unmounts every step after
  // it (their state stays in memory but is no longer visible or reachable).
  //
  // History-backed step navigation (Feb 2026 UX fix):
  //   • The configurator lives at a single route (`/services/:slug`) but
  //     internally walks 3–4 sub-steps. Without history integration the
  //     browser Back button escapes the entire flow to /services after
  //     the customer has invested effort in Step 2/3.
  //   • On mount we tag the current history entry with `{pbStep: 1}` so
  //     the initial entry is identifiable. `confirmStep` pushes a new
  //     entry for each forward step; `editStep` uses `history.go(-delta)`
  //     to walk backwards so the browser back-arrow stays aligned with
  //     the chip-edit path. A `popstate` listener syncs currentStep to
  //     whichever entry the customer lands on. Selections and the brief
  //     never reset because they live in this component's state — only
  //     which step is visible changes.
  const [currentStep, setCurrentStep] = useState(() => {
    const s = typeof window !== "undefined" ? window.history.state?.pbStep : null;
    return typeof s === "number" && s >= 1 ? s : 1;
  });
  // Owner-QA back-nav guard (2026-03-05): once the customer has
  // advanced past Step 1 at least once, the "click the active tier
  // to deselect" toggle in TieredStep1 (originally added for
  // Website Design initial UX) must NOT fire — otherwise going
  // back to Step 1 from Review + a stray click on the active tier
  // resets `tierId` to null and the StickyTotalBar shows AWG 0.
  // Latch: sticky true once set, so a subsequent step-back cannot
  // clear it.
  const [step1EverConfirmed, setStep1EverConfirmed] = useState(false);

  useEffect(() => {
    if (typeof window === "undefined") return;
    // Tag the current history entry as step 1 so browser back from step
    // 2 lands here (still inside the configurator) instead of leaving.
    const state = window.history.state || {};
    if (state.pbStep === undefined) {
      window.history.replaceState({ ...state, pbStep: 1 }, "");
    }
    const onPop = (e) => {
      const s = e.state?.pbStep;
      if (typeof s === "number" && s >= 1) {
        setCurrentStep(s);
      }
      // No matching pbStep → customer navigated to an entry that
      // predates this configurator; React Router handles the route
      // unmount naturally.
    };
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  useEffect(() => {
    api
      .get(`/services/${slug}`)
      .then((r) => {
        setService(r.data);
        // Pass 3A (Feb 2026) — Website Design: Popular is presentation
        // metadata ONLY; neither Starter nor Business is auto-selected.
        // Batch 1 (Feb 2026) — Logo & Brand Identity joins this rule:
        // three tiers span AWG 1,250 → 4,250 and auto-selecting the
        // first one silently commits the customer to a paid decision
        // they did not make. Every other tiered service (none active
        // beyond these two at this time) keeps the fallback for
        // backwards compatibility.
        if (r.data.slug === "website-design" || r.data.slug === "logo-brand-identity") return;
        const popular = (r.data.tiers || []).find((t) => t.popular) || (r.data.tiers || [])[0];
        if (popular) setTierId(popular.id);
      })
      .catch((e) => setError(e?.response?.status === 404 ? "not_found" : "load_error"));
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => {});
  }, [slug]);

  // ECP kit-change synthetic cleanup: story upgrade + map synthetic ids
  // differ per kit (Essentials story ids vs Campaign story ids; Standard
  // + Complex on Essentials vs Complex Upgrade on Camp/Full). ALWAYS
  // drop these on any kit change so the customer re-selects the correct
  // duration/price under the new package. Every OTHER selection is
  // handled by the generic isAddonEligible cleanup effect below — no
  // per-name hardcoded rules needed.
  useEffect(() => {
    if (!service || service.slug !== EVENT_CREATIVE_SLUG) return;
    const toDrop = [];
    for (const id of selectedItemIds) {
      if (ECP_ALL_STORY_UPGRADE_IDS.has(id)) toDrop.push(id);
      else if (ECP_ALL_MAP_SYNTHETIC_IDS.has(id)) toDrop.push(id);
    }
    if (toDrop.length > 0) {
      const nextIds = new Set(selectedItemIds);
      for (const id of toDrop) nextIds.delete(id);
      setSelectedItemIds(nextIds);
      setSelectedItemQtys((prev) => {
        const nx = { ...prev };
        for (const id of toDrop) delete nx[id];
        return nx;
      });
    }
    // Intentionally excluding selectedItemIds from deps: we only want this
    // to fire on kit / service change, not every checkbox toggle.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedKitId, service?.slug]);

  // Generic DB-dependency cleanup: whenever the selection context
  // (kit + selected items) changes, drop any selected item whose
  // depends_on_item_code / depends_on_item_code_any is no longer
  // satisfied. This is the fail-safe that guarantees the frontend
  // never carries an ineligible selection into the running total,
  // review page or cart — regardless of which service is loaded or
  // which specialised UX layer added the selection.
  useEffect(() => {
    if (!service) return;
    const activeCodes = getActiveItemCodes(service, selectedKitId, selectedItemIds);
    const items = service.standalone_items || [];
    const toDrop = [];
    for (const id of selectedItemIds) {
      // Resolve the authoritative DB view for this selection (real DB
      // row OR synthetic view model surfacing a DB row).
      let view = items.find((i) => i.id === id) || null;
      if (!view) view = getMigratedItemView(service, id);
      if (!view) continue;
      // A dependency check that references the item's OWN item_code
      // must not falsely satisfy itself — evaluate against the active
      // codes minus this item's own code.
      const scoped = new Set(activeCodes);
      if (view.item_code) scoped.delete(view.item_code);
      if (!isAddonEligible(view, scoped)) toDrop.push(id);
    }
    if (toDrop.length > 0) {
      setSelectedItemIds((prev) => {
        const nx = new Set(prev);
        for (const id of toDrop) nx.delete(id);
        return nx;
      });
      setSelectedItemQtys((prev) => {
        const nx = { ...prev };
        for (const id of toDrop) delete nx[id];
        return nx;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [service, selectedKitId, selectedItemIds]);

  // ACM package-dependency cleanup: whenever the Campaign Design Package is
  // no longer selected, drop every ACM package-dependent synthetic id
  // (story upgrades + qty add-ons) from selectedItemIds and their qtys so
  // no orphaned charge remains in totals / review / cart / quote.
  useEffect(() => {
    if (!service || service.slug !== ACM_SLUG) return;
    const campaignItem = (service.standalone_items || []).find(
      (i) => i.item_code === ACM_CAMPAIGN_PACKAGE_ITEM_CODE,
    );
    const packageActive = campaignItem && selectedItemIds.has(campaignItem.id);
    if (packageActive) return;
    const toDrop = [];
    for (const id of selectedItemIds) {
      if (ACM_ALL_DEPENDENT_IDS.has(id)) toDrop.push(id);
    }
    if (toDrop.length === 0) return;
    setSelectedItemIds((prev) => {
      const nx = new Set(prev);
      for (const id of toDrop) nx.delete(id);
      return nx;
    });
    setSelectedItemQtys((prev) => {
      const nx = { ...prev };
      for (const id of toDrop) delete nx[id];
      return nx;
    });
  }, [selectedItemIds, service]);

  // Branding package-dependent cleanup: whenever the customer clears their
  // Vehicle Wrap base selection under Logo Design / Brand Identity (Complete
  // Brand System already includes Simple wrap), drop the Additional Vehicle
  // Adaptation quantity so no orphaned adaptation charge survives in totals,
  // review, cart or quote.
  useEffect(() => {
    if (!service || service.slug !== BRANDING_SLUG) return;
    const tier = (service.tiers || []).find((t) => t.id === tierId);
    if (tier?.name === "Complete Brand System") return;
    const addons = service.addons || [];
    const adapt = addons.find((a) => (a.item_code || "") === BRANDING_ADDITIONAL_VEHICLE_ADAPT_CODE);
    if (!adapt) return;
    const simple = addons.find((a) => (a.item_code || "") === "brand_vehicle_wrap_simple");
    const complex = addons.find((a) => (a.item_code || "") === "brand_vehicle_wrap_complex");
    const wrapSelected =
      (simple && selectedAddons.has(simple.id)) ||
      (complex && selectedAddons.has(complex.id));
    if (wrapSelected) return;
    const hasSelection = selectedAddons.has(adapt.id) || !!selectedItemQtys[adapt.id];
    if (!hasSelection) return;
    setSelectedAddons((prev) => {
      const nx = new Set(prev);
      nx.delete(adapt.id);
      return nx;
    });
    setSelectedItemQtys((prev) => {
      const nx = { ...prev };
      delete nx[adapt.id];
      return nx;
    });
  }, [service, tierId, selectedAddons, selectedItemQtys]);

  // Tier-change cleanup: switching packages should never carry a stale
  // add-on that the new tier's tier_ids would silently hide. Drop any
  // selectedAddons whose tier_ids no longer include the current tier.
  // Pass 3B (Feb 2026) — generalised to every tiered service so
  // Website's Business-only Additional Page auto-clears when the
  // customer switches back to Starter.
  //
  // Feb 2026 owner directive — package deselect (tierId → null) must
  // also fully reset Step 2 so Running Total lands on AWG 0 and
  // Continue disables. Every add-on is a companion to a selected
  // package; there is no valid "add-on without a package" state.
  useEffect(() => {
    if (!service) return;
    if (!tierId) {
      // Deselect — clear every selected add-on + its qty. No-op when
      // Step 2 is already clean so re-selecting a tier immediately
      // afterwards doesn't churn state.
      if (selectedAddons.size === 0 && Object.keys(selectedItemQtys).length === 0) return;
      setSelectedAddons((prev) => (prev.size === 0 ? prev : new Set()));
      setSelectedItemQtys((prev) => {
        if (!prev || Object.keys(prev).length === 0) return prev;
        // Preserve any tier-independent item qtys (kits/custom_items
        // flows would use this hook if we ever share state — this
        // block only runs for tiered services because the effect is
        // gated on service.tiers-driven `tierId`). Wipe all here.
        return {};
      });
      return;
    }
    const toDrop = [];
    for (const a of service.addons || []) {
      if (!selectedAddons.has(a.id)) continue;
      const tierIds = a.tier_ids || [];
      if (tierIds.length > 0 && !tierIds.includes(tierId)) toDrop.push(a.id);
    }
    if (toDrop.length === 0) return;
    setSelectedAddons((prev) => {
      const nx = new Set(prev);
      for (const id of toDrop) nx.delete(id);
      return nx;
    });
    setSelectedItemQtys((prev) => {
      const nx = { ...prev };
      for (const id of toDrop) delete nx[id];
      return nx;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tierId, service?.slug]);

  // Website Design Pass 3C — brief-driven multilingual estimate.
  // When the Website Brief answer is "More than 1 language", activate
  // the canonical `web_multilingual` estimate-only add-on. Switch back
  // to "1 language" ⇒ remove it. Customer never picks this in Step 2
  // (it's `driven_by: "brief"` and filtered out of visibleAddons).
  useEffect(() => {
    if (!service || service.slug !== "website-design") return;
    const multilingual = (service.addons || []).find(
      (a) => a.item_code === "web_multilingual"
    );
    if (!multilingual) return;
    const shouldBeSelected = websiteDetails.languages === "many";
    const alreadySelected = selectedAddons.has(multilingual.id);
    if (shouldBeSelected && !alreadySelected) {
      setSelectedAddons((prev) => new Set(prev).add(multilingual.id));
    } else if (!shouldBeSelected && alreadySelected) {
      setSelectedAddons((prev) => {
        const nx = new Set(prev);
        nx.delete(multilingual.id);
        return nx;
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [websiteDetails.languages, service?.slug, service?.addons?.length]);

  // Website Design Pass 3C — conditional field cleanup.
  // Switching Redesign → New clears the stale current_url; switching
  // Own → Need help clears the stale domain_url; switching Many → One
  // clears the stale languages_detail. Hidden fields must never
  // block submission.
  useEffect(() => {
    setWebsiteDetails((prev) => {
      const nx = { ...prev };
      if (nx.project_type !== "redesign" && nx.current_url) nx.current_url = "";
      if (nx.domain_status !== "own" && nx.domain_url) nx.domain_url = "";
      if (nx.languages !== "many" && nx.languages_detail) nx.languages_detail = "";
      return nx;
    });
  }, [websiteDetails.project_type, websiteDetails.domain_status, websiteDetails.languages]);

  // Filter add-ons to the currently-selected tier when tier_ids restrict
  // visibility (Complete Brand System's upgrade add-ons). Empty tier_ids
  // = shown for every tier. Computed BEFORE early returns so rules-of-hooks
  // ordering stays stable across renders. For Branding, layer additional
  // dynamic rules on top: qty-enabled markers, wrap Simple/Complex mutex,
  // and hiding Additional Vehicle Adaptation until a wrap base is present
  // (Complete already includes Simple wrap, so it stays visible there).
  const visibleAddons = useMemo(() => {
    const base = ((service?.addons) || []).filter(
      (a) => !a.tier_ids || a.tier_ids.length === 0 || (tierId && a.tier_ids.includes(tierId))
    );
    // Website Design Pass 3B — decorate non-Branding services with the
    // generic DB fields so AddonsGrid's shared mutex + quantity paths
    // (already wired for Branding) work for any CMS-managed add-on.
    if (service?.slug !== BRANDING_SLUG) {
      return base
        // Pass 3C — hide add-ons that a customer-visible section other
        // than Step 2 activates (e.g. `web_multilingual` is driven by
        // the Website Brief). Prevents double-selection UX.
        .filter((a) => (a.driven_by || "configurator") === "configurator")
        // Pass 3D (Website Design — Commercial Scope Refinement V1):
        // Full Online Store is Request Estimate and belongs on its own
        // visually-distinguished card, NOT on the fixed-price add-ons
        // shelf. Rendered separately by <WebsiteFullOnlineStoreCard/>
        // below AddonsGrid — its selection/deselection still writes to
        // the same `selectedAddons` set so the review + quote pipeline
        // are unchanged.
        .filter((a) => !(service?.slug === "website-design" && (a.item_code || "") === "web_full_online_store"))
        .map((a) => ({
          ...a,
          _quantityEnabled: !!a.quantity_enabled,
          _mutexGroup: (a.option_group || "").trim() || a._mutexGroup,
        }));
    }
    const tier = (service.tiers || []).find((t) => t.id === tierId);
    const tierName = tier?.name;
    const simple = base.find((a) => (a.item_code || "") === "brand_vehicle_wrap_simple");
    const complex = base.find((a) => (a.item_code || "") === "brand_vehicle_wrap_complex");
    const wrapSelected =
      (simple && selectedAddons.has(simple.id)) ||
      (complex && selectedAddons.has(complex.id));
    return base
      .map((a) => {
        // DB-authoritative flags: quantity_enabled + option_group live on
        // every Branding addon after migrate_qty_pricing_cutover_v1.
        const qtyEnabled = !!a.quantity_enabled;
        const isAdapt = (a.item_code || "") === BRANDING_ADDITIONAL_VEHICLE_ADAPT_CODE;
        // Under Logo Design / Brand Identity, the adaptation add-on is
        // meaningless without a base wrap concept. Under Complete Brand
        // System (which includes Simple wrap) it stays visible.
        const adaptHidden =
          isAdapt && tierName !== "Complete Brand System" && !wrapSelected;
        // Complete Brand System — "Additional …" framing overlay so the
        // shared DB item never reads as though the customer is buying
        // the first bundled unit again.
        const completeOverride =
          tierName === "Complete Brand System"
            ? BRANDING_COMPLETE_OVERRIDES[a.name]
            : null;
        return {
          ...a,
          _quantityEnabled: qtyEnabled,
          // option_group already carries the wrap mutex from the DB after
          // migrate_qty_pricing_cutover_v1; no per-name mutex tag needed.
          _mutexGroup: (a.option_group || "").trim() || a._mutexGroup,
          _hidden: adaptHidden,
          _displayName: completeOverride?.displayName || a._displayName,
          _description: completeOverride?.description || a._description,
        };
      })
      .filter((a) => !a._hidden);
  }, [service, tierId, selectedAddons]);

  if (error === "not_found") {
    return (
      <div className="min-h-[60vh] grid place-items-center px-6 text-center">
        <div>
          <div className="text-[#A3A3A3] text-sm tracking-widest uppercase">Service unavailable</div>
          <p className="mt-2 text-white">This service is not currently accepting new orders.</p>
          <Link to="/services" className="ur-btn-secondary mt-6 inline-flex">
            ← All Services <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    );
  }
  if (!service) {
    return <BrandedLoader testid="package-builder-loading" />;
  }

  const pm = service.pricing_model || "tiered";
  const quoteModeStatic = isQuoteMode(service);
  // BMD architecture v1 (Feb 2026) — mixed-mode partition. Selecting
  // "Other Design" no longer nukes the fixed-price rendering of any
  // other selected line. `quoteMode` (boolean) still means "any
  // estimate item present" for the many legacy paths that treat that
  // as "hide prices / show Request Estimate CTA"; the new
  // `estimatePartition` object tells the Review + StickyTotalBar
  // exactly which state to render (fixed-only, estimate-only, or
  // mixed).
  const estimatePartition = selectionEstimatePartition({
    service, pm, tierId, selectedAddons, selectedItemIds, selectedKitId,
  });
  const quoteModeFromSelection = estimatePartition.hasEstimate;
  const quoteMode = quoteModeStatic || quoteModeFromSelection;
  const mixedMode = !quoteModeStatic && estimatePartition.hasEstimate && estimatePartition.hasFixed;
  const hasAddons = pm === "tiered" || pm === "volume_tiered"
    ? visibleAddons.length > 0
    : pm === "kits"
      ? (service.standalone_items || []).length > 0
      : false;

  // Ordered list of step definitions for this service. `hasAddons=false`
  // (e.g. custom_items) collapses the sequence to Step 1 → Step 3 → Step 4;
  // internal numbering renumbers to 1..3 for a clean UI.
  const step1Label = pm === "kits" ? "Select Kit" : pm === "custom_items" ? "Select Items" : "Select Package";
  const step2Label = pm === "kits" ? "Add More to Your Package" : "Add Extras";
  const step2Sub = pm === "kits"
    ? "Optional add-ons to expand your selected event package."
    : "Optional — pick as many or as few extras as you'd like.";
  const steps = [
    { n: 1, kind: "select", label: step1Label, sub: step1Sub(pm) },
    ...(hasAddons ? [{ n: 2, kind: "extras", label: step2Label, sub: step2Sub }] : []),
    { n: hasAddons ? 3 : 2, kind: "brief",  label: "Tell Us About Your Project", sub: "A few details so we can hit the ground running." },
    { n: hasAddons ? 4 : 3, kind: "review", label: "Review & Submit",             sub: "Last look before it lands in your cart." },
  ];
  const reviewStepN = steps[steps.length - 1].n;

  // Selection state for chip content + Continue-enable rules.
  const step1HasSelection =
    (pm === "tiered" || pm === "volume_tiered") ? !!tierId :
    pm === "custom_items" ? selectedItemIds.size > 0 :
    pm === "kits" ? (!!selectedKitId || selectedItemIds.size > 0) : false;

  const step2HasSelection =
    (pm === "tiered" || pm === "volume_tiered") ? selectedAddons.size > 0 :
    pm === "kits" ? selectedItemIds.size > 0 : false;

  const step3Ready =
    !!brief.business_name.trim() &&
    !!brief.industry &&
    !!brief.description.trim() &&
    !!brief.contact_name.trim() &&
    isValidEmail(brief.email) &&
    isValidPhone(brief.phone) &&
    // Website Design Pass 3C — additional required Website Details
    // fields when the customer is on Website Design. Conditional
    // reveals (current_url / domain_url / languages_detail) are
    // required only when their parent choice is picked; the cleanup
    // effect blanks stale values so hidden fields never block submit.
    (
      service?.slug !== "website-design" ||
      (
        !!websiteDetails.project_type &&
        (websiteDetails.project_type !== "redesign" || !!websiteDetails.current_url.trim()) &&
        !!websiteDetails.domain_status &&
        (websiteDetails.domain_status !== "own" || !!websiteDetails.domain_url.trim()) &&
        !!websiteDetails.content_readiness &&
        !!websiteDetails.languages &&
        (websiteDetails.languages !== "many" || !!websiteDetails.languages_detail.trim()) &&
        !!websiteDetails.primary_goal &&
        !!websiteDetails.brand_assets
      )
    );

  const confirmStep = (n) => {
    const idx = steps.findIndex((s) => s.n === n);
    const next = steps[idx + 1];
    if (next) {
      setCurrentStep(next.n);
      if (n === 1) setStep1EverConfirmed(true);
      // Push a history entry for the new step so the browser Back
      // button (and edge-swipe on mobile) returns to the previous
      // configurator step instead of escaping to /services.
      if (typeof window !== "undefined") {
        const state = window.history.state || {};
        window.history.pushState({ ...state, pbStep: next.n }, "");
      }
    }
  };
  const editStep = (n) => {
    // Chip-based edit jumps directly to an earlier step. Walk the
    // browser history back by the same delta so the physical Back
    // arrow stays in sync with which step the customer is on. The
    // popstate handler will call setCurrentStep(n) once the walk
    // settles; a forward jump (delta ≤ 0) falls back to plain state.
    const delta = currentStep - n;
    if (typeof window !== "undefined" && delta > 0) {
      window.history.go(-delta);
    } else {
      setCurrentStep(n);
    }
  };

  // Quantity setter for qty-enabled items. Clamps to a permissive
  // [0, 99] band; the QuantityStepper widget owns the per-item
  // quantity_min/quantity_max ceiling from the DB row so it stops
  // incrementing before the setter is ever called out of bounds.
  // Keeps selectedItemIds in sync so downstream selection logic (chip
  // label, Continue-enable, cart write) treats the row as selected
  // when qty > 0.
  const setItemQuantity = (id, next) => {
    const clamped = Math.max(0, Math.min(99, next | 0));
    setSelectedItemQtys((prev) => {
      const nx = { ...prev };
      if (clamped <= 0) delete nx[id];
      else nx[id] = clamped;
      return nx;
    });
    setSelectedItemIds((prev) => {
      const nx = new Set(prev);
      if (clamped <= 0) nx.delete(id);
      else nx.add(id);
      return nx;
    });
  };

  // Quantity setter for qty-enabled add-ons (Branding). Shares the same
  // qty state as items (keyed by id, ids don't collide across services)
  // but mirrors add/remove into `selectedAddons` (Set) instead of
  // `selectedItemIds`, matching the tiered/volume_tiered selection model.
  const setAddonQuantity = (id, next) => {
    const clamped = Math.max(0, Math.min(99, next | 0));
    setSelectedItemQtys((prev) => {
      const nx = { ...prev };
      if (clamped <= 0) delete nx[id];
      else nx[id] = clamped;
      return nx;
    });
    setSelectedAddons((prev) => {
      const nx = new Set(prev);
      if (clamped <= 0) nx.delete(id);
      else nx.add(id);
      return nx;
    });
  };

  return (
    <div data-testid={`package-builder-${service.slug}`} className="bg-[#0F0F0F] min-h-screen pb-40">
      <CompactHeader service={service} showBackToServices={currentStep === 1} />
      {quoteModeFromSelection && !quoteModeStatic && (
        <div
          data-testid="estimate-mode-notice"
          data-mixed={mixedMode ? "true" : undefined}
          className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 pt-6"
        >
          <div className="rounded-2xl border border-[#FF6600]/40 bg-[#1A1209] p-4 flex items-start gap-3 text-sm">
            <Sparkles className="w-4 h-4 text-[#FF6600] mt-0.5 shrink-0" />
            <div>
              <div className="font-bold text-white">
                {mixedMode ? "Combined project — some fixed, some to estimate" : "Estimate mode"}
              </div>
              <div className="text-[#D1D1D1] mt-0.5">
                {mixedMode
                  ? "Your selection combines known-price work with items we'll price on request. Your fixed subtotal stays visible; the estimated pieces will be quoted alongside — no charge until you approve."
                  : "Your selection includes items priced on request. Complete your project brief and we'll send a tailored estimate — no charge until you approve."}
              </div>
            </div>
          </div>
        </div>
      )}
      <section className="max-w-[900px] mx-auto px-4 sm:px-6 lg:px-8 py-10 lg:py-14 space-y-4">
        {steps.map((s) => {
          if (s.n < currentStep) {
            return (
              <StepChip
                key={s.n}
                step={s}
                service={service}
                pm={pm}
                quoteMode={quoteMode}
                tierId={tierId}
                selectedAddons={selectedAddons}
                selectedItemIds={selectedItemIds}
                selectedItemQtys={selectedItemQtys}
                selectedKitId={selectedKitId}
                brief={brief}
                onEdit={() => editStep(s.n)}
              />
            );
          }
          if (s.n === currentStep) {
            return (
              <ActiveStep
                key={s.n}
                step={s}
                service={service}
                pm={pm}
                quoteMode={quoteMode}
                mixedMode={mixedMode}
                visibleAddons={visibleAddons}
                tierId={tierId} setTierId={setTierId}
                step1EverConfirmed={step1EverConfirmed}
                selectedAddons={selectedAddons} setSelectedAddons={setSelectedAddons}
                selectedItemIds={selectedItemIds} setSelectedItemIds={setSelectedItemIds}
                selectedItemQtys={selectedItemQtys} setItemQuantity={setItemQuantity}
                setAddonQuantity={setAddonQuantity}
                mainArtworkFormats={mainArtworkFormats}
                updateMainArtworkFormats={updateMainArtworkFormats}
                mainArtworkBaseFormat={mainArtworkBaseFormat}
                setMainArtworkBaseFormat={setMainArtworkBaseFormat}
                selectedKitId={selectedKitId} setSelectedKitId={setSelectedKitId}
                brief={brief} setBrief={setBrief}
                briefForSubmit={briefForSubmit}
                websiteDetails={websiteDetails} setWebsiteDetails={setWebsiteDetails}
                settings={settings}
                step1HasSelection={step1HasSelection}
                step2HasSelection={step2HasSelection}
                step3Ready={step3Ready}
                onConfirm={() => confirmStep(s.n)}
                addItem={addItem}
                navigate={navigate}
              />
            );
          }
          return null; // future step — not in the DOM
        })}
        {currentStep > 1 && (
          <CopyChipsButton
            service={service}
            pm={pm}
            tierId={tierId}
            selectedAddons={selectedAddons}
            selectedItemIds={selectedItemIds}
            selectedItemQtys={selectedItemQtys}
            selectedKitId={selectedKitId}
            brief={brief}
            steps={steps}
            currentStep={currentStep}
            mainArtworkBaseFormat={mainArtworkBaseFormat}
          />
        )}
      </section>
      <ServiceHelpLinks className="pb-28" />
      <StickyTotalBar
        service={service}
        pm={pm}
        quoteMode={quoteMode}
        mixedMode={mixedMode}
        tierId={tierId}
        selectedAddons={selectedAddons}
        selectedItemIds={selectedItemIds}
        selectedItemQtys={selectedItemQtys}
        selectedKitId={selectedKitId}
        brief={briefForSubmit}
        addItem={addItem}
        navigate={navigate}
        step1HasSelection={step1HasSelection}
        step3Ready={step3Ready}
        currentStep={currentStep}
        currentStepKind={(steps.find((s) => s.n === currentStep) || {}).kind}
        atReview={currentStep === reviewStepN}
        onStickyAdvance={() => confirmStep(currentStep)}
        mainArtworkFormats={mainArtworkFormats}
        mainArtworkBaseFormat={mainArtworkBaseFormat}
      />
    </div>
  );
}

// ---------- Active step body — dispatches on step.kind ----------
const ActiveStep = ({
  step, service, pm, quoteMode, mixedMode, visibleAddons,
  tierId, setTierId,
  step1EverConfirmed,
  selectedAddons, setSelectedAddons,
  selectedItemIds, setSelectedItemIds,
  selectedItemQtys, setItemQuantity,
  setAddonQuantity,
  mainArtworkFormats, updateMainArtworkFormats,
  mainArtworkBaseFormat, setMainArtworkBaseFormat,
  selectedKitId, setSelectedKitId,
  brief, setBrief, settings,
  briefForSubmit,
  websiteDetails, setWebsiteDetails,
  step1HasSelection, step2HasSelection, step3Ready,
  onConfirm, addItem, navigate,
}) => {
  const bodyRef = useRef(null);
  // When a step mounts as active, scroll it into view — the chips above
  // may have moved during collapse. Skip on Step 1 (initial render).
  useEffect(() => {
    if (step.n === 1) return;
    if (bodyRef.current) {
      const y = bodyRef.current.getBoundingClientRect().top + window.pageYOffset - 96;
      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, [step.n]);

  return (
    <div
      ref={bodyRef}
      data-testid={`step-active-${step.n}`}
      data-step-kind={step.kind}
      className="ur-card p-5 sm:p-8 border-[#FF6600]/40"
    >
      <div className="flex items-baseline gap-3">
        <span className="w-8 h-8 rounded-full bg-[#FF6600] text-black font-display font-black grid place-items-center text-sm shrink-0">
          {step.n}
        </span>
        <div className="min-w-0">
          <h2 className="font-display text-xl sm:text-2xl font-bold tracking-tight leading-tight">{step.label}</h2>
          {step.sub && <p className="text-sm text-[#A3A3A3] mt-1">{step.sub}</p>}
        </div>
      </div>

      <div className="mt-6">
        {step.kind === "select" && (
          <>
            {(pm === "tiered" || pm === "volume_tiered") && (
              <>
                <TieredStep1 service={service} quoteMode={quoteMode} tierId={tierId} setTierId={setTierId} step1EverConfirmed={step1EverConfirmed} />
                <WebsiteCareDisclosurePanel service={service} />
              </>
            )}
            {pm === "custom_items" && (
              <CustomItemsStep1 service={service} quoteMode={quoteMode} selectedItemIds={selectedItemIds} setSelectedItemIds={setSelectedItemIds} selectedItemQtys={selectedItemQtys} setItemQuantity={setItemQuantity} mainArtworkFormats={mainArtworkFormats} updateMainArtworkFormats={updateMainArtworkFormats} mainArtworkBaseFormat={mainArtworkBaseFormat} setMainArtworkBaseFormat={setMainArtworkBaseFormat} />
            )}
            {pm === "kits" && (
              <KitsStep1 service={service} quoteMode={quoteMode} selectedKitId={selectedKitId} setSelectedKitId={setSelectedKitId} />
            )}
            <div className="mt-8 flex justify-end">
              <button
                data-testid={`step-${step.n}-continue`}
                onClick={onConfirm}
                disabled={!step1HasSelection}
                className="ur-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </>
        )}

        {step.kind === "extras" && (
          <>
            {(pm === "tiered" || pm === "volume_tiered") && (
              <>
                <BrandingInclusionsStrip service={service} tierId={tierId} />
                {service?.slug === BRANDING_SLUG ? (
                  <BrandingProgressiveAddons
                    addons={visibleAddons || service.addons || []}
                    quoteMode={quoteMode}
                    selectedAddons={selectedAddons}
                    setSelectedAddons={setSelectedAddons}
                    quantitiesById={selectedItemQtys}
                    setQuantity={setAddonQuantity}
                  />
                ) : (
                  <AddonsGrid
                    addons={visibleAddons || service.addons || []}
                    quoteMode={quoteMode}
                    selectedAddons={selectedAddons}
                    setSelectedAddons={setSelectedAddons}
                    quantitiesById={selectedItemQtys}
                    setQuantity={setAddonQuantity}
                  />
                )}
                <WebsiteFullOnlineStoreCard
                  service={service}
                  selectedAddons={selectedAddons}
                  setSelectedAddons={setSelectedAddons}
                />
                <WebsiteAdvancedProjectPanel service={service} />
              </>
            )}
            {pm === "kits" && (
              <>
                <EventCreativeInclusionsStrip service={service} selectedKitId={selectedKitId} />
                {selectedKitId && service?.slug === EVENT_CREATIVE_SLUG && (
                  <MainArtworkBaseFormatSelector
                    value={mainArtworkBaseFormat}
                    onChange={setMainArtworkBaseFormat}
                    packageInclusionsSet={getMainArtworkIncludedSet(service, selectedKitId)}
                    formats={getMainArtworkFormatsList(service)}
                    formatGroups={getMainArtworkFormatGroups(service)}
                    testidPrefix="ec-main-artwork-base"
                  />
                )}
                <ItemsGrid
                  items={buildEventCreativeStep2Items(service, selectedKitId)}
                  quoteMode={quoteMode}
                  selectedItemIds={selectedItemIds}
                  setSelectedItemIds={setSelectedItemIds}
                  selectedItemQtys={selectedItemQtys}
                  setItemQuantity={setItemQuantity}
                  testidPrefix="kit-item"
                  mainArtworkFormatsById={mainArtworkFormats}
                  updateMainArtworkFormats={updateMainArtworkFormats}
                  mainArtworkIncludedSet={getMainArtworkIncludedSet(service, selectedKitId)}
                  mainArtworkBaseFormatId={mainArtworkBaseFormat?.id || null}
                  mainArtworkFormatsList={getMainArtworkFormatsList(service)}
                  mainArtworkFormatGroups={getMainArtworkFormatGroups(service)}
                />
              </>
            )}
            <div className="mt-8 flex items-center justify-end gap-4 sm:gap-6">
              <button
                data-testid={`step-${step.n}-skip`}
                onClick={onConfirm}
                // Skip is a secondary convenience action — render as
                // a compact underlined text link, not a pill button,
                // so it never dominates the primary orange Continue
                // CTA next to it. `whitespace-nowrap` keeps the label
                // on one line at every mobile width (360px+).
                className="text-sm font-semibold text-[#A3A3A3] hover:text-white underline underline-offset-[6px] decoration-[#404040] hover:decoration-[#FF6600] transition-colors whitespace-nowrap py-3"
                type="button"
              >
                Skip this step
              </button>
              <button
                data-testid={`step-${step.n}-continue`}
                onClick={onConfirm}
                disabled={!step2HasSelection}
                className="ur-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </>
        )}

        {step.kind === "brief" && (
          <>
            <ProjectBriefForm
              brief={brief}
              setBrief={setBrief}
              settings={settings}
              service={service}
              websiteDetails={websiteDetails}
              setWebsiteDetails={setWebsiteDetails}
            />
            {!step3Ready && (
              <p data-testid={`step-${step.n}-hint`} className="mt-3 text-xs text-[#A3A3A3]">
                Complete the required fields above (marked <span className="text-[#FF6600]">*</span>) to continue.
              </p>
            )}
            <div className="mt-6 flex justify-end">
              <button
                data-testid={`step-${step.n}-continue`}
                onClick={onConfirm}
                disabled={!step3Ready}
                className="ur-btn-primary disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Continue <ArrowRight className="w-4 h-4" />
              </button>
            </div>
          </>
        )}

        {step.kind === "review" && (
          <Step4Review
            service={service} pm={pm} quoteMode={quoteMode} mixedMode={mixedMode}
            tierId={tierId} selectedAddons={selectedAddons}
            selectedItemIds={selectedItemIds} selectedItemQtys={selectedItemQtys}
            selectedKitId={selectedKitId}
            mainArtworkFormats={mainArtworkFormats}
            mainArtworkBaseFormat={mainArtworkBaseFormat}
            brief={briefForSubmit} addItem={addItem} navigate={navigate}
          />
        )}
      </div>
    </div>
  );
};

// ---------- Collapsed step chip ----------
const StepChip = ({ step, service, pm, quoteMode, tierId, selectedAddons, selectedItemIds, selectedItemQtys, selectedKitId, brief, onEdit }) => {
  const { label, price, priceUsd, priceSign } = summarizeStep({ step, service, pm, tierId, selectedAddons, selectedItemIds, selectedItemQtys, selectedKitId, brief });
  // Zero-price cases never render a price column — a "+AWG 0" line implies math
  // happened when nothing was added. Absence is the honest signal. Quote-mode
  // categories also never render prices on chips (there's no math to show —
  // pricing is deferred to the confirmation email after Calvin reviews the
  // brief).
  const showPrice = !quoteMode && typeof price === "number" && price > 0;
  return (
    <div
      data-testid={`step-chip-${step.n}`}
      data-step-kind={step.kind}
      className="flex items-center justify-between gap-3 rounded-2xl border border-[#262626] bg-[#141414] px-4 sm:px-5 py-3 hover:border-[#FF6600]/40 transition-colors"
    >
      <div className="flex items-center gap-3 min-w-0 flex-1">
        <span className="w-7 h-7 rounded-full bg-emerald-500 text-black grid place-items-center shrink-0">
          <Check className="w-3.5 h-3.5" strokeWidth={3} />
        </span>
        <div className="min-w-0 flex-1">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#7A7A7A]">Step {step.n} · {step.label}</div>
          <div data-testid={`step-chip-${step.n}-summary`} className="text-sm text-white truncate">{label}</div>
        </div>
      </div>
      {showPrice && (
        <div
          data-testid={`step-chip-${step.n}-price`}
          className="text-sm font-bold text-[#A3A3A3] tabular-nums whitespace-nowrap shrink-0"
        >
          {priceSign || ""}{listMoney(price, priceUsd)}
        </div>
      )}
      <button
        data-testid={`step-chip-${step.n}-edit`}
        onClick={onEdit}
        type="button"
        className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF6600] hover:text-white transition-colors shrink-0"
      >
        Edit
      </button>
    </div>
  );
};

const summarizeStep = ({ step, service, pm, tierId, selectedAddons, selectedItemIds, selectedItemQtys, selectedKitId, brief }) => {
  // US$ display sum for the step chip — approved list prices, undefined
  // (→ exact conversion) when any part lacks one.
  const usdSum = (pairs) => {
    let sum = 0;
    for (const [src, amt] of pairs) {
      const a = Number(amt) || 0;
      if (a <= 0) continue;
      const u = src ? usdFor(src, a) : undefined;
      if (!(u > 0)) return undefined;
      sum += u;
    }
    return sum || undefined;
  };
  if (step.kind === "select") {
    if (pm === "tiered" || pm === "volume_tiered") {
      const t = (service.tiers || []).find((x) => x.id === tierId);
      return t
        ? { label: t.name, price: t.price, priceUsd: t.price_usd || undefined, priceSign: null }
        : { label: "—", price: null, priceSign: null };
    }
    if (pm === "custom_items") {
      const items = materializeSelectedItems(service, selectedItemIds);
      if (items.length === 0) return { label: "—", price: null, priceSign: null };
      const total = items.reduce((s, i) => s + effectiveItemPrice(service, i, selectedItemQtys), 0);
      return {
        label: `${items.length} item${items.length > 1 ? "s" : ""}`,
        price: total,
        priceUsd: usdSum(items.map((i) => [i, effectiveItemPrice(service, i, selectedItemQtys)])),
        priceSign: null,
      };
    }
    if (pm === "kits") {
      // Step 1 chip must ONLY describe the Step 1 decision (the kit).
      // Standalone items are the Step 2 "Build Your Own" decision — they
      // get their own chip; combining them here would double-count in the
      // visible summary stack.
      const kit = (service.kits || []).find((k) => k.id === selectedKitId);
      if (kit) return { label: kit.name, price: kit.bundle_price, priceUsd: kit.price_usd || undefined, priceSign: null };
      // Edge case: user advanced with items only (e.g. after edit-back path),
      // never picked a kit. Be honest about it — no price on this chip
      // because the items ARE the Step 2 contribution, not Step 1's.
      if (selectedItemIds.size > 0) return { label: "No kit — items only", price: null, priceSign: null };
      return { label: "—", price: null, priceSign: null };
    }
  }
  if (step.kind === "extras") {
    if (pm === "tiered" || pm === "volume_tiered") {
      const chosen = (service.addons || []).filter((a) => selectedAddons.has(a.id));
      if (chosen.length === 0) return { label: "None", price: null, priceSign: null };
      const total = chosen.reduce(
        (s, a) => s + effectiveAddonPrice(service, a, selectedItemQtys),
        0,
      );
      const names = chosen
        .map((a) => {
          const q = effectiveAddonQty(service, a, selectedItemQtys);
          return isBrandingQtyAddon(service, a) && q > 1 ? `${a.name} × ${q}` : a.name;
        })
        .join(", ");
      return { label: names, price: total, priceUsd: usdSum(chosen.map((a) => [a, effectiveAddonPrice(service, a, selectedItemQtys)])), priceSign: "+" };
    }
    if (pm === "kits") {
      const chosen = materializeSelectedItems(service, selectedItemIds);
      if (chosen.length === 0) return { label: "None", price: null, priceSign: null };
      const total = chosen.reduce((s, i) => s + effectiveItemPrice(service, i, selectedItemQtys), 0);
      // Line count reflects distinct rows, not summed quantities — matches
      // how Step 2 shows the checklist to the customer.
      return {
        label: `${chosen.length} item${chosen.length > 1 ? "s" : ""}`,
        price: total,
        priceUsd: usdSum(chosen.map((i) => [i, effectiveItemPrice(service, i, selectedItemQtys)])),
        priceSign: "+",
      };
    }
    return { label: "None", price: null, priceSign: null };
  }
  if (step.kind === "brief") {
    const parts = [brief.business_name, brief.industry].filter(Boolean);
    return {
      label: parts.length ? parts.join(" · ") : "—",
      price: null,
      priceSign: null,
    };
  }
  return { label: "—", price: null, priceSign: null };
};

// ---------- Copy chip details — read-only formatting of what's already on the
// chip stack into a single plain-text clipboard payload. No new state, no new
// math — reuses summarizeStep + useSelectionRows.
const CopyChipsButton = ({ service, pm, tierId, selectedAddons, selectedItemIds, selectedItemQtys, selectedKitId, brief, steps, currentStep, mainArtworkBaseFormat }) => {
  const { total } = useSelectionRows({ service, pm, tierId, selectedAddons, selectedItemIds, selectedItemQtys, selectedKitId, mainArtworkBaseFormat });
  const buildText = () => {
    const chipsToCopy = steps.filter((s) => s.n < currentStep);
    const parts = chipsToCopy
      .map((s) => {
        const { label, price, priceSign } = summarizeStep({
          step: s, service, pm, tierId, selectedAddons, selectedItemIds, selectedItemQtys, selectedKitId, brief,
        });
        if (!label || label === "—") return null;
        if (typeof price === "number" && price > 0) {
          return `${label} — ${priceSign || ""}${money(price)}`;
        }
        return label;
      })
      .filter(Boolean);
    return `${service.name}: ${parts.join(", ")}.${service.cta_type === "quote" ? "" : ` Total: ${money(total)}`}`;
  };
  const onCopy = async () => {
    const text = buildText();
    try {
      await navigator.clipboard.writeText(text);
      toast.success("Summary copied to clipboard");
    } catch {
      toast.error("Copy failed — please copy manually");
    }
  };
  return (
    <div className="pt-2 flex justify-end">
      <button
        type="button"
        onClick={onCopy}
        data-testid="copy-chip-details"
        className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.22em] text-[#7A7A7A] hover:text-[#FF6600] transition-colors"
      >
        <Copy className="w-3 h-3" strokeWidth={2.5} />
        Copy summary
      </button>
    </div>
  );
};

// ---------- Step 4 — Website Details summary (customer verification) ----------
// Renders a compact 6-line summary of the Step-3 WebsiteDetails answers
// inside the Review left card, so the customer can visually confirm
// their brief before submitting. Read-only display; the customer edits
// by using the Step-3 chip in the progress ring.
const WebsiteDetailsReviewSummary = ({ details }) => {
  if (!details) return null;
  const {
    project_type, current_url, domain_status, domain_url,
    content_readiness, languages, languages_detail, primary_goal,
    brand_assets,
  } = details;
  // If literally nothing has been answered, don't render the block.
  const hasAny = project_type || domain_status || content_readiness ||
    languages || primary_goal || brand_assets;
  if (!hasAny) return null;
  const projectLabel = project_type === "new"
    ? "New website"
    : project_type === "redesign"
      ? (current_url ? `Redesign · ${current_url}` : "Redesign of existing site")
      : "—";
  const domainLabel = domain_status === "own"
    ? (domain_url ? `Own domain · ${domain_url}` : "Own domain")
    : domain_status === "need_help"
      ? "Need help with domain"
      : "—";
  const contentLabel = {
    yes: "Ready to share",
    partial: "Partially ready",
    no: "Need help creating content",
  }[content_readiness] || "—";
  const languagesLabel = languages === "one"
    ? "1 language (included)"
    : languages === "many"
      ? (languages_detail ? `Multiple · ${languages_detail}` : "Multiple (custom estimate)")
      : "—";
  const primaryGoalLabel = {
    leads: "Generate leads / contact requests",
    info: "Present services / business info",
    bookings: "Accept bookings",
    payments: "Accept simple payments",
    sell: "Sell products online",
    other: "Other",
  }[primary_goal] || primary_goal || "—";
  const brandAssetsLabel = {
    yes: "Yes — ready to share",
    partial: "Partially in place",
    no: "Need help creating them",
  }[brand_assets] || "—";
  const rows = [
    ["Project", projectLabel],
    ["Domain", domainLabel],
    ["Content", contentLabel],
    ["Languages", languagesLabel],
    ["Primary goal", primaryGoalLabel],
    ["Brand assets", brandAssetsLabel],
  ];
  return (
    <div
      data-testid="review-website-details"
      className="mt-6 pt-4 border-t border-[#262626]"
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3] mb-3">
        Website Details
      </div>
      <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2 text-[13px]">
        {rows.map(([label, value]) => (
          <div key={label} className="flex items-start justify-between gap-3 sm:flex-col sm:gap-0.5">
            <dt className="text-[#7A7A7A] text-[11px] uppercase tracking-wider font-semibold shrink-0">{label}</dt>
            <dd className="text-white text-right sm:text-left leading-snug">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
};

// ---------- Step 4 — Review body (only mounts when Step 3 is confirmed) ----------
const Step4Review = ({ service, pm, quoteMode, mixedMode, tierId, selectedAddons, selectedItemIds, selectedItemQtys, selectedKitId, brief, addItem, navigate, mainArtworkFormats, mainArtworkBaseFormat }) => {
  const { rows: rawRows, total, totalUsd, recurring } = useSelectionRows({ service, pm, tierId, selectedAddons, selectedItemIds, selectedItemQtys, selectedKitId, mainArtworkBaseFormat });
  // Site-wide tax — shared source with Checkout via /api/settings.
  // Backend re-validates on order submit; this is display-only.
  const { taxRate, taxLabel, compute: computeTax } = useSalesTax();
  const { taxAmount, total: totalWithTax } = computeTax(total);
  // Enrich the raw selection rows so the customer sees:
  //   • the base Main Artwork format (as an included, free row) — pulled
  //     from mainArtworkBaseFormat / mainArtworkFormats which are threaded
  //     through props but not part of useSelectionRows.
  //   • the paid additional formats broken out per-format with dimensions
  //     (replacing the generic "Main Artwork — Additional Size / Format
  //     × N" summary row so the customer can confirm exactly what sizes
  //     they're paying for before checkout).
  const rows = (() => {
    const out = [];
    const baseValid = !!mainArtworkBaseFormat && isValidMainArtworkEntry(mainArtworkBaseFormat);
    const _formats = getMainArtworkFormatsList(service);
    const _byId = new Map(_formats.map((f) => [f.id, f]));
    const _baseSurcharge = baseValid ? getPrimaryFormatSurcharge(mainArtworkBaseFormat.id, _formats) : 0;
    if (baseValid) {
      out.push({
        label: `Main Artwork Format · ${formatMainArtworkEntry(mainArtworkBaseFormat, service)}`,
        value: _baseSurcharge > 0
          ? `+${money(_baseSurcharge)} · Primary`
          : `${money(0)} · Included`,
        muted: true,
      });
    }
    for (const r of rawRows) {
      const isAdditionalSizeRow = typeof r.label === "string"
        && r.label.startsWith(MAIN_ARTWORK_ITEM_NAME);
      if (!isAdditionalSizeRow) { out.push(r); continue; }
      // Expand the additional-size row into one row per paid format entry.
      const entries = [];
      Object.values(mainArtworkFormats || {}).forEach((arr) => {
        (arr || []).forEach((e) => {
          if (!isValidMainArtworkEntry(e)) return;
          // Custom Size is instance-keyed (by dimensions) — an additional
          // custom never collides with a custom base, so always include.
          if (mainArtworkBaseFormat?.id && e.id === mainArtworkBaseFormat.id && e.id !== "custom") return;
          entries.push(e);
        });
      });
      if (entries.length === 0) { out.push(r); continue; }
      entries.forEach((e) => {
        const fmt = _byId.get(e.id);
        const perPrice = fmt && typeof fmt.additional_price === "number" && fmt.additional_price > 0
          ? fmt.additional_price
          : MAIN_ARTWORK_FORMAT_PRICE;
        out.push({
          label: `Additional Format · ${formatMainArtworkEntry(e, service)}`,
          value: `+${money(perPrice)}`,
          muted: true,
        });
      });
    }
    return out;
  })();
  const [submitting, setSubmitting] = useState(false);
  const submit = () => {
    addSelectionsToCart({ service, pm, tierId, selectedAddons, selectedItemIds, selectedItemQtys, selectedKitId, brief, addItem, mainArtworkFormats, displayUsd: totalUsd, recurringUsd: recurring?.amount_usd, mainArtworkBaseFormat });
    toast.success("Package added to cart with your project brief");
  };
  const submitAndCheckout = () => {
    addSelectionsToCart({ service, pm, tierId, selectedAddons, selectedItemIds, selectedItemQtys, selectedKitId, brief, addItem, mainArtworkFormats, displayUsd: totalUsd, recurringUsd: recurring?.amount_usd, mainArtworkBaseFormat });
    navigate("/checkout");
  };
  const submitQuote = async () => {
    setSubmitting(true);
    const { ok, request_id, service_name, error } = await submitQuoteRequest({
      service, pm, tierId, selectedAddons, selectedItemIds, selectedItemQtys, selectedKitId, brief,
      mainArtworkFormats, mainArtworkBaseFormat,
    });
    setSubmitting(false);
    if (ok) {
      toast.success("Estimate request submitted — we'll be in touch shortly.");
      // Route to the dedicated Estimate confirmation surface, replacing
      // the current history entry so browser Back cannot walk back to
      // Step 4 Review and re-fire the same submission. Refresh on the
      // confirmation page is safe — it never re-POSTs.
      const q = new URLSearchParams();
      if (request_id) q.set("request_id", request_id);
      if (service_name) q.set("service_name", service_name);
      const qs = q.toString();
      navigate(`/estimate/received${qs ? `?${qs}` : ""}`, { replace: true });
    } else {
      toast.error(error || "Submission failed — please try again.");
    }
  };
  return (
    <div className="grid md:grid-cols-[1fr_1fr] gap-6">
      <div className="rounded-2xl border border-[#262626] bg-[#0F0F0F] p-5 sm:p-6">
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF6600]">Your Selections</div>
        {mixedMode ? (
          // BMD architecture v1 correction 3 — Step 4 mixed-mode split.
          // Fixed-price scope (with visible AWG line prices + subtotal)
          // AND Estimate required (custom estimate lines) render as
          // separate sections so the customer sees exactly what stays
          // priced and what gets quoted.
          (() => {
            const fixed = rows.filter((r) => !r.estimate);
            const estimated = rows.filter((r) => r.estimate);
            const fixedSubtotal = fixed.reduce((s, r) => s + (Number(r._price) || 0), 0);
            // US$ list-price subtotal for the fixed rows; undefined when
            // any priced row lacks an approved US$ (falls back to the
            // exact AWG conversion inside money()).
            let _fixedBad = false;
            const fixedUsdSum = fixed.reduce((sum, r) => {
              if ((Number(r._price) || 0) <= 0) return sum;
              if (!(Number(r._usd) > 0)) { _fixedBad = true; return sum; }
              return sum + Number(r._usd);
            }, 0);
            const fixedUsdTotal = _fixedBad ? undefined : fixedUsdSum;
            return (
              <>
                <div data-testid="review-fixed-section" className="mt-3 space-y-2 text-sm">
                  <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#A3A3A3]">Fixed-price scope</div>
                  {fixed.map((r, i) => (
                    <div key={`f-${i}`} className="flex items-start justify-between gap-4">
                      <span className={r.muted ? "text-[#A3A3A3]" : "text-white"}>{r.label}</span>
                      <span className="font-semibold text-white shrink-0 tabular-nums">{r.value}</span>
                    </div>
                  ))}
                  <div className="pt-3 mt-2 border-t border-[#262626] space-y-2">
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-[#A3A3A3]">Fixed subtotal</span>
                      <span data-testid="review-fixed-subtotal" className="font-semibold text-white tabular-nums">{money(fixedSubtotal, fixedUsdTotal)}</span>
                    </div>
                    {taxRate > 0 && (
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-[#A3A3A3]" data-testid="review-fixed-tax-label">{taxLabel}</span>
                        <span data-testid="review-fixed-tax-amount" className="font-semibold text-white tabular-nums">{money(computeTax(fixedSubtotal).taxAmount, fixedUsdTotal != null ? fixedUsdTotal * taxRate : undefined)}</span>
                      </div>
                    )}
                    <div className="pt-2 border-t border-[#262626] flex items-end justify-between">
                      <span className="text-white text-sm font-semibold">Fixed estimated total</span>
                      <div className="text-right">
                        <div data-testid="review-fixed-total" className="font-display text-xl font-black leading-none">{money(computeTax(fixedSubtotal).total, fixedUsdTotal != null ? fixedUsdTotal * (1 + taxRate) : undefined)}</div>
                        <div className="text-[11px] text-[#A3A3A3] mt-1">{usdApprox(computeTax(fixedSubtotal).total)}</div>
                      </div>
                    </div>
                    <ChargeDisclosure awgTotal={computeTax(fixedSubtotal).total} className="pt-1" testId="review-fixed-charge-disclosure" />
                  </div>
                </div>
                <div data-testid="review-estimate-section" className="mt-6 space-y-2 text-sm">
                  <div className="text-[11px] font-bold uppercase tracking-[0.14em] text-[#A3A3A3]">Estimate required</div>
                  {estimated.map((r, i) => (
                    <div key={`e-${i}`} className="flex items-start justify-between gap-4">
                      <span className="text-white">{r.label}</span>
                      <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#A3A3A3] shrink-0">Custom Estimate</span>
                    </div>
                  ))}
                </div>
              </>
            );
          })()
        ) : (
          <>
            <div className="mt-3 space-y-2 text-sm">
              {rows.map((r, i) => (
                <div key={i} className="flex items-start justify-between gap-4">
                  <span className={r.muted ? "text-[#A3A3A3]" : "text-white"}>{r.label}</span>
                  {quoteMode ? (
                    <span className="text-[10px] font-bold uppercase tracking-[0.16em] text-[#A3A3A3] shrink-0">Estimate</span>
                  ) : (
                    <span className="font-semibold text-white shrink-0 tabular-nums">{r.value}</span>
                  )}
                </div>
              ))}
            </div>
            {!quoteMode && (
              <div className="mt-5 pt-4 border-t border-[#262626] space-y-2">
                <div className="flex items-center justify-between text-sm">
                  <span className="text-[#A3A3A3]">Subtotal</span>
                  <span data-testid="review-subtotal" className="font-semibold text-white tabular-nums">{money(total, totalUsd)}</span>
                </div>
                {taxRate > 0 && (
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-[#A3A3A3]" data-testid="review-tax-label">{taxLabel}</span>
                    <span data-testid="review-tax-amount" className="font-semibold text-white tabular-nums">{money(taxAmount, totalUsd != null ? totalUsd * taxRate : undefined)}</span>
                  </div>
                )}
                <div className="pt-2 border-t border-[#262626] flex items-end justify-between">
                  <span className="text-white text-sm font-semibold">Estimated Total</span>
                  <div className="text-right">
                    <div data-testid="review-total" className="font-display text-2xl font-black leading-none">{money(totalWithTax, totalUsd != null ? totalUsd * (1 + taxRate) : undefined)}</div>
                    <div className="text-[11px] text-[#A3A3A3] mt-1">{usdApprox(totalWithTax)}</div>
                  </div>
                </div>
                <ChargeDisclosure awgTotal={totalWithTax} className="pt-1" testId="review-charge-disclosure" />
              </div>
            )}
          </>
        )}
        {/* P.B.3 (Batch 1) — Website Details summary on Review.
            Rendered only for website-design when the Step-3
            WebsiteDetails block has content. Compact, human-labelled
            lines so the customer can visually verify their brief
            answers before submitting. Coexists with fixed/estimate/
            recurring rendering above — this block is BELOW the
            subtotal/mixed-mode split, never inside it. */}
        {service?.slug === "website-design" && brief?.website_details && (
          <WebsiteDetailsReviewSummary details={brief.website_details} />
        )}
        {service?.slug === EVENT_CREATIVE_SLUG && (brief?.event_date || "").trim() && (
          <div
            data-testid="review-event-date"
            className="mt-6 pt-4 border-t border-[#262626] flex items-start justify-between gap-4"
          >
            <div className="text-[11px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3]">
              Event Date
            </div>
            <div className="text-sm text-white font-semibold tabular-nums">
              {formatEventDate(brief.event_date)}
            </div>
          </div>
        )}
      </div>
      <div className="rounded-2xl border border-[#262626] bg-[#0F0F0F] p-5 sm:p-6 flex flex-col justify-between">
        <div>
          {mixedMode ? (
            <>
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF6600]">Combined Project</div>
              <div className="font-display text-3xl sm:text-4xl font-black mt-3 leading-tight">
                Fixed <span className="text-[#FF6600]">+</span> Estimate
              </div>
              <div className="text-xs text-[#A3A3A3] mt-2">
                Fixed subtotal shown at left; estimated items will be quoted alongside.
              </div>
              {recurring?.amount > 0 && (
                <div
                  data-testid="review-mixed-recurring"
                  className="mt-3 text-sm font-bold text-[#FF6600]"
                >
                  + {listMoney(recurring.amount, recurring.amount_usd)}/{recurring.label} ongoing
                </div>
              )}
              <p className="text-xs text-[#A3A3A3] mt-4 leading-relaxed">
                One submission — we'll confirm the fixed portion and reply with a tailored estimate for the custom work, usually within one business day. No charge until you approve.
              </p>
            </>
          ) : quoteMode ? (
            <>
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF6600]">Custom Quote</div>
              <div className="font-display text-3xl sm:text-4xl font-black mt-3 leading-tight">Estimate on request</div>
              <p className="text-xs text-[#A3A3A3] mt-4 leading-relaxed">
                We'll review your project brief and reply with a tailored
                estimate — usually within one business day.
              </p>
            </>
          ) : (
            <>
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF6600]">Estimated Total</div>
              <div className="font-display text-4xl sm:text-5xl font-black mt-3 leading-none">{money(totalWithTax, totalUsd != null ? totalUsd * (1 + taxRate) : undefined)}</div>
              <div className="text-xs text-[#A3A3A3] mt-2">{usdApprox(totalWithTax)}</div>
              {taxRate > 0 && (
                <div
                  data-testid="review-headline-tax-note"
                  className="mt-2 text-[11px] text-[#A3A3A3]"
                >
                  Includes {taxLabel} · {money(taxAmount, totalUsd != null ? totalUsd * taxRate : undefined)}
                </div>
              )}
              {recurring?.amount > 0 && (
                <div
                  data-testid="review-recurring"
                  className="mt-2 text-sm font-bold text-[#FF6600]"
                >
                  + {listMoney(recurring.amount, recurring.amount_usd)}/{recurring.label} ongoing
                </div>
              )}
              <p className="text-xs text-[#A3A3A3] mt-4 leading-relaxed">
                We'll review your project brief and confirm timelines. Payments processed by Sentoo (Aruba Bank) at checkout.
              </p>
            </>
          )}
        </div>
        <div className="mt-6 space-y-2">
          {quoteMode ? (
            <button
              data-testid="step4-request-estimate"
              onClick={submitQuote}
              disabled={submitting}
              className="ur-btn-primary w-full disabled:opacity-40"
            >
              {submitting ? "Submitting…" : (mixedMode ? "Request Combined Project Estimate" : "Request Estimate")}{" "}
              {!submitting && <ArrowRight className="w-4 h-4" />}
            </button>
          ) : (
            <>
              <button data-testid="step4-add-to-cart" onClick={submit} className="ur-btn-primary w-full">
                Add to Cart <ShoppingCart className="w-4 h-4" />
              </button>
              <button data-testid="step4-checkout" onClick={submitAndCheckout} className="ur-btn-secondary w-full">
                Continue to Checkout <ArrowRight className="w-4 h-4" />
              </button>
            </>
          )}
          <TrustRow testidPrefix="step4-trust" />
        </div>
      </div>
    </div>
  );
};

// ---------- Sticky total bar — replaces the full sidebar during Steps 1–3.
// Content = running total + compact Add-to-Cart. No itemised breakdown;
// that lives exclusively in the collapsed chips + Step 4 Review.
// Desktop: right-side pinned panel. Mobile: bottom-pinned bar.
const StickyTotalBar = ({ service, pm, quoteMode, mixedMode, tierId, selectedAddons, selectedItemIds, selectedItemQtys, selectedKitId, brief, addItem, navigate, step1HasSelection, step3Ready, currentStep, currentStepKind, atReview, onStickyAdvance, mainArtworkFormats, mainArtworkBaseFormat }) => {
  const { total, totalUsd, recurring } = useSelectionRows({ service, pm, tierId, selectedAddons, selectedItemIds, selectedItemQtys, selectedKitId, mainArtworkBaseFormat });
  // Price pulse: brief 300ms flash whenever the running total changes after an edit.
  // Skip the initial mount so the bar doesn't pulse on page load.
  const [pulse, setPulse] = useState(false);
  // Chip delta: neutral "+AWG X" / "−AWG X" hint shown for ~2s next to the total.
  // Plain text, no color coding — the sign alone communicates direction.
  const [delta, setDelta] = useState(null); // { sign: "+"|"−", amount: number, usd?: number } | null
  const prevTotalRef = useRef(total);
  const prevTotalUsdRef = useRef(totalUsd);
  useEffect(() => {
    if (prevTotalRef.current === total) return;
    const diff = total - prevTotalRef.current;
    // Display-only US$ delta from the list-price sums so the chip agrees
    // with the card prices (e.g. +US$ 700, not +US$ 698).
    const usdDiff =
      totalUsd != null
        ? totalUsd - (prevTotalUsdRef.current ?? (prevTotalRef.current === 0 ? 0 : totalUsd))
        : undefined;
    prevTotalRef.current = total;
    prevTotalUsdRef.current = totalUsd;
    setPulse(false);
    setDelta(null);
    // rAF ensures the class removal + re-add restarts the animation
    const raf = requestAnimationFrame(() => {
      setPulse(true);
      setDelta({ sign: diff > 0 ? "+" : "−", amount: Math.abs(diff), usd: usdDiff != null ? Math.abs(usdDiff) : undefined });
    });
    const pulseT = setTimeout(() => setPulse(false), 320);
    const deltaT = setTimeout(() => setDelta(null), 2000);
    return () => {
      cancelAnimationFrame(raf);
      clearTimeout(pulseT);
      clearTimeout(deltaT);
    };
  }, [total]);
  // Sticky CTA behaviour — step-gated. Never skips forward multiple steps:
  //   • select step: enabled once a package is chosen → advances one step
  //   • extras step: always enabled → advances one step (Skip semantics)
  //   • brief step: enabled once required fields are valid → advances to review
  //   • review step: enabled once brief is valid → add to cart + navigate to checkout
  // This replaces the earlier `onReviewJump` short-circuit that jumped straight
  // to review whenever a tier was already selected — that path skipped Steps 2
  // & 3 entirely and let customers land on Step 4 with an empty brief.
  const isReviewStep = atReview;
  // Main Artwork base format must be selected for services that offer it
  // (Event Creative kits + ACM Campaign Design Package selection). Block
  // the Continue button on the "extras" step until the customer has
  // picked a valid base format so no order lands with an undefined
  // primary artwork format.
  const requiresMainArtworkBase = (() => {
    if (!service) return false;
    if (service.slug === EVENT_CREATIVE_SLUG && selectedKitId) return true;
    if (service.slug === ACM_SLUG) {
      const acmPkg = (service.standalone_items || []).find(
        (i) => (i.item_code || "") === ACM_CAMPAIGN_PACKAGE_ITEM_CODE,
      );
      return !!acmPkg && selectedItemIds.has(acmPkg.id);
    }
    return false;
  })();
  const hasValidBaseFormat = !!mainArtworkBaseFormat && isValidMainArtworkEntry(mainArtworkBaseFormat);
  const mainArtworkBaseBlocked = requiresMainArtworkBase && !hasValidBaseFormat;
  const canProceed =
    currentStepKind === "select" ? (!!step1HasSelection && !(service?.slug === ACM_SLUG && mainArtworkBaseBlocked)) :
    currentStepKind === "extras" ? !mainArtworkBaseBlocked :
    currentStepKind === "brief"  ? !!step3Ready :
    currentStepKind === "review" ? (!!step3Ready && !mainArtworkBaseBlocked) : false;
  const label =
    !canProceed
      ? (currentStepKind === "select" ? (mainArtworkBaseBlocked ? "Pick a Main Artwork format" : "Pick a package")
         : currentStepKind === "extras" ? "Pick a Main Artwork format"
         : currentStepKind === "brief" ? "Complete brief to continue"
         : currentStepKind === "review" ? (quoteMode ? "Complete brief to submit" : "Complete brief to checkout")
         : "Continue")
      : isReviewStep ? (quoteMode ? "Request Estimate" : "Add & Checkout")
      : currentStepKind === "brief" ? "Continue to Review"
      : "Continue";
  const onCta = () => {
    if (!canProceed) return;
    if (isReviewStep) {
      if (quoteMode) {
        submitQuoteRequest({
          service, pm, tierId, selectedAddons, selectedItemIds, selectedItemQtys, selectedKitId, brief,
          mainArtworkFormats, mainArtworkBaseFormat,
        }).then(({ ok, request_id, service_name, error }) => {
          if (ok) {
            toast.success("Estimate request submitted — we'll be in touch shortly.");
            // Route to the dedicated Estimate confirmation surface,
            // replacing the current history entry so browser Back
            // cannot resubmit. Mirrors Step4Review's submitQuote path.
            const q = new URLSearchParams();
            if (request_id) q.set("request_id", request_id);
            if (service_name) q.set("service_name", service_name);
            const qs = q.toString();
            navigate(`/estimate/received${qs ? `?${qs}` : ""}`, { replace: true });
          } else {
            toast.error(error || "Submission failed — please try again.");
          }
        });
        return;
      }
      addSelectionsToCart({ service, pm, tierId, selectedAddons, selectedItemIds, selectedItemQtys, selectedKitId, brief, addItem, mainArtworkFormats, displayUsd: totalUsd, recurringUsd: recurring?.amount_usd, mainArtworkBaseFormat });
      toast.success("Package added to cart");
      navigate("/checkout");
      return;
    }
    onStickyAdvance && onStickyAdvance();
  };
  return (
    <div
      data-testid="sticky-total-bar"
      className={`fixed left-0 right-0 bottom-0 z-40 border-t border-[#1F1F1F] bg-[#0F0F0F]/95 backdrop-blur-md ${atReview ? "lg:hidden" : ""}`}
    >
      <div className="max-w-[900px] mx-auto px-4 sm:px-6 py-3 flex items-center justify-between gap-3">
        <div className="min-w-0">
          {quoteMode && !mixedMode ? (
            <>
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#7A7A7A]">Quote Mode</div>
              <div data-testid="sticky-quote-label" className="font-display text-lg font-black leading-tight mt-0.5">
                Estimate on request
              </div>
              <div className="text-[11px] text-[#A3A3A3] mt-1 leading-none">We'll confirm pricing after we see your brief.</div>
            </>
          ) : (
            <>
              <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#7A7A7A]">
                {mixedMode ? "Fixed Subtotal" : "Running Total"}
              </div>
              <div className="flex items-baseline gap-2 mt-0.5 flex-wrap">
                <div data-testid="sticky-total" className={`font-display text-2xl font-black leading-none inline-block ${pulse ? "ur-price-pulse" : ""}`}>{money(total, totalUsd)}</div>
                {delta && (
                  <div
                    data-testid="sticky-total-delta"
                    className="text-xs font-medium text-[#A3A3A3] leading-none whitespace-nowrap tabular-nums inline-flex items-center gap-1"
                  >
                    {delta.sign === "+" ? (
                      <ArrowUp data-testid="sticky-total-delta-icon-up" className="w-3 h-3" strokeWidth={2.5} />
                    ) : (
                      <ArrowDown data-testid="sticky-total-delta-icon-down" className="w-3 h-3" strokeWidth={2.5} />
                    )}
                    {delta.sign}{listMoney(delta.amount, delta.usd)}
                  </div>
                )}
              </div>
              <div data-testid="sticky-total-usd" className="text-[11px] text-[#A3A3A3] mt-1 leading-none">
                {mixedMode ? "+ estimated items — quoted after brief" : usdApprox(total)}
              </div>
              {recurring?.amount > 0 && (
                <div
                  data-testid="sticky-total-recurring"
                  className="mt-1 leading-tight"
                >
                  {/* Two-line recurring commitment. Line 1: amount +
                      "after launch" clarifier so the customer knows
                      the charge starts only after the project ships.
                      Line 2: the recurring service name — sourced
                      from the same approved copy ("Website Care &
                      Hosting") used across CheckoutPage, CartDrawer
                      and the AI Assistant replacement disclaimer.
                      Kept compact for 360px viewports. */}
                  <div className="text-[11px] font-semibold text-[#FF6600] leading-none">
                    + {listMoney(recurring.amount, recurring.amount_usd)}/month after launch
                  </div>
                  {service?.slug === "website-design" && (
                    <div
                      data-testid="sticky-total-recurring-name"
                      className="text-[10px] font-medium text-[#A3A3A3] mt-0.5 leading-none"
                    >
                      Website Care &amp; Hosting
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
        <button
          data-testid="sticky-cta"
          onClick={onCta}
          disabled={!canProceed}
          className="ur-btn-primary text-sm py-2.5 px-5 disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {label} <ArrowRight className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
};

const step1Sub = (pm) => {
  if (pm === "custom_items") return "Add exactly what you need — no bundles, no surprises.";
  if (pm === "kits") return "Named bundles built for specific situations — pick the one that fits.";
  if (pm === "volume_tiered") return "Pick the monthly cadence that matches your posting rhythm.";
  return "Pick the base package that fits your goals.";
};

// ---------- Compact header (no big image) ----------
// `showBackToServices` gates the "← All Services" affordance. Once the
// customer has advanced past Step 1 we hide it so the only backward
// path is Continue's counterpart — the browser Back button, which now
// walks the internal steps (see history-backed navigation in the main
// component).
const CompactHeader = ({ service, showBackToServices = true }) => (
  <section className="relative overflow-hidden border-b border-[#1F1F1F]">
    <div
      aria-hidden
      className="absolute -top-20 right-0 w-[500px] h-[400px] rounded-full opacity-15 blur-[140px] pointer-events-none"
      style={{ background: "radial-gradient(circle, #FF6600 0%, transparent 60%)" }}
    />
    <div className="relative max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-10 pt-8 pb-6">
      {showBackToServices && (
        <Link
          to="/services"
          data-testid="pb-breadcrumb"
          className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.2em] font-semibold text-[#A3A3A3] hover:text-[#FF6600] transition-colors"
        >
          <ArrowLeft className="w-3.5 h-3.5" /> All Services
        </Link>
      )}
      <h1 className={`font-display ${showBackToServices ? "mt-3" : ""} text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.02]`}>
        {service.name}
      </h1>
      {service.tagline && (
        <div className="mt-2 text-[#FF6600] font-semibold text-sm sm:text-base">{service.tagline}</div>
      )}
      <p className="mt-3 text-[#B5B5B5] text-sm sm:text-base max-w-2xl leading-relaxed">
        {service.long_description || service.short_description}
      </p>
    </div>
  </section>
);


// ---------- Shared card anatomy ----------
// Two variants:
//   primary   → tier / kit cards (Step 1) — larger, bolder, hosts the popular signal
//   secondary → add-on / item cards (Step 2 & item pool) — smaller footprint, muted icon
//
// Two state axes (INDEPENDENT — that's the whole point of the Phase-6.1 fix):
//   popular   → set once on the recommended tier/kit; always visually dominant
//               (thick orange border, orange icon fill, floating badge, subtle bg tint)
//   active    → whichever card the visitor has selected (may or may not be popular)
//               (adds a checkmark + "Selected"/"Added" label; DOES NOT reuse the
//               solid-orange treatment reserved for popular)
//
// The badge is a SIBLING of the clipped .ur-card (which has overflow-hidden for its
// border-radius). Wrapping in an outer relative div lets the badge float above the
// card edge without being clipped.
const AnchoredCard = ({
  variant = "primary",
  icon: Icon,
  name,
  priceLabel,
  priceValue,
  recurringPrice,      // number > 0 → renders "+ AWG X/<label>" combined-total line
  recurringPriceUsd,   // approved USD list price for the recurring line (optional)
  recurringLabel,      // e.g. "month"
  itemQuoteMode,       // this individual tier/kit is priced on request (independent of global quote mode)
  quoteMode = false,
  description,
  teaser,              // optional single-line orientation hint (e.g. kit deliverables count + first names)
  features,            // optional list of inclusions surfaced by the "View What's Included" accordion
  includedNames,       // optional resolved item names (kits) — same accordion consumer
  active,
  popular,
  selectedLabel,
  defaultLabel,
  onClick,
  testid,
  mobileOrderFirst = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const inclusionList = (features && features.length ? features : (includedNames || [])) || [];
  const isPrimary = variant === "primary";

  // Wrapper (badge-safe outer container). `mobileOrderFirst` bumps the popular
  // tier to the top of the mobile stack per the "mobile visitors don't scroll
  // through every option before deciding" rule.
  const wrapperClass = [
    "relative flex",
    mobileOrderFirst ? "order-first md:order-none" : "",
  ].join(" ");

  // Card border/bg — popular ALWAYS wins the dominant treatment.
  //   popular              → thick orange border + subtle orange bg tint
  //   active (non-popular) → single orange border (thinner than popular)
  //   default              → neutral border
  const cardBorder = popular
    ? POPULAR_CARD_CLASSES
    : active
      ? "border-[#FF6600]"
      : "border-[#262626] hover:border-[#FF6600]/40";

  const paddingClass = isPrimary
    ? popular
      ? "p-6 pt-7 sm:pt-8"
      : "p-6"
    : popular
      ? "p-4 pt-6"
      : "p-4";

  // Icon box — popular dominates in BOTH variants (dominant treatment decoupled
  // from selection). Non-popular respects the variant's muting rules.
  const iconBoxClass = popular
    ? "bg-[#FF6600] text-black"
    : active
      ? "bg-[#FF6600]/15 text-[#FF6600] border border-[#FF6600]/50"
      : isPrimary
        ? "bg-transparent border border-[#333] text-[#FF6600]"
        : "bg-transparent border border-[#262626] text-[#A3A3A3]";

  const iconBoxSize = isPrimary ? "w-11 h-11" : "w-9 h-9";
  const iconSize = isPrimary ? "w-5 h-5" : "w-4 h-4";
  const nameClass = isPrimary
    ? "font-display text-base font-bold uppercase tracking-wide leading-tight"
    : "font-display text-sm font-bold uppercase tracking-wide leading-tight";
  const priceClass = isPrimary
    ? "font-display text-2xl font-black text-[#FF6600] leading-none mt-1"
    : "font-display text-lg font-black text-[#FF6600] leading-none mt-1";

  // Action row: neutral text with a check when active; no full orange fill.
  const label = active ? selectedLabel : defaultLabel;
  const actionClass = active
    ? "border border-[#FF6600] bg-[#FF6600]/10 text-[#FF6600]"
    : "border border-[#333] text-white group-hover:border-[#FF6600]/60";

  return (
    <div className={wrapperClass} data-popular={popular ? "true" : undefined}>
      {popular && (
        <PopularCardBadge testId={testid ? `${testid}-popular-badge` : undefined} />
      )}
      <div
        onClick={onClick}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            onClick?.();
          }
        }}
        role="button"
        tabIndex={0}
        data-testid={testid}
        aria-pressed={active}
        className={`group text-left w-full bg-[#1A1A1A] rounded-2xl overflow-hidden transition-colors flex flex-col cursor-pointer ${paddingClass} ${cardBorder}`}
      >
        <div className={`${iconBoxSize} rounded-xl grid place-items-center shrink-0 transition-colors ${iconBoxClass}`}>
          <Icon className={iconSize} strokeWidth={2} />
        </div>
        <div className="mt-4 min-w-0 flex-1">
          <h3 className={nameClass}>{name}</h3>
          {(quoteMode || itemQuoteMode) ? (
            <div
              data-testid={testid ? `${testid}-quote-tag` : undefined}
              className={`inline-flex items-center gap-1.5 mt-3 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] ${
                popular
                  ? "bg-black/25 text-white/90 border border-white/20"
                  : "bg-[#0F0F0F] text-[#A3A3A3] border border-[#262626]"
              }`}
            >
              <Sparkles className="w-3 h-3" strokeWidth={2} /> Estimate on request
            </div>
          ) : (
            <>
              {priceLabel && (
                <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#7A7A7A] mt-3">
                  {priceLabel}
                </div>
              )}
              <div className={priceClass}>{priceValue}</div>
              {recurringPrice > 0 && (
                <div
                  data-testid={testid ? `${testid}-recurring` : undefined}
                  className="mt-1 text-[11px] font-semibold text-[#FF6600] leading-tight"
                >
                  + {listMoney(recurringPrice, recurringPriceUsd)}/{recurringLabel || "month"}
                </div>
              )}
            </>
          )}
          {description && (
            <p className={`mt-2 text-[#A3A3A3] leading-relaxed ${isPrimary ? "text-xs" : "text-[11px]"}`}>
              {description}
            </p>
          )}
          {teaser && (
            <div
              data-testid={testid ? `${testid}-teaser` : undefined}
              className="mt-2 text-[11px] leading-snug text-[#A3A3A3]"
            >
              {teaser}
            </div>
          )}
          {inclusionList.length > 0 && (
            <div className="mt-3">
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); setExpanded((v) => !v); }}
                data-testid={testid ? `${testid}-view-included` : undefined}
                aria-expanded={expanded}
                className="inline-flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#FF6600] hover:text-[#FF8533] transition-colors"
              >
                {expanded ? "Hide Details" : "View What's Included"}
                <ArrowRight className={`w-3 h-3 transition-transform ${expanded ? "rotate-90" : ""}`} />
              </button>
              {expanded && (
                <ul
                  data-testid={testid ? `${testid}-inclusions` : undefined}
                  className="mt-2.5 space-y-1.5 text-[11.5px] text-[#D1D1D1] leading-snug border-l-2 border-[#FF6600]/40 pl-3"
                >
                  {inclusionList.map((f, idx) => (
                    <li key={idx} className="flex items-start gap-1.5 min-w-0">
                      <Check className="w-3 h-3 text-[#FF6600] mt-0.5 shrink-0" strokeWidth={3} />
                      <span>{f}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          )}
        </div>
        {(selectedLabel || defaultLabel) && (
          <div className="mt-4 pt-1">
            <div
              className={`w-full inline-flex items-center justify-center gap-1.5 rounded-full text-[10.5px] tracking-[0.2em] uppercase font-bold px-3.5 py-2 transition-colors ${actionClass}`}
            >
              {label} {active && <Check className="w-3 h-3" strokeWidth={3} />}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ---------- Step 1 renderers per pricing_model ----------
const TieredStep1 = ({ service, quoteMode, tierId, setTierId, step1EverConfirmed }) => {
  const tiers = service.tiers || [];
  if (tiers.length === 0) return <EmptyBody message="This service is being configured — check back soon for available packages." />;
  return (
    <div className="grid md:grid-cols-3 gap-4 md:gap-6 items-start">
      {tiers.map((t, i) => {
        const Icon = tierIconFor(i, tiers.length);
        return (
          <AnchoredCard
            key={t.id}
            variant="primary"
            icon={Icon}
            name={t.name}
            priceLabel={quoteMode || t.request_estimate ? undefined : "Starting At"}
            priceValue={quoteMode || t.request_estimate ? undefined : listMoney(t.price, t.price_usd)}
            recurringPrice={t.recurring_price || 0}
            recurringPriceUsd={t.recurring_price_usd || 0}
            recurringLabel={t.recurring_label || "month"}
            itemQuoteMode={!!t.request_estimate}
            quoteMode={quoteMode}
            description={t.description || (t.features?.length ? t.features.slice(0, 4).join(" • ") : "")}
            features={t.features || []}
            active={t.id === tierId}
            popular={!!t.popular}
            selectedLabel="Selected"
            defaultLabel="Select Package"
            mobileOrderFirst={!!t.popular}
            onClick={() => {
              // Feb 2026 owner directive — package toggle-off must be
              // consistent across every tiered service (Logo & Brand
              // Identity, Website Design, and any future tiered
              // service): click an unselected tier → replace; click
              // the currently-selected tier → deselect and drop back
              // to the neutral "no package" state. When tierId
              // becomes null the tier-change cleanup effect in the
              // parent (see PackageBuilder's "Tier-change cleanup"
              // useEffect) drops every selectedAddon + its qty so
              // Running Total lands on AWG 0 and Continue disables.
              //
              // The earlier "back-nav guard" (only Website, only
              // before step1EverConfirmed) was intentionally removed
              // — the owner's mental model is "the tap on the active
              // card means clear my choice", full stop. Re-confirming
              // a selection after Back is done by clicking Continue,
              // not by tapping the card.
              setTierId(t.id === tierId ? null : t.id);
            }}
            testid={`tier-${(t.name || "").toLowerCase().replace(/\s+/g, "-")}`}
          />
        );
      })}
    </div>
  );
};

const CustomItemsStep1 = ({ service, quoteMode, selectedItemIds, setSelectedItemIds, selectedItemQtys, setItemQuantity, mainArtworkFormats, updateMainArtworkFormats, mainArtworkBaseFormat, setMainArtworkBaseFormat }) => {
  // Focus-only URL routing (?focus=<group-code>) — scrolls the matching
  // family into view, never selects anything. Canonicalises the URL.
  useFocusRouting(service?.slug);
  // P1 — SPECIALISED_ITEM_CODES flags the 11 items rendered by the
  // package-conditional UX below. Their commercial data (price, name,
  // description, quantity_pricing, dependency) comes from the same DB
  // rows via getMigratedItemView() — one catalog source, one
  // presentation layer selected by item_code.
  const dbItems = (service.standalone_items || [])
    .filter((i) => i.active !== false)
    .filter((i) => !isSpecialisedItem(i));

  // BMD architecture v1 (Feb 2026) — active-code set + auto state-cleanup
  // for dependents whose parent is no longer active. Kept ABOVE the
  // early-return so both hooks fire on every render (Rules of Hooks).
  const activeCodes = React.useMemo(() => {
    const codes = new Set();
    for (const it of dbItems) {
      if (selectedItemIds.has(it.id) && it.item_code) codes.add(it.item_code);
    }
    return codes;
  }, [dbItems, selectedItemIds]);

  // Runs post-render so a mutex switch (e.g. 6-page → 2-page menu)
  // cascades cleanly through selection + quantity state without the
  // Review / cart / quote payload seeing a stale line.
  React.useEffect(() => {
    const stale = [];
    for (const it of dbItems) {
      if (!isAddonEligible(it, activeCodes)) {
        if (selectedItemIds.has(it.id) || (selectedItemQtys?.[it.id] || 0) > 0) {
          stale.push(it.id);
        }
      }
    }
    if (stale.length === 0) return;
    setSelectedItemIds((prev) => {
      const nx = new Set(prev);
      for (const id of stale) nx.delete(id);
      return nx;
    });
    if (setItemQuantity) {
      for (const id of stale) setItemQuantity(id, 0);
    }
  }, [activeCodes, dbItems, selectedItemIds, selectedItemQtys, setSelectedItemIds, setItemQuantity]);

  if (dbItems.length === 0) return <EmptyBody message="This service is being configured — check back soon for available items." />;

  // ACM: when the Campaign Design Package is selected, inject its five
  // dependent add-ons immediately below the package row, in order:
  //   • Additional Campaign Graphic (qty stepper)
  //   • Additional Size / Format (qty stepper)
  //   • Animated Story Upgrade — up to 10 sec / 15 sec / 30 sec (mutex)
  // All hidden otherwise so they can never be purchased standalone.
  let items = dbItems;
  if (service.slug === ACM_SLUG) {
    const campaignItem = dbItems.find((i) => i.item_code === "acm_campaign_design_package"
      || (i.item_code || "") === ACM_CAMPAIGN_PACKAGE_ITEM_CODE);
    if (campaignItem && selectedItemIds.has(campaignItem.id)) {
      const qtyAddons = ACM_QTY_ADDON_IDS
        .map((id) => getMigratedItemView(service, id))
        .filter(Boolean)
        .map((i) => ({ ...i, _quantityEnabled: true }));
      const storyUpgrades = ACM_STORY_UPGRADE_IDS
        .map((id) => getMigratedItemView(service, id))
        .filter(Boolean)
        .map((i) => ({ ...i, _mutexGroup: ACM_STORY_MUTEX }));
      const idx = dbItems.findIndex((i) => i.id === campaignItem.id);
      items = [...dbItems];
      items.splice(idx + 1, 0, ...qtyAddons, ...storyUpgrades);
    }
  }

  // Generic (non-ACM) dependency + quantity_enabled surfacing. Filters
  // ineligible dependents out of the visible list, and enables the DB
  // quantity_enabled path so BMD Additional Menu Page / Additional
  // Stationery Piece / Additional Concept Direction show a stepper
  // driven by the same UI ACM uses.
  if (service.slug !== ACM_SLUG) {
    items = items
      .filter((i) => isAddonEligible(i, activeCodes))
      .map((i) => i.quantity_enabled ? { ...i, _quantityEnabled: true } : i);
  }

  // For ACM: surface the Main Artwork base format selector once the
  // Campaign Design Package (which includes Main Event Artwork) is
  // selected. Same visual language as the Event Creative Step 2 selector.
  const acmPackage = (service.standalone_items || []).find(
    (i) => (i.item_code || "") === ACM_CAMPAIGN_PACKAGE_ITEM_CODE,
  );
  const acmPackageSelected = !!acmPackage && selectedItemIds.has(acmPackage.id);
  // BMD: surface the same base-format picker when Standard or Advanced
  // Artwork is selected. Uses the exact same MainArtworkBaseFormatSelector
  // component + CMS-driven `main_artwork_config.available_formats` list.
  const bmdArtworkItems = (service.standalone_items || []).filter(
    (i) => BMD_MAIN_ARTWORK_PRODUCERS.has(i.item_code || "") && (i.active !== false),
  );
  const bmdArtworkSelected = bmdArtworkItems.some((i) => selectedItemIds.has(i.id));
  const showBaseSelector =
    (service?.slug === ACM_SLUG && acmPackageSelected)
    || (service?.slug === "business-marketing-design" && bmdArtworkSelected);
  const baseTestidPrefix = service?.slug === ACM_SLUG
    ? "acm-main-artwork-base"
    : "bmd-main-artwork-base";
  return (
    <>
      {showBaseSelector && setMainArtworkBaseFormat && (
        <MainArtworkBaseFormatSelector
          value={mainArtworkBaseFormat}
          onChange={setMainArtworkBaseFormat}
          packageInclusionsSet={getMainArtworkIncludedSet(service, null)}
          formats={getMainArtworkFormatsList(service)}
          formatGroups={getMainArtworkFormatGroups(service)}
          testidPrefix={baseTestidPrefix}
        />
      )}
      {service.slug === ACM_SLUG ? (
        // Batch 2 P.B.5 — 4-group progressive disclosure. Every product,
        // price, mutex, quantity, dependency, mainArtwork logic is the
        // one from `items` above; we simply partition the array by
        // group and render each subset in its own labelled ItemsGrid.
        // Campaign-dependent add-ons (already injected right after the
        // package row inside `items`) are placed inside the Campaign
        // Package group because their DB item_codes belong to that
        // group's memberItemCodes set (`acm_additional_campaign_graphic`,
        // `acm_main_artwork_additional_format`, `acm_story_upgrade_*`).
        <div className="space-y-8">
          {ACM_STEP1_GROUPS.map((group) => {
            const groupItems = items.filter((it) => {
              // A migrated view of a synthetic ACM dependent carries its
              // real item_code (`acm_additional_campaign_graphic`, etc.)
              // via getMigratedItemView, so item_code membership works
              // uniformly for both original DB rows and injected views.
              const code = it.item_code
                || ACM_SYNTHETIC_ITEM_CODE_BY_ID[it.id]
                || "";
              // Story-upgrade group membership: all 3 upgrades belong to
              // the Campaign Package group. Extra membership hooks stay
              // narrow to codes we defined above.
              if (group.memberItemCodes.has(code)) return true;
              if (group.id === ACM_GROUP_CAMPAIGN) {
                if (code.startsWith("acm_story_upgrade")) return true;
                if (code === "acm_additional_campaign_graphic") return true;
                if (code === "acm_main_artwork_additional_format") return true;
              }
              return false;
            });
            if (groupItems.length === 0) return null;
            return (
              <div
                key={group.id}
                data-testid={`acm-group-${group.id}`}
                className="space-y-3"
              >
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF6600]">
                    {group.heading}
                  </div>
                  <p className="mt-1 text-[13px] text-[#A3A3A3] leading-snug">
                    {group.subhead}
                  </p>
                </div>
                <ItemsGrid
                  items={groupItems}
                  quoteMode={quoteMode}
                  selectedItemIds={selectedItemIds}
                  setSelectedItemIds={setSelectedItemIds}
                  selectedItemQtys={selectedItemQtys}
                  setItemQuantity={setItemQuantity}
                  testidPrefix="item"
                  mainArtworkFormatsById={mainArtworkFormats}
                  updateMainArtworkFormats={updateMainArtworkFormats}
                  mainArtworkIncludedSet={getMainArtworkIncludedSet(service, null)}
                  mainArtworkBaseFormatId={mainArtworkBaseFormat?.id || null}
                  mainArtworkFormatsList={getMainArtworkFormatsList(service)}
                  mainArtworkFormatGroups={getMainArtworkFormatGroups(service)}
                />
              </div>
            );
          })}
          {(() => {
            // Fallback group: anything active/renderable in `items` that
            // did not land in one of the four semantic groups above.
            // Keeps CMS as the source of truth — an admin-added item
            // with an unrecognised item_code still surfaces to the
            // customer instead of silently vanishing.
            const mappedCodes = new Set();
            ACM_STEP1_GROUPS.forEach((g) => g.memberItemCodes.forEach((c) => mappedCodes.add(c)));
            const additionalItems = items.filter((it) => {
              const code = it.item_code
                || ACM_SYNTHETIC_ITEM_CODE_BY_ID[it.id]
                || "";
              if (mappedCodes.has(code)) return false;
              // Campaign-package dependents are attached to the
              // Campaign group by the extra membership rules above.
              if (code.startsWith("acm_story_upgrade")) return false;
              if (code === "acm_additional_campaign_graphic") return false;
              if (code === "acm_main_artwork_additional_format") return false;
              return true;
            });
            if (additionalItems.length === 0) return null;
            const group = ACM_STEP1_ADDITIONAL_GROUP;
            return (
              <div
                key={group.id}
                data-testid={`acm-group-${group.id}`}
                className="space-y-3"
              >
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF6600]">
                    {group.heading}
                  </div>
                  <p className="mt-1 text-[13px] text-[#A3A3A3] leading-snug">
                    {group.subhead}
                  </p>
                </div>
                <ItemsGrid
                  items={additionalItems}
                  quoteMode={quoteMode}
                  selectedItemIds={selectedItemIds}
                  setSelectedItemIds={setSelectedItemIds}
                  selectedItemQtys={selectedItemQtys}
                  setItemQuantity={setItemQuantity}
                  testidPrefix="item"
                  mainArtworkFormatsById={mainArtworkFormats}
                  updateMainArtworkFormats={updateMainArtworkFormats}
                  mainArtworkIncludedSet={getMainArtworkIncludedSet(service, null)}
                  mainArtworkBaseFormatId={mainArtworkBaseFormat?.id || null}
                  mainArtworkFormatsList={getMainArtworkFormatsList(service)}
                  mainArtworkFormatGroups={getMainArtworkFormatGroups(service)}
                />
              </div>
            );
          })()}
        </div>
      ) : service.slug === "business-marketing-design" ? (
        // BMD Step-1 grouping — mirrors ACM Batch 2 pattern. Partitions
        // the flat CMS item list into 4 semantic groups + a fallback so
        // any future active item whose item_code is unmapped still
        // surfaces to the customer instead of silently vanishing. CMS
        // remains the source of truth; no frontend commercial logic.
        <div className="space-y-8">
          {BMD_STEP1_GROUPS.map((group) => {
            const groupItems = items.filter((it) => {
              const code = it.item_code || "";
              return group.memberItemCodes.has(code);
            });
            if (groupItems.length === 0) return null;
            // Step-1 UX Pass — customer-scan order + contextual nesting
            // of dependent options directly beneath their family.
            const arrangedItems = arrangeBmdItemsForStep1(groupItems, group.id, activeCodes);
            return (
              <div
                key={group.id}
                data-testid={`bmd-group-${group.id}`}
                data-focus-target={group.id}
                className="space-y-3 rounded-xl transition-shadow duration-300 data-[focus-active=true]:ring-2 data-[focus-active=true]:ring-[#FF6600]/60 data-[focus-active=true]:ring-offset-2 data-[focus-active=true]:ring-offset-[#0F0F0F]"
              >
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF6600]">
                    {group.heading}
                  </div>
                  <p className="mt-1 text-[13px] text-[#A3A3A3] leading-snug">
                    {group.subhead}
                  </p>
                </div>
                <ItemsGrid
                  items={arrangedItems}
                  quoteMode={quoteMode}
                  selectedItemIds={selectedItemIds}
                  setSelectedItemIds={setSelectedItemIds}
                  selectedItemQtys={selectedItemQtys}
                  setItemQuantity={setItemQuantity}
                  testidPrefix="item"
                  mainArtworkFormatsById={mainArtworkFormats}
                  updateMainArtworkFormats={updateMainArtworkFormats}
                  mainArtworkIncludedSet={getMainArtworkIncludedSet(service, null)}
                  mainArtworkBaseFormatId={mainArtworkBaseFormat?.id || null}
                  mainArtworkFormatsList={getMainArtworkFormatsList(service)}
                  mainArtworkFormatGroups={getMainArtworkFormatGroups(service)}
                />
              </div>
            );
          })}
          {(() => {
            const mappedCodes = new Set();
            BMD_STEP1_GROUPS.forEach((g) => g.memberItemCodes.forEach((c) => mappedCodes.add(c)));
            const additionalItems = items.filter((it) => !mappedCodes.has(it.item_code || ""));
            if (additionalItems.length === 0) return null;
            const group = BMD_STEP1_ADDITIONAL_GROUP;
            return (
              <div
                key={group.id}
                data-testid={`bmd-group-${group.id}`}
                className="space-y-3"
              >
                <div>
                  <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF6600]">
                    {group.heading}
                  </div>
                  <p className="mt-1 text-[13px] text-[#A3A3A3] leading-snug">
                    {group.subhead}
                  </p>
                </div>
                <ItemsGrid
                  items={additionalItems}
                  quoteMode={quoteMode}
                  selectedItemIds={selectedItemIds}
                  setSelectedItemIds={setSelectedItemIds}
                  selectedItemQtys={selectedItemQtys}
                  setItemQuantity={setItemQuantity}
                  testidPrefix="item"
                  mainArtworkFormatsById={mainArtworkFormats}
                  updateMainArtworkFormats={updateMainArtworkFormats}
                  mainArtworkIncludedSet={getMainArtworkIncludedSet(service, null)}
                  mainArtworkBaseFormatId={mainArtworkBaseFormat?.id || null}
                  mainArtworkFormatsList={getMainArtworkFormatsList(service)}
                  mainArtworkFormatGroups={getMainArtworkFormatGroups(service)}
                />
              </div>
            );
          })()}
        </div>
      ) : (service.slug === "packaging-labels-vehicle-wraps" || service.slug === "3d-rendering-visualization") ? (
        // PLVW / 3D Step-1 grouping — same pattern as BMD. Groups and
        // family definitions are keyed on stable item_codes (plvw_* /
        // viz_*). CMS remains the source of truth for prices,
        // dependencies and copy; the frontend only decides visual
        // arrangement.
        <div className="space-y-8">
          {(service.slug === "packaging-labels-vehicle-wraps" ? PLVW_STEP1_GROUPS : VIZ_STEP1_GROUPS).map((group) => {
            const groupItems = items.filter((it) => group.memberItemCodes.has(it.item_code || ""));
            if (groupItems.length === 0) return null;
            const families = (service.slug === "packaging-labels-vehicle-wraps" ? PLVW_FAMILIES : VIZ_FAMILIES)[group.id] || [];
            const arrangedItems = arrangeCustomFamilyItemsForStep1(groupItems, families, activeCodes);
            const groupTestid = service.slug === "packaging-labels-vehicle-wraps"
              ? `plvw-group-${group.id.replace(/^plvw-/, "")}`
              : `viz-group-${group.id.replace(/^viz-/, "")}`;
            return (
              <div
                key={group.id}
                data-testid={groupTestid}
                data-focus-target={group.id}
                className="space-y-3 rounded-xl transition-shadow duration-300 data-[focus-active=true]:ring-2 data-[focus-active=true]:ring-[#FF6600]/60 data-[focus-active=true]:ring-offset-2 data-[focus-active=true]:ring-offset-[#0F0F0F]"
              >
                <div>
                  <h3 className="font-display text-xs font-bold uppercase tracking-[0.14em] text-[#FF6600]">
                    {group.heading}
                  </h3>
                  {group.subhead && (
                    <p className="text-xs text-[#A3A3A3] mt-1 leading-snug">{group.subhead}</p>
                  )}
                </div>
                <ItemsGrid
                  items={arrangedItems}
                  quoteMode={quoteMode}
                  selectedItemIds={selectedItemIds}
                  setSelectedItemIds={setSelectedItemIds}
                  selectedItemQtys={selectedItemQtys}
                  setItemQuantity={setItemQuantity}
                  testidPrefix="item"
                  mainArtworkFormatsById={mainArtworkFormats}
                  updateMainArtworkFormats={updateMainArtworkFormats}
                  mainArtworkIncludedSet={getMainArtworkIncludedSet(service, null)}
                  mainArtworkBaseFormatId={mainArtworkBaseFormat?.id || null}
                  mainArtworkFormatsList={getMainArtworkFormatsList(service)}
                  mainArtworkFormatGroups={getMainArtworkFormatGroups(service)}
                />
              </div>
            );
          })}
        </div>
      ) : (
        <ItemsGrid
          items={items}
          quoteMode={quoteMode}
          selectedItemIds={selectedItemIds}
          setSelectedItemIds={setSelectedItemIds}
          selectedItemQtys={selectedItemQtys}
          setItemQuantity={setItemQuantity}
          testidPrefix="item"
          mainArtworkFormatsById={mainArtworkFormats}
          updateMainArtworkFormats={updateMainArtworkFormats}
          mainArtworkIncludedSet={getMainArtworkIncludedSet(service, null)}
          mainArtworkBaseFormatId={mainArtworkBaseFormat?.id || null}
          mainArtworkFormatsList={getMainArtworkFormatsList(service)}
          mainArtworkFormatGroups={getMainArtworkFormatGroups(service)}
        />
      )}
    </>
  );
};

const KitsStep1 = ({ service, quoteMode, selectedKitId, setSelectedKitId }) => {
  const kits = (service.kits || []).filter((k) => k.active !== false);
  const items = service.standalone_items || [];
  if (kits.length === 0) return <EmptyBody message="This service is being configured — check back soon for available kits." />;
  return (
    <div className="grid md:grid-cols-3 gap-4 md:gap-6 items-start">
      {kits.map((k, i) => {
        const Icon = tierIconFor(i, kits.length);
        const includedNames = items.filter((it) => (k.included_item_ids || []).includes(it.id)).map((it) => it.name);
        // Phase C — Step-1 kit deliverables teaser. Source of truth is
        // the CMS `kit.included_items` list (each entry carries a
        // `name`); no hard-coded counts or commercial labels are added
        // here. First 3 names are shown as an orientation hint; when
        // there are more, we append "+ more" so the customer knows
        // the card is not the full list. Presentation stays visually
        // secondary to the kit name / price / CTA.
        const inc = Array.isArray(k.included_items) ? k.included_items : [];
        const teaserNames = inc
          .slice(0, 3)
          .map((it) => (it && typeof it === "object" ? (it.name || "").trim() : ""))
          .filter(Boolean);
        const teaser = inc.length > 0
          ? `${inc.length} deliverable${inc.length === 1 ? "" : "s"} · ${teaserNames.join(", ")}${inc.length > 3 ? " + more" : ""}`
          : undefined;
        return (
          <AnchoredCard
            key={k.id}
            variant="primary"
            icon={Icon}
            name={k.name}
            priceLabel={quoteMode ? undefined : "Bundle Price"}
            priceValue={quoteMode ? undefined : listMoney(k.bundle_price, k.price_usd)}
            quoteMode={quoteMode}
            description={k.description || includedNames.join(" • ")}
            teaser={teaser}
            includedNames={includedNames}
            active={k.id === selectedKitId}
            popular={!!k.popular}
            selectedLabel="Selected"
            defaultLabel="Select Kit"
            mobileOrderFirst={!!k.popular}
            onClick={() => setSelectedKitId(k.id === selectedKitId ? null : k.id)}
            testid={`kit-${k.id}`}
          />
        );
      })}
    </div>
  );
};

// ---------- Checkbox list — replaces the card grid for secondary decision layers
// (add-ons in tiered/volume_tiered Step 2, standalone_items in custom_items Step 1,
// and the kit item-pool "build your own from these items"). The Phase 6.1 muted
// card-grid was still visually mimicking the Step 1 tier cards; switching to a
// genuinely different pattern (list, no cards) gives Step 1 the exclusive card
// treatment and lets the eye scan optional extras faster.
//
// Popular-item dominance (Phase 6.2) still applies — re-expressed as a
// "RECOMMENDED" text label + star icon + subtle left-edge accent on the row,
// since the card badge/border language is gone. Popular row is force-ordered
// first via `order-first` so it always heads the list.
// ---------- Main Artwork — Additional Size / Format picker row ----------
// Renders a full <li> row (self-contained) inside CheckboxList. Replaces
// the previous linear qty stepper with a package-aware multi-select
// format picker. Included formats are shown as ✓ INCLUDED and cannot
// contribute to price. Custom Size reveals width/height/unit inputs and
// only counts as paid once all three are provided.
const MainArtworkPickerRow = ({
  it,
  testidPrefix,
  entries = [],
  includedSet,
  baseFormatId,
  formats = [],
  formatGroups = [],
  onChange, // (nextEntries) => void
}) => {
  const [open, setOpen] = React.useState(false);
  const selectedIds = new Set(entries.map((e) => e.id));
  const paidCount = countPaidMainArtworkFormats(entries, includedSet, baseFormatId);
  const total = sumPaidMainArtworkFormatPrice(entries, includedSet, baseFormatId, formats);
  const rowSelected = paidCount > 0;

  const toggleId = (fid) => {
    // Base Main Artwork format — already delivered free; cannot double.
    // Custom is instance-keyed by dimensions, so a custom additional
    // never collides with a custom base — always allow.
    if (baseFormatId && fid === baseFormatId && fid !== "custom") return;
    // Format already included as a separate package deliverable.
    if (fid !== "custom" && includedSet.has(fid)) return;
    let next;
    if (selectedIds.has(fid)) {
      next = entries.filter((e) => e.id !== fid);
    } else if (fid === "custom") {
      next = [...entries, { id: "custom", width: "", height: "", unit: "px" }];
    } else {
      next = [...entries, { id: fid }];
    }
    onChange(next);
  };
  const updateCustom = (patch) => {
    const next = entries.map((e) => (e.id === "custom" ? { ...e, ...patch } : e));
    onChange(next);
  };

  const groups = formatGroups.length > 0 ? formatGroups : ["DIGITAL / SOCIAL", "PRINT", "CUSTOM"];
  const description = it._description || it.description;

  return (
    <li>
      <div
        data-testid={`${testidPrefix}-${it.id}`}
        data-active={rowSelected ? "true" : undefined}
        data-formats-count={paidCount}
        className={`relative py-3.5 pl-3 pr-3 transition-colors ${rowSelected ? "" : "hover:bg-white/[0.02]"}`}
      >
        <div className="flex items-start gap-3 sm:gap-4">
          <div className="flex-1 min-w-0">
            <div className="text-sm font-semibold leading-tight text-[#D1D1D1]">
              {it._displayName || it.name}
            </div>
            {description && (
              <p className="text-xs text-[#7A7A7A] mt-1 leading-snug">{description}</p>
            )}
            <div className="mt-2 flex items-center gap-3">
              <button
                type="button"
                data-testid={`${testidPrefix}-${it.id}-picker-toggle`}
                onClick={() => setOpen((o) => !o)}
                className="inline-flex items-center gap-1.5 rounded-full border border-[#3A3A3A] bg-[#0F0F0F] px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.14em] text-[#D1D1D1] hover:border-[#FF6600]/60 transition-colors"
              >
                {open ? "Hide sizes / formats" : "Select sizes / formats"}
              </button>
              {paidCount > 0 && (
                <span
                  data-testid={`${testidPrefix}-${it.id}-selected-count`}
                  className="text-[11px] font-semibold text-[#A3A3A3]"
                >
                  {paidCount} {paidCount === 1 ? "format" : "formats"} selected
                </span>
              )}
            </div>
          </div>
          <span className="shrink-0 self-center text-right">
            <span
              data-testid={`${testidPrefix}-${it.id}-price`}
              className="font-display font-bold text-sm text-[#FF6600] tabular-nums"
            >
              {money(paidCount > 0 ? total : MAIN_ARTWORK_FORMAT_PRICE)}
            </span>
            {paidCount === 0 && (
              <span className="block text-[10px] text-[#7A7A7A] font-medium mt-0.5">each</span>
            )}
          </span>
        </div>

        {open && (
          <div
            data-testid={`${testidPrefix}-${it.id}-picker`}
            className="mt-3 ml-0 rounded-md border border-[#1F1F1F] bg-[#0A0A0A] p-3"
          >
            {groups.map((g) => (
              <div key={g} className="mb-2 last:mb-0">
                <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#7A7A7A] mb-1.5">
                  {g}
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                  {formats.filter((f) => f.group === g).map((f) => {
                    const isSelected = selectedIds.has(f.id);
                    // Custom is instance-keyed (by dimensions), so a
                    // custom additional can coexist with a custom base —
                    // never treat the picker's Custom option as "the base".
                    const isBase = baseFormatId && f.id === baseFormatId && f.id !== "custom";
                    const isPackageInc = f.id !== "custom" && includedSet.has(f.id);
                    const disabled = isBase || isPackageInc;
                    return (
                      <label
                        key={f.id}
                        data-testid={`${testidPrefix}-${it.id}-format-${f.id}`}
                        data-base={isBase ? "true" : undefined}
                        data-included={isPackageInc ? "true" : undefined}
                        data-selected={isSelected ? "true" : undefined}
                        className={`flex items-start gap-2 rounded border px-2.5 py-2 text-xs ${
                          disabled
                            ? "border-[#1F1F1F] bg-[#0F0F0F]/50 cursor-not-allowed"
                            : isSelected
                              ? "border-[#FF6600] bg-[#FF6600]/[0.06] cursor-pointer"
                              : "border-[#262626] bg-transparent hover:border-[#3A3A3A] cursor-pointer"
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isSelected || isBase}
                          disabled={disabled}
                          onChange={() => toggleId(f.id)}
                          className="sr-only peer"
                        />
                        <span
                          aria-hidden
                          className={`mt-0.5 w-4 h-4 rounded-sm border shrink-0 grid place-items-center transition-colors ${
                            isSelected || isBase
                              ? "bg-[#FF6600] border-[#FF6600] text-black"
                              : disabled
                                ? "bg-transparent border-[#2A2A2A]"
                                : "bg-transparent border-[#3A3A3A]"
                          }`}
                        >
                          {(isSelected || isBase) && <Check className="w-2.5 h-2.5" strokeWidth={3.5} />}
                        </span>
                        <span className="flex-1 min-w-0">
                          <span className="font-semibold text-[#D1D1D1] block leading-tight">
                            {f.label}
                          </span>
                          {formatDimensions(f) && (
                            <span className="block text-[10px] text-[#7A7A7A] mt-0.5">
                              {formatDimensions(f)}
                            </span>
                          )}
                          {f.guidance && (
                            <span
                              data-testid={`${testidPrefix}-${it.id}-format-${f.id}-guidance`}
                              className="block text-[10px] text-[#7A7A7A]/90 mt-0.5 italic leading-snug"
                            >
                              {f.guidance}
                            </span>
                          )}
                        </span>
                        <span
                          className={`shrink-0 self-center text-[10px] font-bold uppercase tracking-[0.14em] ${
                            isBase
                              ? "text-[#FF6600]"
                              : isPackageInc
                                ? "text-[#4CB782]"
                                : "text-[#FF6600]"
                          }`}
                        >
                          {isBase
                            ? "✓ Main Artwork"
                            : isPackageInc
                              ? "✓ In Package"
                              : `+ ${listMoney(f.additional_price || MAIN_ARTWORK_FORMAT_PRICE, (f.additional_price || MAIN_ARTWORK_FORMAT_PRICE) === MAIN_ARTWORK_FORMAT_PRICE ? MAIN_ARTWORK_FORMAT_PRICE_USD : undefined)}`}
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            ))}
            {selectedIds.has("custom") && (() => {
              const custom = entries.find((e) => e.id === "custom") || {};
              return (
                <div
                  data-testid={`${testidPrefix}-${it.id}-custom-fields`}
                  className="mt-2 grid grid-cols-3 gap-2 rounded border border-[#262626] bg-[#0F0F0F] p-2"
                >
                  <label className="text-[10px] uppercase tracking-[0.14em] text-[#7A7A7A]">
                    Width
                    <input
                      type="text"
                      inputMode="decimal"
                      data-testid={`${testidPrefix}-${it.id}-custom-width`}
                      value={custom.width || ""}
                      onChange={(e) => updateCustom({ width: e.target.value })}
                      className="mt-1 w-full rounded border border-[#3A3A3A] bg-[#0A0A0A] px-2 py-1 text-xs text-white outline-none focus:border-[#FF6600]"
                    />
                  </label>
                  <label className="text-[10px] uppercase tracking-[0.14em] text-[#7A7A7A]">
                    Height
                    <input
                      type="text"
                      inputMode="decimal"
                      data-testid={`${testidPrefix}-${it.id}-custom-height`}
                      value={custom.height || ""}
                      onChange={(e) => updateCustom({ height: e.target.value })}
                      className="mt-1 w-full rounded border border-[#3A3A3A] bg-[#0A0A0A] px-2 py-1 text-xs text-white outline-none focus:border-[#FF6600]"
                    />
                  </label>
                  <label className="text-[10px] uppercase tracking-[0.14em] text-[#7A7A7A]">
                    Unit
                    <select
                      data-testid={`${testidPrefix}-${it.id}-custom-unit`}
                      value={custom.unit || "px"}
                      onChange={(e) => updateCustom({ unit: e.target.value })}
                      className="mt-1 w-full rounded border border-[#3A3A3A] bg-[#0A0A0A] px-2 py-1 text-xs text-white outline-none focus:border-[#FF6600]"
                    >
                      {MAIN_ARTWORK_UNITS.map((u) => (
                        <option key={u} value={u}>{u}</option>
                      ))}
                    </select>
                  </label>
                  {!isValidMainArtworkEntry(custom) && (
                    <p className="col-span-3 text-[10px] text-[#FF6600]/80 italic">
                      Enter width, height and unit to include this custom size.
                    </p>
                  )}
                </div>
              );
            })()}
          </div>
        )}
      </div>
    </li>
  );
};

// Base-format single-select for the primary Main Artwork output. AWG 0 —
// included with any package that contains the "Main Event Artwork" /
// "Main Artwork" deliverable. Custom Size reveals width/height/unit
// inputs and remains invalid until all three are populated.
// `packageName` drives the label copy. `included` (set of preset ids)
// disables options already included as separate package deliverables —
// e.g. under ACM Campaign Design Package, A3 Portrait is delivered as a
// distinct "A3 Poster" adaptation and therefore not selectable here.
const MainArtworkBaseFormatSelector = ({ value, onChange, packageInclusionsSet, formats = [], formatGroups = [], testidPrefix = "main-artwork-base" }) => {
  const selectedId = value?.id || null;
  const included = packageInclusionsSet || new Set();
  const groups = formatGroups.length > 0 ? formatGroups : ["DIGITAL / SOCIAL", "PRINT", "CUSTOM"];
  // Only formats flagged base-eligible in the DB can be picked as the
  // primary Main Artwork output. Facebook Cover is base_eligible=false
  // in the seed so it's implicitly excluded here.
  const options = formats.filter((f) => f.base_eligible !== false);
  const pick = (fid) => {
    if (included.has(fid)) return; // already delivered separately — cannot double
    if (fid === "custom") {
      onChange({ id: "custom", width: "", height: "", unit: "px" });
    } else {
      onChange({ id: fid });
    }
  };
  const patchCustom = (patch) => {
    if (selectedId !== "custom") return;
    onChange({ ...value, ...patch });
  };
  const invalid = !selectedId || (selectedId === "custom" && !isValidMainArtworkEntry(value));
  return (
    <div
      data-testid={testidPrefix}
      data-selected-format={selectedId || undefined}
      data-invalid={invalid ? "true" : undefined}
      className="mt-4 mb-5 rounded-md border border-[#1F1F1F] bg-white/[0.02] px-4 py-3"
    >
      <div className="flex items-baseline justify-between gap-3">
        <div>
          <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#D1D1D1]">
            Main Artwork Format <span className="text-[#FF6600]">*</span>
          </div>
          <p className="mt-1 text-xs text-[#7A7A7A] leading-snug">
            Choose the primary format for your Main Artwork. One format is included with your package.
          </p>
        </div>
        <span className="shrink-0 text-[11px] font-bold uppercase tracking-[0.14em] text-[#4CB782]">
          {money(0)} · Included
        </span>
      </div>
      <div className="mt-2.5">
        {groups.map((g) => (
          <div key={g} className="mb-2 last:mb-0">
            <div className="text-[10px] font-bold uppercase tracking-[0.18em] text-[#7A7A7A] mb-1.5">{g}</div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
              {options.filter((f) => f.group === g).map((f) => {
                const isSelected = selectedId === f.id;
                const isBlocked = included.has(f.id);
                return (
                  <label
                    key={f.id}
                    data-testid={`${testidPrefix}-option-${f.id}`}
                    data-selected={isSelected ? "true" : undefined}
                    data-blocked={isBlocked ? "true" : undefined}
                    className={`flex items-start gap-2 rounded border px-2.5 py-2 text-xs ${
                      isBlocked
                        ? "border-[#1F1F1F] bg-[#0F0F0F]/50 cursor-not-allowed"
                        : isSelected
                          ? "border-[#FF6600] bg-[#FF6600]/[0.06] cursor-pointer"
                          : "border-[#262626] bg-transparent hover:border-[#3A3A3A] cursor-pointer"
                    }`}
                  >
                    <input
                      type="radio"
                      name={testidPrefix}
                      checked={isSelected}
                      disabled={isBlocked}
                      onChange={() => pick(f.id)}
                      className="sr-only peer"
                    />
                    <span
                      aria-hidden
                      className={`mt-0.5 w-4 h-4 rounded-full border shrink-0 grid place-items-center transition-colors ${
                        isSelected
                          ? "border-[#FF6600]"
                          : isBlocked
                            ? "border-[#2A2A2A]"
                            : "border-[#3A3A3A]"
                      }`}
                    >
                      {isSelected && <span className="w-2 h-2 rounded-full bg-[#FF6600]" />}
                    </span>
                    <span className="flex-1 min-w-0">
                      <span className="font-semibold text-[#D1D1D1] block leading-tight">{f.label}</span>
                      {formatDimensions(f) && (
                        <span className="block text-[10px] text-[#7A7A7A] mt-0.5">{formatDimensions(f)}</span>
                      )}
                      {f.guidance && (
                        <span
                          data-testid={`${testidPrefix}-option-${f.id}-guidance`}
                          className="block text-[10px] text-[#7A7A7A]/90 mt-0.5 italic leading-snug"
                        >
                          {f.guidance}
                        </span>
                      )}
                    </span>
                    {isBlocked && (
                      <span className="shrink-0 self-center text-[9px] font-bold uppercase tracking-[0.14em] text-[#7A7A7A]">
                        In Package
                      </span>
                    )}
                  </label>
                );
              })}
            </div>
          </div>
        ))}
        {selectedId === "custom" && (
          <div
            data-testid={`${testidPrefix}-custom-fields`}
            className="mt-2 grid grid-cols-3 gap-2 rounded border border-[#262626] bg-[#0F0F0F] p-2"
          >
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#7A7A7A]">
              Width
              <input
                type="text"
                inputMode="decimal"
                data-testid={`${testidPrefix}-custom-width`}
                value={value?.width || ""}
                onChange={(e) => patchCustom({ width: e.target.value })}
                className="mt-1 w-full rounded border border-[#3A3A3A] bg-[#0A0A0A] px-2 py-1 text-xs text-white outline-none focus:border-[#FF6600]"
              />
            </label>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#7A7A7A]">
              Height
              <input
                type="text"
                inputMode="decimal"
                data-testid={`${testidPrefix}-custom-height`}
                value={value?.height || ""}
                onChange={(e) => patchCustom({ height: e.target.value })}
                className="mt-1 w-full rounded border border-[#3A3A3A] bg-[#0A0A0A] px-2 py-1 text-xs text-white outline-none focus:border-[#FF6600]"
              />
            </label>
            <label className="text-[10px] uppercase tracking-[0.14em] text-[#7A7A7A]">
              Unit
              <select
                data-testid={`${testidPrefix}-custom-unit`}
                value={value?.unit || "px"}
                onChange={(e) => patchCustom({ unit: e.target.value })}
                className="mt-1 w-full rounded border border-[#3A3A3A] bg-[#0A0A0A] px-2 py-1 text-xs text-white outline-none focus:border-[#FF6600]"
              >
                {MAIN_ARTWORK_UNITS.map((u) => (
                  <option key={u} value={u}>{u}</option>
                ))}
              </select>
            </label>
            {selectedId === "custom" && !isValidMainArtworkEntry(value) && (
              <p className="col-span-3 text-[10px] text-[#FF6600]/80 italic">
                Enter width, height and unit to complete your Main Artwork format.
              </p>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

const CheckboxList = ({ items, selectedIds, toggle, testidPrefix, pricePrefix = "", quoteMode = false, quantitiesById, setQuantity, mainArtworkFormatsById, updateMainArtworkFormats, mainArtworkIncludedSet, mainArtworkBaseFormatId, mainArtworkFormatsList = [], mainArtworkFormatGroups = [] }) => {
  if (items.length === 0) return null;
  // BMD architecture v1 (Feb 2026) — generic customer-facing family
  // heading. When two consecutive rows share the same option_group,
  // render their `option_group_label` once above the first row so the
  // 2/4/6 Restaurant Menu variants (etc.) read as ONE decision instead
  // of three loose radio buttons. Any group whose members carry an
  // empty label renders no heading (backwards-compatible for ACM/ECP
  // groups that don't want one). Group heading tracking is per render.
  const _labelSeenFor = new Set();
  const _labelForItem = (it) => {
    const og = (it.option_group || "").trim();
    if (!og) return null;
    // Pick the first non-empty label across every row that shares this
    // group; deterministic across renders because `items` array order
    // is stable.
    const label = items
      .filter((x) => (x.option_group || "").trim() === og)
      .map((x) => (x.option_group_label || "").trim())
      .find(Boolean);
    if (!label) return null;
    if (_labelSeenFor.has(og)) return null;
    _labelSeenFor.add(og);
    return { og, label };
  };
  return (
    <ul
      data-testid={`${testidPrefix}-list`}
      className="flex flex-col divide-y divide-[#1F1F1F] border-y border-[#1F1F1F]"
    >
      {items.map((it) => {
        // Group heading — emitted once per option_group, immediately
        // above the first row belonging to that group.
        const heading = _labelForItem(it);
        const headingNode = heading ? (
          <li
            key={`heading-${heading.og}`}
            data-testid={`${testidPrefix}-group-${heading.og}`}
            className="pt-4 pb-1.5 px-3 text-[11px] font-bold uppercase tracking-[0.16em] text-[#A3A3A3]"
          >
            {heading.label}
          </li>
        ) : null;
        // Main Artwork multi-format picker — self-contained row (skips the
        // standard checkbox/qty markup below). Still emits any pending
        // option_group heading before its own row so contextual headings
        // like "Options for your Promotional Artwork" survive when the
        // first row of the group is a Main Artwork picker.
        if (isMainArtworkItem(it) && updateMainArtworkFormats) {
          return (
            <React.Fragment key={it.id}>
              {headingNode}
              <MainArtworkPickerRow
                it={it}
                testidPrefix={testidPrefix}
                entries={mainArtworkFormatsById?.[it.id] || []}
                includedSet={mainArtworkIncludedSet || new Set()}
                baseFormatId={mainArtworkBaseFormatId || null}
                formats={mainArtworkFormatsList}
                formatGroups={mainArtworkFormatGroups}
                onChange={(next) => updateMainArtworkFormats(it.id, next)}
              />
            </React.Fragment>
          );
        }
        const active = selectedIds.has(it.id);
        const popular = !!it.popular;
        const disabled = !!it._disabled;
        const description = it._description || it.description;
        const qtyEnabled = !!it._quantityEnabled && !disabled && !quoteMode && !it.request_estimate;
        const qty = qtyEnabled ? (quantitiesById?.[it.id] || 0) : (active ? 1 : 0);
        // Per-item stepper bounds. BMD Step-1 UX pass (Feb 2026) — every
        // qty-enabled row in this list represents an OPTIONAL additive
        // item (Additional Page / Slide / Revision / Concept / Format /
        // Source File). The customer must always be able to decrement
        // it back to 0 (= unselected). CMS `quantity_min` still governs
        // pricing schedules and downstream validation; it does not gate
        // the UI floor. ACM/Event Creative already default to 0 via
        // EVENT_CREATIVE_QTY_MIN, so this is a net no-op for those.
        const qtyMin = 0;
        const qtyMax = (Number.isFinite(it?.quantity_max) && it.quantity_max > 0) ? it.quantity_max : EVENT_CREATIVE_QTY_MAX;
        const computedPrice = qtyEnabled
          ? (() => {
              // Same DB → legacy → flat priority as effectiveItemPrice.
              const dbTotal = priceFromDbSchedule(it?.quantity_pricing, qty);
              if (dbTotal > 0) return dbTotal;
              const scheduled = eventCreativeQtyPrice(it.name, qty);
              if (scheduled > 0) return scheduled;
              return qty > 0 ? (it.price || 0) * qty : 0;
            })()
          : it.price;
        const showAsSelected = qtyEnabled ? qty > 0 : active;
        const rowInteractive = !qtyEnabled;
        // Step-1 UX pass (Feb 2026) — dependent option rows nested
        // under a family read as visually secondary so the customer
        // clearly parses primary vs. add-on. Tinted background, indented
        // padding, and a data-attribute for regression selectors.
        const isSecondaryOption = !!it._secondaryOption;
        return (
          <React.Fragment key={it.id}>
            {headingNode}
          <li
            data-popular={popular ? "true" : undefined}
            data-secondary={isSecondaryOption ? "true" : undefined}
            data-item-code={it.item_code || undefined}
            className={`${popular ? "order-first" : ""} ${isSecondaryOption ? "bg-white/[0.015]" : ""} transition-shadow duration-300 data-[focus-active=true]:ring-2 data-[focus-active=true]:ring-[#FF6600]/60 data-[focus-active=true]:ring-offset-2 data-[focus-active=true]:ring-offset-[#0F0F0F] data-[focus-active=true]:rounded-md`}
          >
            <label
              data-testid={`${testidPrefix}-${it.id}`}
              data-active={showAsSelected ? "true" : undefined}
              data-disabled={disabled ? "true" : undefined}
              data-qty={qtyEnabled ? qty : undefined}
              aria-disabled={disabled || undefined}
              className={`relative flex flex-wrap sm:flex-nowrap items-start gap-3 sm:gap-4 py-3 pl-3 pr-3 transition-colors ${popular ? `${POPULAR_ROW_CLASSES} pl-[10px]` : ""} ${isSecondaryOption ? "pl-5 sm:pl-6 py-2.5" : ""} ${disabled ? "cursor-not-allowed opacity-50" : rowInteractive ? "cursor-pointer hover:bg-white/[0.02]" : "hover:bg-white/[0.02]"}`}
            >
              {qtyEnabled ? (
                <QuantityStepper
                  testid={`${testidPrefix}-${it.id}-qty`}
                  value={qty}
                  min={qtyMin}
                  max={qtyMax}
                  onChange={(next) => setQuantity && setQuantity(it.id, next)}
                />
              ) : (
                <>
                  {/* Items with a DB option_group OR frontend _mutexGroup
                      are mutually exclusive — render them as radio-style
                      dots (filled disc when active) so the customer
                      instantly reads "one of these" instead of "several".
                      Every other row keeps the checkmark box. */}
                  {(() => {
                    const isRadio = !!getMutexGroup(it);
                    return isRadio ? (
                      <>
                        <input
                          type="radio"
                          checked={active}
                          disabled={disabled}
                          onChange={() => !disabled && toggle(it.id)}
                          // Native <input type="radio"> does NOT fire
                          // `change` when the already-checked radio is
                          // clicked again — that swallows the deselect
                          // intent for OPTIONAL mutex families (PLVW
                          // Packaging Design, PLVW Vehicle Wrap Design,
                          // BMD family variants, 3D Standard/Advanced
                          // tiers, etc.). The shared toggle helper
                          // `applyOptionGroupSelection` already returns
                          // an empty selection when the target is
                          // re-clicked, so we surface that intent by
                          // routing re-clicks through onClick. Fresh
                          // selects still flow through onChange so
                          // keyboard/screen-reader users are unaffected.
                          onClick={() => { if (!disabled && active) toggle(it.id); }}
                          name={`radiogroup-${testidPrefix}-${getMutexGroup(it)}`}
                          className="sr-only peer"
                          data-testid={`${testidPrefix}-${it.id}-radio`}
                        />
                        <span
                          aria-hidden
                          data-testid={`${testidPrefix}-${it.id}-radio-visual`}
                          className={`mt-0.5 w-5 h-5 rounded-full border shrink-0 grid place-items-center transition-colors ${
                            active
                              ? "border-[#FF6600]"
                              : disabled
                                ? "border-[#2A2A2A]"
                                : "border-[#3A3A3A] peer-hover:border-[#FF6600]/60"
                          }`}
                        >
                          {active && <span className="w-2.5 h-2.5 rounded-full bg-[#FF6600]" />}
                        </span>
                      </>
                    ) : (
                      <>
                        <input
                          type="checkbox"
                          checked={active}
                          disabled={disabled}
                          onChange={() => !disabled && toggle(it.id)}
                          className="sr-only peer"
                        />
                        <span
                          aria-hidden
                          className={`mt-0.5 w-5 h-5 rounded border shrink-0 grid place-items-center transition-colors ${
                            active
                              ? "bg-[#FF6600] border-[#FF6600] text-black"
                              : disabled
                                ? "bg-transparent border-[#2A2A2A]"
                                : "bg-transparent border-[#3A3A3A] peer-hover:border-[#FF6600]/60"
                          }`}
                        >
                          {active && <Check className="w-3 h-3" strokeWidth={3.5} />}
                        </span>
                      </>
                    );
                  })()}
                </>
              )}
              <div className="flex-1 min-w-0">
                <div className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5">
                  {popular && (
                    <PopularRowMarker testId={`${testidPrefix}-${it.id}-recommended`} />
                  )}
                  <span
                    className={`text-sm font-semibold leading-tight ${showAsSelected ? "text-white" : "text-[#D1D1D1]"}`}
                  >
                    {it._displayName || it.name}
                  </span>
                </div>
                {description && (
                  <p className="text-xs text-[#7A7A7A] mt-1 leading-snug">{description}</p>
                )}
                {disabled && it._disabledNote && (
                  <p
                    data-testid={`${testidPrefix}-${it.id}-unavailable-note`}
                    className="text-[11px] text-[#FF6600]/80 mt-1 leading-snug italic"
                  >
                    {it._disabledNote}
                  </p>
                )}
                {/* P.B.4a (Batch 1) — Recurring-replacement disclosure.
                    For addons flagged `replaces_base_recurring` (Website
                    Design AI Assistant), the addon's recurring price
                    OVERRIDES the tier's base recurring rather than
                    stacking on top. Disclosed at the moment of
                    selection so the customer isn't surprised by the
                    higher monthly figure on Step 4 Review. Purely
                    informational; no pricing change.

                    Rendered INSIDE the flex-1 title column so it
                    consumes the same width as the description above
                    it. Rendering it as a sibling of the flex row's
                    price/title cells collapsed the description into
                    a 1-word-per-line column (Feb 2026 layout fix). */}
                {it.replaces_base_recurring && it.recurring_price > 0 && (
                  <div
                    data-testid={`${testidPrefix}-${it.id}-replaces-note`}
                    className="mt-1 text-[11px] text-[#FFB380] leading-snug"
                  >
                    Replaces your Website Care recurring — you pay {listMoney(it.recurring_price, it.recurring_price_usd)}/{it.recurring_label || "month"} total, not stacked.
                  </div>
                )}
              </div>
              {it.request_estimate ? (
                <span
                  data-testid={`${testidPrefix}-${it.id}-quote-tag`}
                  className="basis-full sm:basis-auto shrink-0 self-start sm:self-center inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.16em] bg-[#0F0F0F] text-[#A3A3A3] border border-[#262626] ml-8 sm:ml-0 mt-1 sm:mt-0 w-fit"
                >
                  Estimate
                </span>
              ) : quoteMode && !it.price ? (
                <span
                  data-testid={`${testidPrefix}-${it.id}-quote-tag`}
                  className="basis-full sm:basis-auto shrink-0 self-start sm:self-center inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-[0.16em] bg-[#0F0F0F] text-[#A3A3A3] border border-[#262626] ml-8 sm:ml-0 mt-1 sm:mt-0 w-fit"
                >
                  Estimate
                </span>
              ) : (
                <span className="basis-full sm:basis-auto shrink-0 self-start sm:self-center pl-8 sm:pl-0 mt-1 sm:mt-0 text-left sm:text-right">
                  <span
                    data-testid={qtyEnabled ? `${testidPrefix}-${it.id}-price` : undefined}
                    className="font-display font-bold text-sm text-[#FF6600] tabular-nums"
                  >
                    {qtyEnabled
                      ? (() => {
                          const amt = qty > 0
                            ? computedPrice
                            : (priceFromDbSchedule(it?.quantity_pricing, 1) || eventCreativeQtyPrice(it.name, 1) || (it.price || 0));
                          return listMoney(amt, usdFor(it, amt));
                        })()
                      : `${pricePrefix}${listMoney(it.price, it.price_usd)}`}
                  </span>
                  {qtyEnabled && qty > 0 && (
                    <span
                      data-testid={`${testidPrefix}-${it.id}-qty-label`}
                      className="block text-[10px] text-[#A3A3A3] font-semibold mt-0.5 tabular-nums"
                    >
                      {qty}{" "}
                      {it.name === "Additional Campaign Graphic"
                        ? (qty === 1 ? "graphic" : "graphics")
                        : it.name === "Main Artwork — Additional Size / Format"
                          ? (qty === 1 ? "size" : "sizes")
                          : it.name === "Additional Vehicle Adaptation"
                            ? (qty === 1 ? "adaptation" : "adaptations")
                            : it.name === "Uniform Design Concept — Simple" || it.name === "Uniform Design Concept — Complex"
                              ? (qty === 1 ? "concept" : "concepts")
                              : it.name === "Social Media Templates — 3 Designs"
                                ? (qty === 1 ? "set" : "sets")
                                : it.name === "Poster / Billboard Design — 1 Layout"
                                  ? (qty === 1 ? "layout" : "layouts")
                                  : it.name === "Packaging Design — Standard" || it.name === "Packaging Design — Advanced"
                                    ? (qty === 1 ? "design" : "designs")
                                    : (qty === 1 ? "item" : "items")}
                    </span>
                  )}
                  {!qtyEnabled && it.recurring_price > 0 && (
                    <span
                      data-testid={`${testidPrefix}-${it.id}-recurring`}
                      className="block text-[10px] text-white/60 font-semibold mt-0.5"
                    >
                      +{listMoney(it.recurring_price, it.recurring_price_usd)}/{it.recurring_label || "month"}
                    </span>
                  )}
                </span>
              )}
            </label>
          </li>
          </React.Fragment>
        );
      })}
    </ul>
  );
};

// ---------- Compact − qty + stepper used by quantity-enabled items ----------
const QuantityStepper = ({ testid, value, min, max, onChange }) => {
  const canDec = value > min;
  const canInc = value < max;
  return (
    <div
      data-testid={testid}
      className="mt-0.5 shrink-0 inline-flex items-center gap-1 rounded-full border border-[#3A3A3A] bg-[#0F0F0F] pl-0.5 pr-0.5 h-8"
      onClick={(e) => e.stopPropagation()}
    >
      <button
        type="button"
        data-testid={`${testid}-dec`}
        aria-label="Decrease quantity"
        onClick={(e) => { e.preventDefault(); if (canDec) onChange(value - 1); }}
        disabled={!canDec}
        className={`w-7 h-7 rounded-full grid place-items-center transition-colors ${canDec ? "text-[#FF6600] hover:bg-[#FF6600]/10" : "text-[#3A3A3A] cursor-not-allowed"}`}
      >
        <Minus className="w-3.5 h-3.5" strokeWidth={3} />
      </button>
      <span
        data-testid={`${testid}-value`}
        className="min-w-[1.25rem] text-center text-sm font-bold text-white tabular-nums"
      >
        {value}
      </span>
      <button
        type="button"
        data-testid={`${testid}-inc`}
        aria-label="Increase quantity"
        onClick={(e) => { e.preventDefault(); if (canInc) onChange(value + 1); }}
        disabled={!canInc}
        className={`w-7 h-7 rounded-full grid place-items-center transition-colors ${canInc ? "text-[#FF6600] hover:bg-[#FF6600]/10" : "text-[#3A3A3A] cursor-not-allowed"}`}
      >
        <Plus className="w-3.5 h-3.5" strokeWidth={3} />
      </button>
    </div>
  );
};

// ---------- Items list (used by custom_items step 1 AND kits step 2) ----------
const ItemsGrid = ({ items, selectedItemIds, setSelectedItemIds, selectedItemQtys, setItemQuantity, testidPrefix, quoteMode = false, mainArtworkFormatsById, updateMainArtworkFormats, mainArtworkIncludedSet, mainArtworkBaseFormatId, mainArtworkFormatsList = [], mainArtworkFormatGroups = [] }) => {
  const toggle = (id) => {
    const target = items.find((i) => i.id === id);
    if (!target || target._disabled) return;
    // Mutex-group and DB `option_group` behave identically — the reusable
    // helper handles both. Selecting a new member replaces any prior
    // selection in the same group. See applyOptionGroupSelection().
    setSelectedItemIds(applyOptionGroupSelection(items, target, selectedItemIds));
  };
  return (
    <CheckboxList
      items={items}
      selectedIds={selectedItemIds}
      toggle={toggle}
      testidPrefix={testidPrefix}
      quoteMode={quoteMode}
      quantitiesById={selectedItemQtys}
      setQuantity={setItemQuantity}
      mainArtworkFormatsById={mainArtworkFormatsById}
      updateMainArtworkFormats={updateMainArtworkFormats}
      mainArtworkIncludedSet={mainArtworkIncludedSet}
      mainArtworkBaseFormatId={mainArtworkBaseFormatId}
      mainArtworkFormatsList={mainArtworkFormatsList}
      mainArtworkFormatGroups={mainArtworkFormatGroups}
    />
  );
};

// ---------- Add-ons list (tiered / volume_tiered step 2) ----------
// Accepts optional quantitiesById + setQuantity for services (Branding)
// that mark specific addons `_quantityEnabled`. Honours `_mutexGroup` so
// checkbox families like Vehicle Wrap Simple/Complex only allow one
// selection at a time; toggling one clears any other member of the group.
const AddonsGrid = ({ addons, selectedAddons, setSelectedAddons, quoteMode = false, quantitiesById, setQuantity }) => {
  const toggle = (id) => {
    const target = (addons || []).find((a) => a.id === id);
    const next = new Set(selectedAddons);
    if (next.has(id)) {
      next.delete(id);
    } else {
      if (target?._mutexGroup) {
        for (const other of addons) {
          if (other.id !== id && other._mutexGroup === target._mutexGroup) {
            next.delete(other.id);
          }
        }
      }
      next.add(id);
    }
    setSelectedAddons(next);
  };
  return (
    <CheckboxList
      items={addons}
      selectedIds={selectedAddons}
      toggle={toggle}
      testidPrefix="addon"
      pricePrefix="+"
      quoteMode={quoteMode}
      quantitiesById={quantitiesById}
      setQuantity={setQuantity}
    />
  );
};

// Branding Step 2 — grouped, progressive-disclosure presentation of the
// full addon list. Uses the shared AddonsGrid inside each group so mutex
// / quantity / selection state stays the SAME as the flat presentation —
// this is a UI restructuring only, not a behaviour change.
//
// Behaviour rules per spec:
//   • Groups collapsed by default EXCEPT any group with an active
//     selection or an active quantity.
//   • The open-state persists once the customer manually toggles a group,
//     so a group they've opened doesn't slam shut every render.
//   • Category headers show a discreet count when a group has selected
//     items so the customer can still see "Vehicle Branding — 2 selected"
//     when it's collapsed.
const BrandingProgressiveAddons = ({
  addons, selectedAddons, setSelectedAddons, quoteMode, quantitiesById, setQuantity,
}) => {
  // group id → array of addon rows (visible, in original order).
  const grouped = React.useMemo(() => {
    const map = {};
    for (const g of BRANDING_ADDON_GROUP_ORDER) map[g.id] = [];
    for (const a of addons || []) {
      const gid = groupForBrandingAddon(a);
      (map[gid] || map["logo-brand"]).push(a);
    }
    return map;
  }, [addons]);

  // Which groups have at least one active selection or non-zero qty.
  const activeGroups = React.useMemo(() => {
    const out = new Set();
    for (const g of BRANDING_ADDON_GROUP_ORDER) {
      const rows = grouped[g.id] || [];
      const hit = rows.some(
        (a) => selectedAddons.has(a.id) || (quantitiesById?.[a.id] || 0) > 0,
      );
      if (hit) out.add(g.id);
    }
    return out;
  }, [grouped, selectedAddons, quantitiesById]);

  const [manuallyOpened, setManuallyOpened] = React.useState(() => new Set());
  const [manuallyClosed, setManuallyClosed] = React.useState(() => new Set());

  const isOpen = (gid) => {
    if (manuallyClosed.has(gid)) return false;
    if (manuallyOpened.has(gid)) return true;
    return activeGroups.has(gid);
  };
  const toggleGroup = (gid) => {
    if (isOpen(gid)) {
      setManuallyClosed((s) => new Set(s).add(gid));
      setManuallyOpened((s) => { const n = new Set(s); n.delete(gid); return n; });
    } else {
      setManuallyOpened((s) => new Set(s).add(gid));
      setManuallyClosed((s) => { const n = new Set(s); n.delete(gid); return n; });
    }
  };

  return (
    <div className="mt-6">
      <div className="mb-4">
        <div className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#FF6600] mb-1">
          Enhance Your Brand
        </div>
        <p className="text-sm text-[#A3A3A3]">
          Optional — choose only what your business needs.
        </p>
      </div>
      <div className="space-y-3" data-testid="branding-progressive-addons">
        {BRANDING_ADDON_GROUP_ORDER.map((g) => {
          const rows = grouped[g.id] || [];
          if (rows.length === 0) return null;
          const open = isOpen(g.id);
          const count = rows.reduce(
            (n, a) => n + (selectedAddons.has(a.id) ? 1 : 0) + (quantitiesById?.[a.id] > 0 ? 1 : 0),
            0,
          );
          return (
            <div
              key={g.id}
              data-testid={`branding-group-${g.id}`}
              className={`rounded-2xl border ${open ? "border-[#FF6600]/40" : "border-[#262626]"} bg-[#151515] overflow-hidden`}
            >
              <button
                type="button"
                onClick={() => toggleGroup(g.id)}
                aria-expanded={open}
                data-testid={`branding-group-${g.id}-toggle`}
                className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-[#181818] focus:outline-none focus-visible:bg-[#181818]"
              >
                <div className="flex items-center gap-3 min-w-0">
                  <span className="font-display font-bold text-base sm:text-lg truncate">
                    {g.label}
                  </span>
                  {count > 0 && (
                    <span
                      className="text-[10px] font-bold uppercase tracking-[0.14em] text-[#FF6600] bg-[#FF6600]/10 border border-[#FF6600]/30 px-2 py-0.5 rounded-full shrink-0"
                      data-testid={`branding-group-${g.id}-count`}
                    >
                      {count} selected
                    </span>
                  )}
                </div>
                <ChevronDown
                  className={`w-4 h-4 text-[#A3A3A3] shrink-0 transition-transform ${open ? "rotate-180 text-[#FF6600]" : ""}`}
                />
              </button>
              {open && (
                <div
                  className="px-5 pb-5 border-t border-[#262626]"
                  data-testid={`branding-group-${g.id}-body`}
                >
                  <AddonsGrid
                    addons={rows}
                    selectedAddons={selectedAddons}
                    setSelectedAddons={setSelectedAddons}
                    quoteMode={quoteMode}
                    quantitiesById={quantitiesById}
                    setQuantity={setQuantity}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Website Design — Commercial Scope Refinement V1 (Pass 3D, Feb 2026).
//
// Three Website-only surfaces sit alongside the shared Step 1 / Step 2 chrome:
//
//   • <WebsiteCareDisclosurePanel/>  — Step 1, below the tier grid. Surfaces
//     the mandatory AWG 179/month Website Care & Hosting commitment plus a
//     concise scope statement so the recurring fee is never a Review-step
//     surprise. Non-legalistic. AI Assistant swap footnote appears so the
//     customer knows AI upgrades Care to 315/month (replacement, not stack).
//
//   • <WebsiteAdvancedProjectPanel/> — Step 2, below the AddonsGrid. Warm,
//     progressive-disclosure escape hatch for portals / accounts / custom
//     integrations. CTA hands the customer off to /contact with a topic
//     query param so the Contact form prefills the message field with a
//     Custom Website Quote intent. Uses the existing quote/inquiry surface.
//     Does NOT create a third package.
//
//   • <WebsiteFullOnlineStoreCard/>  — Step 2, next to the advanced panel.
//     Full Online Store is filtered out of the fixed-price AddonsGrid
//     (see visibleAddons above) and rendered on its own visually-distinct
//     estimate-only card so customers see it as scope-review, not a
//     checkbox purchase. Selection state still writes to `selectedAddons`
//     — Review, Cart and quote submission stay untouched.
//
// Zero new commercial records. Zero new endpoints. Zero schema changes.
// ---------------------------------------------------------------------------
const WEB_CARE_DEFAULT_MONTHLY = 179;
const WEB_CARE_DEFAULT_MONTHLY_USD = 100;
const WEB_AI_UPGRADED_MONTHLY = 315;

const WebsiteCareDisclosurePanel = ({ service }) => {
  if (service?.slug !== "website-design") return null;
  return (
    <div
      data-testid="web-care-disclosure"
      className="mt-8 rounded-xl border border-[#FF6600]/25 bg-[#FF6600]/[0.05] p-5 sm:p-6"
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF6600]">
        Ongoing — Website Care &amp; Hosting
      </div>
      <div className="mt-2 font-display text-lg sm:text-xl font-black text-white leading-tight">
        {listMoney(WEB_CARE_DEFAULT_MONTHLY, WEB_CARE_DEFAULT_MONTHLY_USD)}/month after launch
      </div>
      <p
        data-testid="web-care-disclosure-scope"
        className="mt-2.5 text-[13px] text-[#C4C4C4] leading-relaxed"
      >
        Keeps your website hosted, secure and running smoothly after launch —
        hosting, SSL, routine technical maintenance and support for
        website-related technical issues.
      </p>
      <p
        data-testid="web-care-disclosure-boundary"
        className="mt-2 text-[12px] text-[#A3A3A3] leading-relaxed"
      >
        New pages, redesigns, new functionality, substantial content updates
        and changes required by third-party services are not included and may
        be quoted separately.
      </p>
      <p
        data-testid="web-care-disclosure-ai-note"
        className="mt-3 text-[11px] text-[#8A8A8A] leading-relaxed"
      >
        Adding the AI Assistant upgrades Website Care to{" "}
        {listMoney(WEB_AI_UPGRADED_MONTHLY)}/month (replaces the{" "}
        {listMoney(WEB_CARE_DEFAULT_MONTHLY, WEB_CARE_DEFAULT_MONTHLY_USD)}/month base — the two do not
        stack).
      </p>
    </div>
  );
};

const WebsiteAdvancedProjectPanel = ({ service }) => {
  if (service?.slug !== "website-design") return null;
  return (
    <div
      data-testid="web-advanced-project-panel"
      className="mt-8 rounded-xl border border-[#262626] bg-[#141414] p-5 sm:p-6"
    >
      <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#A3A3A3]">
        Need something more advanced?
      </div>
      <p
        data-testid="web-advanced-project-copy"
        className="mt-2 text-[13px] text-[#C4C4C4] leading-relaxed"
      >
        Have a website project that requires custom functionality, integrations
        or a more specialized setup? Tell us what you need and we'll review your
        project to determine the best approach. Some advanced or highly
        specialized systems may fall outside our development scope.
      </p>
      <a
        data-testid="web-advanced-project-cta"
        href="/contact?topic=custom-website-quote"
        className="mt-4 inline-flex items-center gap-2 px-4 py-2.5 rounded-lg border border-[#FF6600] text-[#FF6600] text-sm font-semibold hover:bg-[#FF6600]/10 transition-colors"
      >
        Request a Custom Website Quote
        <ArrowRight className="w-4 h-4" strokeWidth={2} />
      </a>
    </div>
  );
};

const WebsiteFullOnlineStoreCard = ({
  service,
  selectedAddons,
  setSelectedAddons,
}) => {
  if (service?.slug !== "website-design") return null;
  const store = (service.addons || []).find(
    (a) => (a.item_code || "") === "web_full_online_store"
  );
  if (!store) return null;
  const active = selectedAddons?.has(store.id);
  const toggle = () => {
    const next = new Set(selectedAddons);
    if (next.has(store.id)) {
      next.delete(store.id);
    } else {
      // Mutex against Simple Payments — enforced server-side too via
      // option_group="web_payment_scope"; mirror it here so the customer
      // never lands on a self-conflicting Review page.
      const sp = (service.addons || []).find(
        (a) => (a.item_code || "") === "web_simple_payments"
      );
      if (sp) next.delete(sp.id);
      next.add(store.id);
    }
    setSelectedAddons(next);
  };
  return (
    <div
      data-testid="web-full-online-store-card"
      className={`mt-4 rounded-xl border p-5 sm:p-6 transition-colors ${
        active
          ? "border-[#FF6600] bg-[#FF6600]/[0.06]"
          : "border-[#262626] bg-[#141414]"
      }`}
    >
      <div className="flex items-start justify-between gap-4">
        <div className="min-w-0">
          <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF6600]">
            Advanced Project — Estimate Only
          </div>
          <h4 className="mt-2 font-display text-base font-bold uppercase tracking-wide text-white leading-tight">
            Need an Online Store?
          </h4>
          <p
            data-testid="web-full-online-store-copy"
            className="mt-2 text-[13px] text-[#C4C4C4] leading-relaxed"
          >
            Tell us about your products and requirements. Online stores are
            quoted based on scope — catalog size, variants, inventory,
            shipping, taxes, payments and integrations all shape the estimate.
          </p>
          <div
            data-testid="web-full-online-store-tag"
            className="mt-3 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-[0.18em] bg-[#0F0F0F] text-[#A3A3A3] border border-[#262626]"
          >
            <Sparkles className="w-3 h-3" strokeWidth={2} /> Estimate on request
          </div>
        </div>
        <button
          type="button"
          data-testid="web-full-online-store-toggle"
          aria-pressed={!!active}
          onClick={toggle}
          className={`shrink-0 px-4 py-2 rounded-lg text-xs font-semibold transition-colors ${
            active
              ? "border border-[#FF6600] bg-[#FF6600]/10 text-[#FF6600]"
              : "border border-[#333] text-white hover:border-[#FF6600]/60"
          }`}
        >
          {active ? "Added to quote" : "Add to quote"}
        </button>
      </div>
    </div>
  );
};


// ---------- Website Design Pass 3C: compact Website Details section ----------
// Rendered inside ProjectBriefForm ONLY when service.slug === "website-design".
// Fields map 1:1 to the Website Brief spec:
//   project_type       — new | redesign (→ reveals current_url)
//   domain_status      — own | need_help (→ reveals domain_url)
//   content_readiness  — yes | partial | no (partial/no shows note)
//   languages          — one | many (many → reveals languages_detail + activates
//                        the canonical web_multilingual estimate item elsewhere)
//   primary_goal       — leads | info | bookings | payments | sell | other
//   expected_pages     — free text
//   brand_assets       — yes | partial | no
// Content-responsibility copy appears once at the top of the section.
const RADIO_ROW = "grid sm:grid-cols-2 lg:grid-cols-3 gap-2";
const WebsiteRadio = ({ testid, name, value, current, onChange, label }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={() => onChange(value)}
    className={`text-left px-4 py-3 rounded-lg border text-sm transition-colors ${
      current === value
        ? "border-[#FF6600] bg-[#FF6600]/10 text-white"
        : "border-[#262626] bg-[#0F0F0F] text-[#C4C4C4] hover:border-[#FF6600]/40"
    }`}
  >
    {current === value && <span className="text-[#FF6600] mr-2">●</span>}
    {label}
  </button>
);

const WebsiteDetailsSection = ({ websiteDetails, setWD }) => {
  const wd = websiteDetails;
  return (
    <div
      data-testid="website-details-section"
      className="rounded-xl border border-[#FF6600]/30 bg-[#FF6600]/[0.04] p-5 space-y-5"
    >
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF6600]">
          Website Details
        </div>
        <p className="mt-2 text-[12px] text-[#A3A3A3] leading-relaxed">
          Website package pricing assumes final approved text and usable images
          / brand assets are supplied by you. Full copywriting, translation and
          substantial content creation are separate unless quoted.
        </p>
        <p
          data-testid="wd-scope-change-note"
          className="mt-2 text-[12px] text-[#A3A3A3] leading-relaxed"
        >
          If your requirements change or expand after the project begins, any
          work outside the originally approved scope will be reviewed and
          quoted before proceeding.
        </p>
      </div>

      {/* Project type */}
      <div>
        <div className="text-xs uppercase tracking-widest text-[#A3A3A3] font-semibold mb-2">
          What are we building? <span className="text-[#FF6600]">*</span>
        </div>
        <div className={RADIO_ROW}>
          <WebsiteRadio testid="wd-project-type-new" value="new" current={wd.project_type} onChange={setWD("project_type")} label="New website" />
          <WebsiteRadio testid="wd-project-type-redesign" value="redesign" current={wd.project_type} onChange={setWD("project_type")} label="Redesign an existing website" />
        </div>
        {wd.project_type === "redesign" && (
          <div className="mt-3">
            <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-semibold">
              Current Website URL <span className="text-[#FF6600]">*</span>
            </label>
            <input
              data-testid="wd-current-url"
              type="url"
              value={wd.current_url}
              onChange={(e) => setWD("current_url")(e.target.value)}
              placeholder="https://…"
              className="mt-2 w-full bg-[#0F0F0F] border border-[#262626] rounded-xl px-4 py-3 text-sm text-white placeholder:text-[#555] focus:outline-none focus:border-[#FF6600]"
            />
            <p className="mt-1.5 text-[11px] text-[#A3A3A3]">
              Unusually complex migrations or legacy-platform work may require
              a revised estimate after review.
            </p>
          </div>
        )}
      </div>

      {/* Domain */}
      <div>
        <div className="text-xs uppercase tracking-widest text-[#A3A3A3] font-semibold mb-2">
          Domain <span className="text-[#FF6600]">*</span>
        </div>
        <div className={RADIO_ROW}>
          <WebsiteRadio testid="wd-domain-own" value="own" current={wd.domain_status} onChange={setWD("domain_status")} label="I already own my domain" />
          <WebsiteRadio testid="wd-domain-need-help" value="need_help" current={wd.domain_status} onChange={setWD("domain_status")} label="I need help choosing / setting up a domain" />
        </div>
        {wd.domain_status === "own" && (
          <div className="mt-3">
            <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-semibold">
              Domain / Website Address <span className="text-[#FF6600]">*</span>
            </label>
            <input
              data-testid="wd-domain-url"
              type="text"
              value={wd.domain_url}
              onChange={(e) => setWD("domain_url")(e.target.value)}
              placeholder="yourbusiness.com"
              className="mt-2 w-full bg-[#0F0F0F] border border-[#262626] rounded-xl px-4 py-3 text-sm text-white placeholder:text-[#555] focus:outline-none focus:border-[#FF6600]"
            />
            <p className="mt-1.5 text-[11px] text-[#A3A3A3]">
              Your domain remains owned and paid by you. Ur Designs can assist
              with connecting it — no registrar passwords required here.
            </p>
          </div>
        )}
      </div>

      {/* Content readiness */}
      <div>
        <div className="text-xs uppercase tracking-widest text-[#A3A3A3] font-semibold mb-2">
          Is your website content ready? <span className="text-[#FF6600]">*</span>
        </div>
        <div className={RADIO_ROW}>
          <WebsiteRadio testid="wd-content-yes" value="yes" current={wd.content_readiness} onChange={setWD("content_readiness")} label="Yes — final copy and images are ready" />
          <WebsiteRadio testid="wd-content-partial" value="partial" current={wd.content_readiness} onChange={setWD("content_readiness")} label="Partially ready" />
          <WebsiteRadio testid="wd-content-no" value="no" current={wd.content_readiness} onChange={setWD("content_readiness")} label="Not ready yet" />
        </div>
        {(wd.content_readiness === "partial" || wd.content_readiness === "no") && (
          <p className="mt-2 text-[11px] text-[#A3A3A3] leading-relaxed">
            Your project timeline depends on receiving approved content. Full
            copywriting, translation and major content creation are separate
            services unless included in your estimate.
          </p>
        )}
      </div>

      {/* Languages */}
      <div>
        <div className="text-xs uppercase tracking-widest text-[#A3A3A3] font-semibold mb-2">
          How many languages will the website need? <span className="text-[#FF6600]">*</span>
        </div>
        <div className={RADIO_ROW}>
          <WebsiteRadio testid="wd-languages-one" value="one" current={wd.languages} onChange={setWD("languages")} label="1 language — Included" />
          <WebsiteRadio testid="wd-languages-many" value="many" current={wd.languages} onChange={setWD("languages")} label="More than 1 language — Additional scope" />
        </div>
        {/* P.B.4b (Batch 1) — Pricing-mode consequence disclosed AT the
            selection point, not only after the customer picks "many".
            Choosing More than 1 language automatically adds the
            Additional Languages estimate to the quote (silent auto-add
            at PackageBuilder.jsx cleanup effect ~L1358). Making that
            visible up-front prevents Step-4 surprise. Once the customer
            picks "many", the existing orange note below the reveal
            still explains that the fixed Website + Website Care price
            remains, with the estimate quoted alongside. */}
        <p className="mt-2 text-[11px] text-[#7A7A7A] leading-snug">
          Choosing More than 1 language adds an Additional Languages custom estimate to your quote — your fixed Website + Website Care price stays as shown.
        </p>
        {wd.languages === "many" && (
          <div className="mt-3">
            <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-semibold">
              Which languages? <span className="text-[#FF6600]">*</span>
            </label>
            <input
              data-testid="wd-languages-detail"
              type="text"
              value={wd.languages_detail}
              onChange={(e) => setWD("languages_detail")(e.target.value)}
              placeholder="e.g. English + Papiamento + Dutch"
              className="mt-2 w-full bg-[#0F0F0F] border border-[#262626] rounded-xl px-4 py-3 text-sm text-white placeholder:text-[#555] focus:outline-none focus:border-[#FF6600]"
            />
            <p className="mt-1.5 text-[11px] text-[#FF6600]">
              Additional languages are custom scope — we'll send an estimate
              alongside your fixed Website price and Website Care.
            </p>
          </div>
        )}
      </div>

      {/* Primary goal */}
      <div>
        <div className="text-xs uppercase tracking-widest text-[#A3A3A3] font-semibold mb-2">
          What's the primary goal of this website? <span className="text-[#FF6600]">*</span>
        </div>
        <div className={RADIO_ROW}>
          <WebsiteRadio testid="wd-goal-leads" value="leads" current={wd.primary_goal} onChange={setWD("primary_goal")} label="Generate leads / contact requests" />
          <WebsiteRadio testid="wd-goal-info" value="info" current={wd.primary_goal} onChange={setWD("primary_goal")} label="Present services / business information" />
          <WebsiteRadio testid="wd-goal-bookings" value="bookings" current={wd.primary_goal} onChange={setWD("primary_goal")} label="Accept bookings" />
          <WebsiteRadio testid="wd-goal-payments" value="payments" current={wd.primary_goal} onChange={setWD("primary_goal")} label="Accept simple payments" />
          <WebsiteRadio testid="wd-goal-sell" value="sell" current={wd.primary_goal} onChange={setWD("primary_goal")} label="Sell products online" />
          <WebsiteRadio testid="wd-goal-other" value="other" current={wd.primary_goal} onChange={setWD("primary_goal")} label="Other" />
        </div>
      </div>

      {/* Expected pages */}
      <div>
        <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-semibold">
          Expected pages / structure notes
        </label>
        <textarea
          data-testid="wd-expected-pages"
          value={wd.expected_pages}
          onChange={(e) => setWD("expected_pages")(e.target.value)}
          rows={3}
          placeholder="e.g. Home · About · Services (3) · Portfolio · Contact"
          className="mt-2 w-full bg-[#0F0F0F] border border-[#262626] rounded-xl px-4 py-3 text-sm text-white placeholder:text-[#555] focus:outline-none focus:border-[#FF6600]"
        />
      </div>

      {/* Brand assets */}
      <div>
        <div className="text-xs uppercase tracking-widest text-[#A3A3A3] font-semibold mb-2">
          Do you already have brand assets (logo, colours, fonts)? <span className="text-[#FF6600]">*</span>
        </div>
        <div className={RADIO_ROW}>
          <WebsiteRadio testid="wd-brand-yes" value="yes" current={wd.brand_assets} onChange={setWD("brand_assets")} label="Yes — ready to share" />
          <WebsiteRadio testid="wd-brand-partial" value="partial" current={wd.brand_assets} onChange={setWD("brand_assets")} label="Partially — some in place" />
          <WebsiteRadio testid="wd-brand-no" value="no" current={wd.brand_assets} onChange={setWD("brand_assets")} label="No — need help creating them" />
        </div>
      </div>
    </div>
  );
};

// ---------- Step 3: Project Brief form ----------
const ProjectBriefForm = ({ brief, setBrief, settings, service, websiteDetails, setWebsiteDetails }) => {
  const industries = settings?.service_industries || [];
  const deadlines = settings?.service_deadlines || [];
  const set = (k) => (v) => setBrief({ ...brief, [k]: v });
  // Pass 3C — Website-specific field setter. Only wired when service
  // is Website Design; the section itself is conditionally rendered.
  const setWD = (k) => (v) => setWebsiteDetails && setWebsiteDetails({ ...websiteDetails, [k]: v });
  const isWebsite = service?.slug === "website-design";
  // Per-field validity for inline feedback. Only surfaced once the user has
  // typed something into the field — no red squiggles on an empty form.
  const emailTouched = brief.email && brief.email.length > 0;
  const emailInvalid = emailTouched && !isValidEmail(brief.email);
  const phoneTouched = brief.phone && brief.phone.length > 0;
  const phoneInvalid = phoneTouched && !isValidPhone(brief.phone);
  return (
    <div data-testid="project-brief-form" className="ur-card p-6 sm:p-8 space-y-5">
      {/* Contact information — required so Calvin can actually reach the
          customer back. Placed first because it's the primary follow-up
          data point on any submitted brief. */}
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF6600] mb-3">
          Contact Information
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field
            label="Your Name"
            testid="brief-contact-name"
            value={brief.contact_name}
            onChange={set("contact_name")}
            required
            placeholder="Full name"
          />
          <Field
            label="Email Address"
            testid="brief-email"
            type="email"
            value={brief.email}
            onChange={set("email")}
            required
            placeholder="you@example.com"
            invalid={emailInvalid}
            invalidHint="Enter a valid email address"
          />
          <Field
            label="Phone Number"
            testid="brief-phone"
            type="tel"
            value={brief.phone}
            onChange={set("phone")}
            required
            placeholder="+297 XXX XXXX"
            invalid={phoneInvalid}
            invalidHint="Enter a valid phone number"
          />
        </div>
      </div>
      <div>
        <div className="text-[10px] font-bold uppercase tracking-[0.22em] text-[#FF6600] mb-3">
          Project Details
        </div>
        <div className="grid sm:grid-cols-2 gap-5">
          <Field
            label="Business Name"
            testid="brief-business-name"
            value={brief.business_name}
            onChange={set("business_name")}
            required
            placeholder="Your business name"
          />
          <SelectField
            label="Industry"
            testid="brief-industry"
            value={brief.industry}
            onChange={set("industry")}
            options={industries}
            required
            placeholder="Select your industry"
          />
        </div>
      </div>
      <TextareaField
        label="Project Description"
        testid="brief-description"
        value={brief.description}
        onChange={set("description")}
        required
        placeholder="Tell us about your project, goals, target audience, and any specific requirements."
        rows={4}
      />
      <SelectField
        label="Deadline"
        testid="brief-deadline"
        value={brief.deadline}
        onChange={set("deadline")}
        options={deadlines}
        placeholder="Select deadline"
      />
      {service?.slug === EVENT_CREATIVE_SLUG && (
        <Field
          label="Event Date (Optional)"
          testid="brief-event-date"
          type="date"
          value={brief.event_date || ""}
          onChange={set("event_date")}
          placeholder="YYYY-MM-DD"
        />
      )}
      {isWebsite && (
        <WebsiteDetailsSection
          websiteDetails={websiteDetails}
          setWD={setWD}
        />
      )}
      <ReferenceUploader
        label="Reference Images or Sketches (Optional)"
        value={brief.reference_images || []}
        onChange={set("reference_images")}
      />
      <TextareaField
        label="Reference Links / Notes (Optional)"
        testid="brief-references"
        value={brief.reference_links}
        onChange={set("reference_links")}
        placeholder="Paste URLs to references, past work, mood boards, or leave any notes here."
        rows={3}
      />
    </div>
  );
};

const Field = ({ label, value, onChange, testid, required, placeholder, type = "text", invalid, invalidHint }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-semibold">
      {label} {required && <span className="text-[#FF6600]">*</span>}
    </label>
    <input
      data-testid={testid}
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      placeholder={placeholder}
      aria-invalid={invalid ? "true" : undefined}
      className={`mt-2 w-full bg-[#0F0F0F] border rounded-xl px-4 py-3 text-sm text-white placeholder:text-[#555] focus:outline-none ${invalid ? "border-red-500 focus:border-red-500" : "border-[#262626] focus:border-[#FF6600]"}`}
    />
    {invalid && invalidHint && (
      <div data-testid={`${testid}-invalid`} className="mt-1.5 text-[11px] text-red-400">
        {invalidHint}
      </div>
    )}
  </div>
);

const SelectField = ({ label, value, onChange, options, testid, required, placeholder }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-semibold">
      {label} {required && <span className="text-[#FF6600]">*</span>}
    </label>
    <Select value={value || undefined} onValueChange={onChange}>
      <SelectTrigger
        data-testid={testid}
        className="mt-2 w-full h-auto bg-[#0F0F0F] border border-[#262626] rounded-xl px-4 py-3 text-sm text-white hover:border-[#FF6600]/40 focus:border-[#FF6600] focus:ring-0 shadow-none data-[placeholder]:text-[#555]"
      >
        <SelectValue placeholder={placeholder || "Select…"} />
      </SelectTrigger>
      <SelectContent className="bg-[#141414] border border-[#262626] text-white max-h-[300px]">
        {options.map((o) => (
          <SelectItem
            key={o}
            value={o}
            data-testid={`${testid}-option-${o.toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "")}`}
            className="text-sm focus:bg-[#FF6600]/15 focus:text-white data-[state=checked]:text-[#FF6600] cursor-pointer"
          >
            {o}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  </div>
);

// ---------- Reference image / sketch uploader (Step 3, optional) ----------
const REF_MIME_ALLOWED = ["image/png", "image/jpeg", "image/webp", "application/pdf"];
const REF_MAX_BYTES = 10 * 1024 * 1024;
const REF_MAX_FILES = 4;

const ReferenceUploader = ({ label, value, onChange }) => {
  const urls = value || [];
  const [uploading, setUploading] = useState(0);
  const [error, setError] = useState("");
  const inputRef = useRef(null);
  const backendUrl = process.env.REACT_APP_BACKEND_URL || "";
  const abs = (u) => (u && u.startsWith("/") ? `${backendUrl}${u}` : u);
  const isImage = (u) => /\.(png|jpe?g|webp|gif)$/i.test(u || "");

  const pushFiles = async (files) => {
    setError("");
    const remaining = REF_MAX_FILES - urls.length;
    const list = Array.from(files).slice(0, remaining);
    if (files.length > remaining) {
      setError(`Only ${REF_MAX_FILES} files allowed — ${files.length - remaining} skipped.`);
    }
    const accepted = [];
    for (const f of list) {
      if (!REF_MIME_ALLOWED.includes(f.type)) {
        setError(`Unsupported file: ${f.name}. Use PNG, JPG, WEBP or PDF.`);
        continue;
      }
      if (f.size > REF_MAX_BYTES) {
        setError(`${f.name} is too large (max 10MB).`);
        continue;
      }
      accepted.push(f);
    }
    if (accepted.length === 0) return;
    setUploading((c) => c + accepted.length);
    const uploaded = [];
    for (const f of accepted) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const res = await api.post("/project-brief/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        if (res.data?.url) uploaded.push(res.data.url);
      } catch (e) {
        setError(e?.response?.data?.detail || `Upload failed for ${f.name}`);
      } finally {
        setUploading((c) => c - 1);
      }
    }
    if (uploaded.length > 0) onChange([...urls, ...uploaded]);
  };

  const onSelect = (e) => {
    const files = e.target.files;
    if (files && files.length > 0) pushFiles(files);
    e.target.value = "";
  };
  const remove = (u) => onChange(urls.filter((x) => x !== u));

  return (
    <div>
      <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-semibold">
        {label}
      </label>
      <div className="mt-2">
        <input
          ref={inputRef}
          type="file"
          multiple
          accept="image/png,image/jpeg,image/webp,application/pdf"
          onChange={onSelect}
          className="hidden"
          data-testid="brief-references-file-input"
        />
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          disabled={urls.length >= REF_MAX_FILES}
          data-testid="brief-references-upload-btn"
          className="w-full flex items-center justify-center gap-2 border border-dashed border-[#333] hover:border-[#FF6600] hover:bg-[#141414] rounded-xl px-4 py-6 text-sm text-[#A3A3A3] hover:text-[#FF6600] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          {uploading > 0 ? (
            <>
              <Loader2 className="w-4 h-4 animate-spin" /> Uploading {uploading} file{uploading > 1 ? "s" : ""}…
            </>
          ) : urls.length >= REF_MAX_FILES ? (
            <>Max {REF_MAX_FILES} files reached — remove one to add more</>
          ) : (
            <>
              <Upload className="w-4 h-4" /> Drop or click to upload — PNG, JPG, WEBP, PDF · max 10MB · up to {REF_MAX_FILES}
            </>
          )}
        </button>
        {error && (
          <div data-testid="brief-references-error" className="mt-2 text-xs text-amber-400">{error}</div>
        )}
        {urls.length > 0 && (
          <div data-testid="brief-references-preview" className="mt-3 grid grid-cols-2 sm:grid-cols-4 gap-2">
            {urls.map((u) => (
              <div key={u} className="relative group rounded-lg overflow-hidden border border-[#262626] bg-[#0F0F0F] aspect-square">
                {isImage(u) ? (
                  <img src={abs(u)} alt="reference" className="w-full h-full object-cover" />
                ) : (
                  <div className="w-full h-full grid place-items-center text-[#FF6600]">
                    <div className="flex flex-col items-center gap-1">
                      <FileText className="w-6 h-6" />
                      <span className="text-[10px] uppercase tracking-widest text-[#A3A3A3]">PDF</span>
                    </div>
                  </div>
                )}
                <button
                  type="button"
                  onClick={() => remove(u)}
                  data-testid={`brief-references-remove-${u.split("/").pop()}`}
                  className="absolute top-1 right-1 w-6 h-6 rounded-full bg-black/70 hover:bg-red-500 text-white grid place-items-center opacity-0 group-hover:opacity-100 transition-opacity"
                  aria-label="Remove reference"
                >
                  <XIcon className="w-3 h-3" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

const TextareaField = ({ label, value, onChange, testid, required, placeholder, rows = 3 }) => (
  <div>
    <label className="text-xs uppercase tracking-widest text-[#A3A3A3] font-semibold">
      {label} {required && <span className="text-[#FF6600]">*</span>}
    </label>
    <textarea
      data-testid={testid}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      required={required}
      placeholder={placeholder}
      rows={rows}
      className="mt-2 w-full bg-[#0F0F0F] border border-[#262626] rounded-xl px-4 py-3 text-sm text-white placeholder:text-[#555] focus:outline-none focus:border-[#FF6600] resize-none"
    />
  </div>
);


// ---------- Shared selection → rows/total (math unchanged from legacy) ----------
function useSelectionRows({ service, pm, tierId, selectedAddons, selectedItemIds, selectedItemQtys, selectedKitId, mainArtworkBaseFormat }) {
  const tier = useMemo(() => (service.tiers || []).find((t) => t.id === tierId), [service, tierId]);
  const addonItems = useMemo(
    () => (service.addons || []).filter((a) => selectedAddons.has(a.id)),
    [service, selectedAddons]
  );
  const chosenItems = useMemo(
    () => materializeSelectedItems(service, selectedItemIds),
    [service, selectedItemIds]
  );
  const chosenKit = useMemo(
    () => (service.kits || []).find((k) => k.id === selectedKitId),
    [service, selectedKitId]
  );
  // Primary-format surcharge (e.g. BMD Billboard = 150 on top of the
  // Standard/Advanced Artwork price). CMS-driven — reads primary_surcharge
  // from service.main_artwork_config.available_formats. Adds once per
  // qualifying selected item (per qty).
  const primarySurcharge = useMemo(() => {
    const baseId = mainArtworkBaseFormat?.id || null;
    if (!baseId) return 0;
    const formats = getMainArtworkFormatsList(service);
    const surchargePerItem = getPrimaryFormatSurcharge(baseId, formats);
    if (surchargePerItem <= 0) return 0;
    let total = 0;
    for (const it of chosenItems) {
      if (!isMainArtworkProducingItem(service, it)) continue;
      total += surchargePerItem * effectiveItemQty(service, it, selectedItemQtys);
    }
    return total;
  }, [service, chosenItems, mainArtworkBaseFormat, selectedItemQtys]);
  const total = useMemo(() => {
    const itemsSum = chosenItems.reduce(
      (s, i) => s + effectiveItemPrice(service, i, selectedItemQtys),
      0
    );
    if (pm === "tiered" || pm === "volume_tiered") {
      const addonSum = addonItems.reduce(
        (s, a) => s + effectiveAddonPrice(service, a, selectedItemQtys),
        0,
      );
      return (tier?.price || 0) + addonSum;
    }
    if (pm === "custom_items") return itemsSum + primarySurcharge;
    if (pm === "kits") return (chosenKit?.bundle_price || 0) + itemsSum;
    return 0;
  }, [pm, tier, addonItems, chosenItems, chosenKit, service, selectedItemQtys, primarySurcharge]);

  // Sep 2026 — DISPLAY-ONLY US$ list-price sum. Mirrors `total` so the
  // USD running total equals the sum of the approved US$ list prices the
  // customer saw on the cards (e.g. Logo Design US$ 700, not US$ 698.32).
  // AWG `total` above remains the only authoritative/settlement amount.
  // undefined → caller falls back to the exact AWG conversion.
  const totalUsd = useMemo(() => {
    let ok = true;
    let sum = 0;
    const add = (src, amt) => {
      const a = Number(amt) || 0;
      if (a <= 0 || !ok) return;
      const u = src ? usdFor(src, a) : undefined;
      if (!(u > 0)) { ok = false; return; }
      sum += u;
    };
    if (pm === "tiered" || pm === "volume_tiered") {
      add(tier, tier?.price || 0);
      for (const a of addonItems) add(a, effectiveAddonPrice(service, a, selectedItemQtys));
    } else if (pm === "custom_items") {
      for (const i of chosenItems) add(i, effectiveItemPrice(service, i, selectedItemQtys));
      if (primarySurcharge > 0) ok = false; // format surcharges have no approved US$ list price
    } else if (pm === "kits") {
      add(chosenKit, chosenKit?.bundle_price || 0);
      for (const i of chosenItems) add(i, effectiveItemPrice(service, i, selectedItemQtys));
    }
    return ok && sum > 0 ? sum : undefined;
  }, [pm, tier, addonItems, chosenItems, chosenKit, service, selectedItemQtys, primarySurcharge]);

  // Recurring companion total. `replaces_base_recurring` add-ons override
  // the tier's recurring_price (Website Design AI Assistant rule).
  const recurring = useMemo(() => {
    if (pm !== "tiered" && pm !== "volume_tiered") return { amount: 0, label: "" };
    let base = tier?.recurring_price || 0;
    let baseUsd = tier?.recurring_price_usd || 0;
    let label = tier?.recurring_label || "";
    let extras = 0;
    let extrasUsd = 0;
    let usdComplete = true;
    for (const a of addonItems) {
      if (!a.recurring_price) continue;
      if (!(a.recurring_price_usd > 0)) usdComplete = false;
      if (a.replaces_base_recurring) {
        base = a.recurring_price;
        baseUsd = a.recurring_price_usd || 0;
        label = a.recurring_label || label;
      } else {
        extras += a.recurring_price;
        extrasUsd += a.recurring_price_usd || 0;
      }
    }
    if (base > 0 && !(baseUsd > 0)) usdComplete = false;
    // amount_usd = approved USD list price of the recurring commitment;
    // undefined when any part lacks one (falls back to exact conversion).
    return { amount: base + extras, amount_usd: usdComplete ? baseUsd + extrasUsd : undefined, label: label || "month" };
  }, [pm, tier, addonItems]);

  const rows = [];
  if (pm === "tiered" || pm === "volume_tiered") {
    rows.push({
      label: `Tier · ${tier?.name || "—"}`,
      value: tier ? `${listMoney(tier.price, tier.price_usd)}` : "—",
      estimate: !!tier?.request_estimate,
      _price: tier?.price || 0,
      _usd: tier?.price_usd || 0,
    });
    addonItems.forEach((a) => {
      const q = effectiveAddonQty(service, a, selectedItemQtys);
      const p = effectiveAddonPrice(service, a, selectedItemQtys);
      const label = isBrandingQtyAddon(service, a) && q > 1 ? `${a.name} × ${q}` : a.name;
      rows.push({
        label,
        value: `+${listMoney(p, usdFor(a, p))}`,
        muted: true,
        estimate: !!a.request_estimate,
        _price: p,
        _usd: usdFor(a, p) || 0,
      });
    });
  } else if (pm === "custom_items") {
    chosenItems.forEach((i) => {
      const q = effectiveItemQty(service, i, selectedItemQtys);
      const p = effectiveItemPrice(service, i, selectedItemQtys);
      const label = q > 1 ? `${i.name} × ${q}` : i.name;
      rows.push({
        label,
        value: `${listMoney(p, usdFor(i, p))}`,
        muted: true,
        // BMD architecture v1 — flag estimate rows so Step 4 Review can
        // render them in a separate section from fixed-price rows.
        estimate: !!i.request_estimate,
        _price: p,
        _usd: usdFor(i, p) || 0,
      });
    });
  } else if (pm === "kits") {
    if (chosenKit) rows.push({
      label: `Kit · ${chosenKit.name}`,
      value: `${listMoney(chosenKit.bundle_price, chosenKit.price_usd)}`,
      estimate: !!chosenKit.request_estimate,
      _price: chosenKit.bundle_price || 0,
      _usd: chosenKit.price_usd || 0,
    });
    chosenItems.forEach((i) => {
      const q = effectiveItemQty(service, i, selectedItemQtys);
      const p = effectiveItemPrice(service, i, selectedItemQtys);
      const label = q > 1 ? `${i.name} × ${q}` : i.name;
      rows.push({
        label,
        value: `+${listMoney(p, usdFor(i, p))}`,
        muted: true,
        estimate: !!i.request_estimate,
        _price: p,
        _usd: usdFor(i, p) || 0,
      });
    });
  }
  return { rows, total, totalUsd, recurring };
}

// ---------- Quote request submitter (services in cta_type=quote mode) ----------
// Mirrors addSelectionsToCart but POSTs to /api/service-quote-requests and
// never touches the local cart. Selection labels are flattened into a
// single list the admin email can render without a second DB round-trip.
// P0 — the payload now carries the same structured configuration as
// paid orders (Main Artwork base_format + selected_formats + quantity)
// so estimate requests and paid orders converge on one shape.
async function submitQuoteRequest({ service, pm, tierId, selectedAddons, selectedItemIds, selectedItemQtys, selectedKitId, brief, mainArtworkFormats, mainArtworkBaseFormat }) {
  const selections = [];
  const tier = (service.tiers || []).find((t) => t.id === tierId);
  const chosenKit = (service.kits || []).find((k) => k.id === selectedKitId);
  const chosenAddons = (service.addons || []).filter((a) => selectedAddons.has(a.id));
  const chosenItems = materializeSelectedItems(service, selectedItemIds);

  const itemLabel = (i) => {
    const q = effectiveItemQty(service, i, selectedItemQtys);
    return q > 1 ? `${i.name} × ${q}` : i.name;
  };

  // Structured Main Artwork base_format meta — shared shape with paid
  // orders. Only emitted when the configurator captured a valid entry.
  const baseMeta = mainArtworkBaseFormat && isValidMainArtworkEntry(mainArtworkBaseFormat)
    ? mainArtworkEntryMeta(mainArtworkBaseFormat, service)
    : null;
  const _includedSet = getMainArtworkIncludedSet(service, selectedKitId);
  const _baseId = mainArtworkBaseFormat?.id || null;
  const formatEntries = (item) =>
    (mainArtworkFormats?.[item.id] || [])
      .filter((e) => isValidMainArtworkEntry(e))
      .filter((e) => e.id === "custom" || (!_includedSet.has(e.id) && e.id !== _baseId))
      .map((e) => mainArtworkEntryMeta(e, service))
      .filter(Boolean);

  if ((pm === "tiered" || pm === "volume_tiered") && tier) {
    // Pass 3A — quote-mode tier submission. Emit item_code +
    // service_slug + line_type so the backend can server-resolve the
    // fixed portion (tier price + recurring commitment + fixed-price
    // add-ons). Estimate-only add-ons (request_estimate=true, e.g.
    // Full Online Store) are pushed as SEPARATE selections without an
    // item_code so the backend leaves them as estimate rows.
    const fixedAddons = chosenAddons.filter((a) => !a.request_estimate);
    const estimateAddons = chosenAddons.filter((a) => a.request_estimate);
    selections.push({
      kind: "tier",
      label: tier.name,
      slug: tier.id,
      ...(tier.item_code && !tier.request_estimate
        ? {
            item_code: tier.item_code,
            service_slug: service.slug,
            line_type: "tier",
          }
        : {}),
      ...(fixedAddons.length
        ? {
            selected_addons: fixedAddons.map((a) => ({
              item_code: a.item_code || "",
              item_id: a.id,
              quantity: isBrandingQtyAddon(service, a)
                ? effectiveAddonQty(service, a, selectedItemQtys)
                : 1,
            })),
          }
        : {}),
    });
    fixedAddons.forEach((a) => {
      const q = effectiveAddonQty(service, a, selectedItemQtys);
      const label = isBrandingQtyAddon(service, a) && q > 1 ? `${a.name} × ${q}` : a.name;
      selections.push({
        kind: "addon",
        label,
        slug: a.id,
        ...(isBrandingQtyAddon(service, a) ? { quantity: q } : {}),
      });
    });
    estimateAddons.forEach((a) => {
      selections.push({
        kind: "addon",
        label: a.name,
        slug: a.id,
      });
    });
  } else if (pm === "kits") {
    if (chosenKit) selections.push({
      kind: "kit",
      label: chosenKit.name,
      slug: chosenKit.id,
      ...(baseMeta ? { main_artwork_base_format: baseMeta } : {}),
    });
    chosenItems.forEach((i) => selections.push({
      kind: "item",
      label: itemLabel(i),
      slug: i.id,
      quantity: effectiveItemQty(service, i, selectedItemQtys),
      ...(isMainArtworkItem(i) ? { selected_formats: formatEntries(i) } : {}),
    }));
  } else if (pm === "custom_items") {
    chosenItems.forEach((i) => {
      const isAcmPackage = service?.slug === ACM_SLUG && (i.item_code || "") === ACM_CAMPAIGN_PACKAGE_ITEM_CODE;
      selections.push({
        kind: "item",
        label: itemLabel(i),
        slug: i.id,
        // BMD architecture v1 — pass immutable identity so the
        // backend can resolve this line's fixed price against the DB
        // catalog. Absence (e.g. an unmigrated legacy row without
        // an item_code) causes the backend to leave the line as an
        // estimate row, which is the safe fallback.
        ...(i.item_code ? { item_code: i.item_code, service_slug: service.slug, line_type: "standalone" } : {}),
        quantity: effectiveItemQty(service, i, selectedItemQtys),
        ...(isMainArtworkItem(i) ? { selected_formats: formatEntries(i) } : {}),
        ...(isAcmPackage && baseMeta ? { main_artwork_base_format: baseMeta } : {}),
      });
    });
  }

  const payload = {
    service_id: service.id,
    service_slug: service.slug,
    service_name: service.name,
    selections,
    reference_images: brief.reference_images || [],
    contact: {
      name: brief.contact_name || "",
      email: brief.email || "",
      phone: brief.phone || "",
      business: brief.business_name || "",
      industry: brief.industry || "",
      deadline: brief.deadline || "",
      description: brief.description || "",
      reference_links: brief.reference_links || "",
    },
    // Website Design Pass 3C — persist the structured Website Details
    // block on the quote record so admin dashboards render a
    // human-readable production intake instead of parsing description
    // free text.
    ...(brief.website_details ? { website_details: brief.website_details } : {}),
  };
  try {
    const resp = await api.post("/service-quote-requests", payload);
    trackEvent("quote_request", { service_name: service?.name });
    // Backend echoes back { request_id, status, service_name } — surface
    // request_id + service_name to the caller so the confirmation page
    // can display a stable reference and the correct service label.
    return {
      ok: true,
      request_id: resp?.data?.request_id || null,
      service_name: resp?.data?.service_name || service?.name || null,
    };
  } catch (e) {
    return { ok: false, error: e?.response?.data?.detail || "Submission failed" };
  }
}

// ---------- Add-to-cart writer (attaches project_brief to every line) ----------
function addSelectionsToCart({ service, pm, tierId, selectedAddons, selectedItemIds, selectedItemQtys, selectedKitId, brief, addItem, mainArtworkFormats, displayUsd, recurringUsd, mainArtworkBaseFormat }) {
  const tier = (service.tiers || []).find((t) => t.id === tierId);
  const addonItems = (service.addons || []).filter((a) => selectedAddons.has(a.id));
  const chosenItems = materializeSelectedItems(service, selectedItemIds);
  const chosenKit = (service.kits || []).find((k) => k.id === selectedKitId);
  const briefPayload = brief.business_name || brief.industry || brief.description || brief.event_date ? brief : null;
  const withBrief = (meta) => (briefPayload ? { ...meta, project_brief: briefPayload } : meta);

  if (pm === "tiered" || pm === "volume_tiered") {
    if (!tier) return;
    const addonsSum = addonItems.reduce(
      (s, a) => s + effectiveAddonPrice(service, a, selectedItemQtys),
      0,
    );
    const addonLabels = addonItems.map((a) => {
      const q = effectiveAddonQty(service, a, selectedItemQtys);
      return isBrandingQtyAddon(service, a) && q > 1 ? `${a.name} × ${q}` : a.name;
    });
    const addonMeta = addonItems.map((a) => ({
      name: a.name,
      price: effectiveAddonPrice(service, a, selectedItemQtys),
      ...(isBrandingQtyAddon(service, a) ? { quantity: effectiveAddonQty(service, a, selectedItemQtys) } : {}),
    }));
    // Pass 3A — compute recurring commitment client-side for display
    // (server re-resolves authoritatively). AI Assistant-style
    // `replaces_base_recurring` add-ons OVERRIDE the tier's base
    // recurring; other add-ons stack.
    let recurringPrice = tier.recurring_price || 0;
    let recurringLabel = tier.recurring_label || "";
    for (const a of addonItems) {
      if (!a.recurring_price) continue;
      if (a.replaces_base_recurring) {
        recurringPrice = a.recurring_price;
        recurringLabel = a.recurring_label || recurringLabel;
      } else {
        recurringPrice += a.recurring_price;
        if (!recurringLabel) recurringLabel = a.recurring_label || "";
      }
    }
    addItem({
      kind: "service",
      name: `${service.name} — ${tier.name}`,
      price: (tier.price || 0) + addonsSum,
      // Sep 2026 — display-only US$ list-price sum for this line (never
      // used for charging; AWG `price` stays authoritative).
      price_usd: displayUsd || 0,
      // Pass 3A display-only recurring companion (server is authoritative).
      recurring_price: Math.round(recurringPrice * 100) / 100,
      recurring_label: recurringLabel,
      recurring_price_usd: recurringUsd || 0,
      // Canonical LineItemConfig fields — server pulls the real price
      // from tier.id / tier.item_code + resolves each addon similarly.
      service_slug: service.slug,
      line_type: "tier",
      item_code: tier.item_code || "",
      item_id: tier.id,
      quantity: 1,
      parent_service_id: service.id,
      selected_addons: addonItems.map((a) => ({
        item_code: a.item_code || "",
        item_id: a.id,
        quantity: isBrandingQtyAddon(service, a)
          ? effectiveAddonQty(service, a, selectedItemQtys)
          : 1,
      })),
      meta: withBrief({
        slug: service.slug,
        pricing_model: pm,
        tier: tier.name,
        addons: addonLabels,
        addon_details: addonMeta,
        // Branding v3 — every tier-based Branding order comes from the
        // "I need a new brand or logo" path (Path A of the situation
        // gate). The existing-logo paths are handled by a dedicated
        // configurator that writes its own metadata.
        ...(service.slug === BRANDING_SLUG
          ? {
              branding_entry_path: "new",
              branding_service_path:
                tier.name === "Logo Design"
                  ? "logo_design"
                  : tier.name === "Brand Identity"
                  ? "brand_identity"
                  : tier.name === "Complete Brand System"
                  ? "complete_brand_system"
                  : "unknown",
              existing_logo_required: false,
            }
          : {}),
      }),
    });
  } else if (pm === "custom_items") {
    const _includedSet = getMainArtworkIncludedSet(service, selectedKitId);
    const _baseId = mainArtworkBaseFormat?.id || null;
    const _formats = getMainArtworkFormatsList(service);
    const _primarySurcharge = _baseId ? getPrimaryFormatSurcharge(_baseId, _formats) : 0;
    // Base format meta attached to the parent package item (ACM Campaign
    // Design Package). Only emitted for the package item, not for
    // dependent synthetic items.
    const baseMeta = mainArtworkBaseFormat && isValidMainArtworkEntry(mainArtworkBaseFormat)
      ? { main_artwork_base_format: mainArtworkEntryMeta(mainArtworkBaseFormat, service) }
      : {};
    chosenItems.forEach((it) => {
      const qty = effectiveItemQty(service, it, selectedItemQtys);
      const rawPrice = effectiveItemPrice(service, it, selectedItemQtys);
      const surchargeForThisItem = (_primarySurcharge > 0 && isMainArtworkProducingItem(service, it))
        ? _primarySurcharge * qty
        : 0;
      const price = rawPrice + surchargeForThisItem;
      const suffix = qty > 1 ? ` × ${qty}` : "";
      const formatMeta = isMainArtworkItem(it)
        ? {
            selected_formats: (mainArtworkFormats?.[it.id] || [])
              .filter((e) => isValidMainArtworkEntry(e))
              .filter((e) => e.id === "custom" || (!_includedSet.has(e.id) && e.id !== _baseId))
              .map((e) => mainArtworkEntryMeta(e, service))
              .filter(Boolean),
          }
        : {};
      const isAcmPackage = service?.slug === ACM_SLUG && (it.item_code || "") === ACM_CAMPAIGN_PACKAGE_ITEM_CODE;
      // P0 — canonical identity. For DB items `it.item_code` is populated
      // by migrate_acm_item_codes_p0 / migrate_ecp_item_codes_p0; for
      // synthetics the maps hand us the immutable server code.
      const canonicalItemCode = it.item_code
        || ACM_SYNTHETIC_ITEM_CODE_BY_ID[it.id]
        || ECP_SYNTHETIC_ITEM_CODE_BY_ID[it.id]
        || "";
      const isQtyItem = isEventCreativeQtyItem(service, it) || ACM_QTY_ADDON_IDS.includes(it.id);
      addItem({
        kind: "service",
        name: `${service.name} — ${it.name}${suffix}`,
        price,
        price_usd: displayUsd || 0,
        service_slug: service.slug,
        line_type: "standalone",
        item_code: canonicalItemCode,
        item_id: it.id,
        quantity: qty,
        parent_service_id: service.id,
        selected_options: {
          ...formatMeta,
          ...(isMainArtworkProducingItem(service, it) ? baseMeta : {}),
        },
        meta: withBrief({
          slug: service.slug,
          pricing_model: pm,
          parent_service_id: service.id,
          item_id: it.id,
          // P0 — quantity is now emitted for BOTH Event Creative AND ACM
          // quantity-enabled items (the pre-P0 bug only emitted it for
          // ECP). Admin Orders + production surface can parse quantity
          // from structured data, never from the "× 5" display name.
          ...(isQtyItem ? { quantity: qty } : {}),
          ...formatMeta,
          ...(isMainArtworkProducingItem(service, it) ? baseMeta : {}),
        }),
      });
    });
  } else if (pm === "kits") {
    if (chosenKit) {
      const baseMeta = mainArtworkBaseFormat && isValidMainArtworkEntry(mainArtworkBaseFormat)
        ? { main_artwork_base_format: mainArtworkEntryMeta(mainArtworkBaseFormat, service) }
        : {};
      addItem({
        kind: "service",
        name: `${service.name} — ${chosenKit.name}`,
        price: chosenKit.bundle_price,
        price_usd: displayUsd || 0,
        service_slug: service.slug,
        line_type: "kit",
        item_code: chosenKit.item_code || "",
        item_id: chosenKit.id,
        quantity: 1,
        parent_service_id: service.id,
        selected_options: { ...baseMeta },
        meta: withBrief({
          slug: service.slug,
          pricing_model: pm,
          kit_id: chosenKit.id,
          kit_included_item_ids: chosenKit.included_item_ids || [],
          ...baseMeta,
        }),
      });
    }
    chosenItems.forEach((it) => {
      const qty = effectiveItemQty(service, it, selectedItemQtys);
      const price = effectiveItemPrice(service, it, selectedItemQtys);
      const suffix = qty > 1 ? ` × ${qty}` : "";
      const _includedSet = getMainArtworkIncludedSet(service, selectedKitId);
      const _baseId = mainArtworkBaseFormat?.id || null;
      const formatMeta = isMainArtworkItem(it)
        ? {
            selected_formats: (mainArtworkFormats?.[it.id] || [])
              .filter((e) => isValidMainArtworkEntry(e))
              .filter((e) => e.id === "custom" || (!_includedSet.has(e.id) && e.id !== _baseId))
              .map((e) => mainArtworkEntryMeta(e, service))
              .filter(Boolean),
          }
        : {};
      addItem({
        kind: "service",
        name: `${service.name} — ${it.name}${suffix}`,
        price,
        price_usd: displayUsd || 0,
        service_slug: service.slug,
        line_type: "standalone",
        // P0 — DB item_code first, then ECP synthetic map for the story
        // upgrades + 3D complex upgrade that ride alongside ECP kits.
        item_code: it.item_code
          || ECP_SYNTHETIC_ITEM_CODE_BY_ID[it.id]
          || ACM_SYNTHETIC_ITEM_CODE_BY_ID[it.id]
          || "",
        item_id: it.id,
        quantity: qty,
        parent_service_id: service.id,
        selected_options: { ...formatMeta },
        meta: withBrief({
          slug: service.slug,
          pricing_model: pm,
          parent_service_id: service.id,
          item_id: it.id,
          alongside_kit: !!chosenKit,
          ...(isEventCreativeQtyItem(service, it) ? { quantity: qty } : {}),
          ...formatMeta,
        }),
      });
    });
  }
}

// ---------- Trust signals (rendered under both CTA blocks — muted, no accent color) ----------
const TrustRow = ({ testidPrefix }) => (
  <div
    data-testid={testidPrefix}
    className="mt-4 flex flex-wrap items-center justify-center gap-x-2.5 gap-y-1 text-[10px] font-semibold uppercase tracking-[0.14em] text-[#6E6E6E]"
  >
    <span data-testid={`${testidPrefix}-secure`} className="inline-flex items-center gap-1.5">
      <ShieldCheck className="w-3 h-3" strokeWidth={2} /> Secure checkout
    </span>
    <span aria-hidden className="text-[#3A3A3A]">·</span>
    <span data-testid={`${testidPrefix}-cancel`} className="inline-flex items-center gap-1.5">
      <Clock className="w-3 h-3" strokeWidth={2} /> Cancel anytime
    </span>
    <span aria-hidden className="text-[#3A3A3A]">·</span>
    <span data-testid={`${testidPrefix}-support`} className="inline-flex items-center gap-1.5">
      <MapPin className="w-3 h-3" strokeWidth={2} /> Local Aruba support
    </span>
  </div>
);

// ---------- Atoms ----------
const EmptyBody = ({ message }) => (
  <div className="ur-card p-10 text-center">
    <div className="w-14 h-14 rounded-full bg-[#FF6600]/10 border border-[#FF6600]/30 grid place-items-center mx-auto">
      <Sparkles className="w-6 h-6 text-[#FF6600]" />
    </div>
    <h3 className="font-display mt-4 text-xl font-bold">Coming soon</h3>
    <p className="mt-2 text-sm text-[#A3A3A3] max-w-md mx-auto">{message}</p>
    <Link to="/services" className="ur-btn-secondary mt-6 inline-flex">
      ← All Services <ArrowRight className="w-4 h-4" />
    </Link>
  </div>
);
